// code-base/App.js

import './App.css';
import React, { useState, useMemo, useCallback, useEffect, useReducer, Suspense, useRef } from 'react';
import { ATLAS_FONT_FAMILIES, ATLAS_RMI_ZONE_COLORS } from './oceanographicAtlas';

// --- Modern UI Imports ---
import { motion, AnimatePresence, MotionConfig, useReducedMotion, useAnimationControls, useDragControls } from 'framer-motion';
import { clsx } from 'clsx';
import { 
    Settings, 
    BarChart3, 
    Info, 
    Download, 
    Camera, 
    Play, 
    Pause, 
    Menu as MenuIconLucide, 
    ChevronDown, 
    ChevronRight,
    Activity,
    Waves,
    Wind,
    Thermometer,
    Droplets,
    Monitor,
    X,
    ChevronLeft,
    Globe,
    Map as MapIcon,
    Home,
    Layers,
    Maximize,
    Minus,
    Plus,
    ChevronUp,
    MoreHorizontal,
} from 'lucide-react';

// --- Material UI Imports ---
import {
    ThemeProvider,
    createTheme,
    CssBaseline,
    Box,
    IconButton,
    Slider, // MUI Slider
    useMediaQuery, // Hook for responsiveness
    Dialog, DialogContent, DialogTitle, Select, MenuItem, Menu,
    CircularProgress,
    Switch
} from '@mui/material';
import { HelmetProvider, Helmet } from 'react-helmet-async';

// --- Your Existing DeckGL and Mapbox GL imports ---
import { getJSON } from './getJSON';
import DeckGL from '@deck.gl/react';
import * as ReactMapGL from 'react-map-gl';
import { GeoJsonLayer, ScatterplotLayer, TextLayer, IconLayer } from '@deck.gl/layers';
import { LinearInterpolator, MapView } from '@deck.gl/core';
import { FlatGeobufLoader } from '@loaders.gl/flatgeobuf';
import { PathStyleExtension } from '@deck.gl/extensions';

// --- Your Component Imports ---
// import ScatterPlotComponent from "./ScatterPlot"; // Removed direct import
// import CRWMenuBar from './CRWMenuBar'; // Use the new/updated CRWMenuBar
import { useMapLayers } from './MapLoader'; // Import hook instead of component
import ErrorBoundary from './ErrorBoundary'; // Import ErrorBoundary
import { getVariableConfig } from './variableConfig'; // IMPORT CONFIG HANDLER
import cmip7ProjectionCatalogFallback from './cmip7ProjectionCatalog';
import { regionalSeaLevelMetadata } from './regionalSeaLevelConfig';
import { RMI_ATOLL_LABELS } from './rmiAtollReference';
import { mhwScopeMetadata } from './mhwConfig';
import { dashboardReducer, initialState } from './dashboardReducer';
import { useAnimationLoop } from './useAnimationLoop';
import CachedImage from './components/CachedImage';
import ConvergenceZoneLegend from './ConvergenceZoneLegend';
import ConvergenceZoneMethodology from './ConvergenceZoneMethodology';
import { useTemperatureUnit } from './useTemperatureUnit';
// import APCCSSTScale from './APCCSSTScale'; // Uncomment if used
// import APCCPrecipScale from './APCCPrecipScale'; // Uncomment if used

// --- Your Variable/Constant Imports ---
import {
    historicCRWYears,
    currentCRWYears,
    mhwMonthlyDates,
    mhwSeasonalDates,
    mhwDecadalDates,
    documentationMap,
} from './components/vars'; // Assuming './vars' is the correct path
import { oisstIndoPacificDates, oisstPalauDates, oisstRegionMetadata, oisstRmiDates } from './oisstConfig';
import {
    cfsRegionMetadata,
    cfsVariableMetadata,
    cfsDates_sst,
} from './cfsConfig';
import {
    gfsRegionMetadata,
    gfsVariableMetadata,
    gfsDates_sst,
} from './gfsConfig';
import {
    FORECAST_ENTRIES,
    forecastPageTitle,
    isForecastVariable,
    isEkmanVariable,
    parseForecastId,
} from './forecastMenuConfig';
import {
    hurricaneActiveStorms,
    hurricaneModels,
    hurricaneBasePath,
    hurricaneUpdated,
} from './hurricaneConfig';
import { CRW_DAILY_PRODUCTS, crwProductFromVariable } from './crwDailyProducts';
import { VIEW_STATES } from './vars';
import {
    APCC_VARIABLES,
    apccMenuChildren,
    apccSelectionLabel,
    getApccAvailableAggregations,
    parseApccId,
} from './apccConfig';

// --- Mapbox GL Fallbacks / Destructuring ---
const { StaticMap, ScaleControl } = ReactMapGL;
const MapContext = ReactMapGL.MapContext || ReactMapGL._MapContext; // Safe fallback across v5/v6/v7

// Render deck.gl layers across repeated world copies so data that spans the
// antimeridian (e.g. the whole-Pacific CRW layer at lon 125E..245E) keeps
// drawing when the camera pans across the dateline instead of disappearing.
const REPEAT_MAP_VIEW = new MapView({ id: 'default', repeat: true });

// Keep operational timestamps precise in Details, while making the overview
// readable at a glance. This intentionally rounds down: "2 h ago" should not
// flicker to "3 h ago" until the third hour has actually started.
const formatRelativeTime = (value) => {
    const timestamp = Date.parse(value || '');
    if (!Number.isFinite(timestamp)) return 'Updated recently';
    const deltaMs = Date.now() - timestamp;
    const future = deltaMs < 0;
    const minutes = Math.floor(Math.abs(deltaMs) / 60000);
    const unit = minutes < 60 ? ['min', Math.max(1, minutes)]
        : minutes < 1440 ? ['h', Math.floor(minutes / 60)]
            : ['d', Math.floor(minutes / 1440)];
    return `Updated ${unit[1]} ${unit[0]} ${future ? 'from now' : 'ago'}`;
};

const TIMELINE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CONVERGENCE_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const convergenceTimestamp = (value) => {
    const match = String(value || '').match(/(\d{4})-(\d{2})(?:-(\d{2}))?(?:T(\d{2}))?/);
    if (!match) return null;
    return {
        year: match[1],
        month: match[2],
        day: match[3] || '01',
        hour: match[4] || '',
        date: `${match[1]}-${match[2]}-${match[3] || '01'}`,
        exact: `${match[1]}-${match[2]}-${match[3] || '01'}${match[4] ? `T${match[4]}` : ''}`,
    };
};

const convergenceDateLabel = (stamp, temporalResolution) => {
    if (!stamp) return '';
    const date = new Date(`${stamp.date}T00:00:00Z`);
    if (!Number.isFinite(date.getTime())) return stamp.date;
    if (temporalResolution?.includes('monthly')) {
        return `${CONVERGENCE_MONTHS[Number(stamp.month) - 1]} ${stamp.year}`;
    }
    const label = new Intl.DateTimeFormat('en', {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
    }).format(date);
    return stamp.hour ? `${label} · ${stamp.hour} UTC` : label;
};

// Timeline data ranges from annual CMIP6 frames to dense daily operational
// products.  The exact timestamp remains available in the value label and the
// readout beside the slider; marks are intentionally concise navigation cues.
const formatTimelineMarkLabel = (value) => {
    const label = String(value ?? '').trim();
    const dateMatch = label.match(/^(\d{4})-(\d{2})(?:-\d{2})?/);
    if (!dateMatch) return label;

    const monthIndex = Number(dateMatch[2]) - 1;
    if (monthIndex < 0 || monthIndex > 11) return label;
    return `${TIMELINE_MONTHS[monthIndex]} '${dateMatch[1].slice(-2)}`;
};

const compactTimelineMarks = (markerSelected, dateData, maxVisibleMarks) => {
    const markerData = typeof markerSelected === 'function' ? markerSelected() : markerSelected;
    const sourceMarks = Array.isArray(markerData)
        ? markerData
        : (markerData && typeof markerData === 'object'
            ? Object.entries(markerData).map(([value, label]) => ({ value: Number(value), label }))
            : []);

    // Some legacy generators add a mark for every frame with an empty label.
    // Those ticks crowd the rail without communicating time, so remove them
    // before applying the responsive label budget.
    const labelledMarks = sourceMarks
        .map((mark) => ({ value: Number(mark?.value), label: mark?.label }))
        .filter((mark) => Number.isFinite(mark.value) && String(mark.label ?? '').trim())
        .map((mark) => ({ ...mark, label: formatTimelineMarkLabel(mark.label) }));

    const fallbackMarks = Array.isArray(dateData)
        ? dateData.map((date, value) => ({ value, label: formatTimelineMarkLabel(date) }))
        : [];
    const endpointMarks = fallbackMarks.length > 1
        ? [fallbackMarks[0], fallbackMarks[fallbackMarks.length - 1]]
        : [];
    const candidateMarks = [...(labelledMarks.length ? labelledMarks : fallbackMarks), ...endpointMarks]
        .sort((a, b) => a.value - b.value)
        .filter((mark, index, marks) => index === 0 || mark.value !== marks[index - 1].value);

    if (candidateMarks.length < 2) return false;
    if (candidateMarks.length <= maxVisibleMarks) return candidateMarks;

    const chosenIndices = new Set();
    for (let index = 0; index < maxVisibleMarks; index += 1) {
        chosenIndices.add(Math.round((index * (candidateMarks.length - 1)) / (maxVisibleMarks - 1)));
    }
    return [...chosenIndices].sort((a, b) => a - b).map((index) => candidateMarks[index]);
};

const projectSheetRelease = (offset, velocity) => offset + ((velocity / 1000) * 499);

const CRW_DIAL_FIELDS = [
    'CRW_BAA_Current_Rounded',
    'CRW_BAA_Week_01-04',
    'CRW_BAA_Week_05-08',
    'CRW_BAA_Week_09-12',
];

const CRW_DIAL_LABELS = ['Current', 'Weeks 1–4', 'Weeks 5–8', 'Weeks 9–12'];

const nearestCrwFeature = (collection, latitude, longitude) => {
    const features = collection?.features || collection || [];
    return features.reduce((nearest, feature) => {
        const properties = feature?.properties || feature || {};
        const lat = Number(properties.lat ?? properties.latitude);
        const lon = Number(properties.lon ?? properties.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return nearest;
        const distance = Math.pow(lat - latitude, 2) + Math.pow(lon - longitude, 2);
        return !nearest || distance < nearest.distance ? { feature, distance } : nearest;
    }, null)?.feature || null;
};

const crwDialsAtLocation = (properties, latitude, longitude, grids = {}) => CRW_DIAL_FIELDS.map((field, index) => {
    const localValue = properties[field] ?? (index === 0 ? properties.value : null);
    if (localValue != null) return Number(localValue);
    const record = nearestCrwFeature(grids[field] || grids.all, latitude, longitude);
    const recordProperties = record?.properties || record || {};
    const value = recordProperties[field] ?? (index === 0 ? recordProperties.value : null);
    return value == null || !Number.isFinite(Number(value)) ? null : Number(value);
});

// Polyfill process for shp-write
if (typeof process === 'undefined') {
    window.process = { browser: true };
}

// --- Free Terrain Basemap Style (Esri World Topo) ---
const TERRAIN_STYLE = {
    version: 8,
    sources: {
        'esri-topo': {
            type: 'raster',
            tiles: [
                'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'
            ],
            tileSize: 256,
            attribution: 'Map data © Esri'
        }
    },
    layers: [{
        id: 'esri-topo',
        type: 'raster',
        source: 'esri-topo',
        minzoom: 0,
        maxzoom: 19
    }]
};

// --- Free Satellite Basemap Style (Esri World Imagery) ---
const SATELLITE_STYLE = {
    version: 8,
    sources: {
        'esri-satellite': {
            type: 'raster',
            tiles: [
                'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ],
            tileSize: 256,
            attribution: 'Map data © Esri'
        }
    },
    layers: [{
        id: 'esri-satellite',
        type: 'raster',
        source: 'esri-satellite',
        minzoom: 0,
        maxzoom: 19
    }]
};

// --- Custom Lazy Loader to handle ChunkLoadErrors ---
const lazyWithRetry = (componentImport) =>
  React.lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // If chunk fails to load, force refresh the page once to get the latest Webpack manifest
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
      }
      throw error;
    }
  });

// [PERFORMANCE] Lazy load CRWMenuBar.
// This moves 'react-plotly.js' (imported by MuiGauge inside CRWMenuBar)
// into a separate chunk, reducing the main bundle size significantly.
const CRWMenuBar = lazyWithRetry(() => import('./CRWMenuBar'));
const CRWDailyScopeControl = lazyWithRetry(() => import('./CRWDailyScopeControl'));

// [PERFORMANCE] Lazy load RightSidebar.
// The sidebar is hidden on initial load. Loading it lazily saves parsing time
// and bandwidth for the initial view.
const RightSidebar = lazyWithRetry(() => import('./components/RightSidebar'));

// [PERFORMANCE] Lazy load Documentation and non-critical UI components to reduce initial bundle size
const LandingPage = lazyWithRetry(() => import('./LandingPage'));
const MapLegend = lazyWithRetry(() => import('./MapLegend'));
const MHWDocumentation = lazyWithRetry(() => import('./MHWDocumentation'));
const RegionalSeaLevelDocumentation = lazyWithRetry(() => import('./RegionalSeaLevelDocumentation'));
const FloodFrequencyDocumentation = lazyWithRetry(() => import('./FloodFrequencyDocumentation'));
const FloodDurationDocumentation = lazyWithRetry(() => import('./FloodDurationDocumentation'));
const WaveDocumentation = lazyWithRetry(() => import('./WaveDocumentation'));
const CMIP6Documentation = lazyWithRetry(() => import('./CMIP6Documentation'));
const CMIP7PreviewDocumentation = lazyWithRetry(() => import('./CMIP7PreviewDocumentation'));
const SurfaceTempDocumentation = lazyWithRetry(() => import('./SurfaceTempDocumentation'));
const HotDaysDocumentation = lazyWithRetry(() => import('./HotDaysDocumentation'));
const ColdNightsDocumentation = lazyWithRetry(() => import('./ColdNightsDocumentation'));
const TotalWetDayDocumentation = lazyWithRetry(() => import('./TotalWetDayDocumentation'));
const ConsecutiveDryDaysDocumentation = lazyWithRetry(() => import('./ConsecutiveDryDaysDocumentation'));
const HeavyRainfallDocumentation = lazyWithRetry(() => import('./HeavyRainfallDocumentation'));
const WetDaysDocumentation = lazyWithRetry(() => import('./WetDaysDocumentation'));
const DryDaysDocumentation = lazyWithRetry(() => import('./DryDaysDocumentation'));
const WindBarbDocumentation = lazyWithRetry(() => import('./WindBarbDocumentation'));

// --- PCC Probes Metadata ---
const PCC_METADATA = {
    "PCC-CRE_Ngerkebesang": { "latitude": 7.3539583, "longitude": 134.4484336, "name": "Ngerkebesang", "data_file": "PCC-CRE_Ngerkebesang.json" },
    "PCC-CRE_Oikull": { "latitude": 7.3683684, "longitude": 134.584445, "name": "Oikull", "data_file": "PCC-CRE_Oikull.json" },
    "PCC-CRE_Peleliu": { "latitude": 7.0394674, "longitude": 134.2570748, "name": "Peleliu", "data_file": "PCC-CRE_Peleliu.json" },
    "PCC-CRE_Ngermid": { "latitude": 7.3412812, "longitude": 134.5030186, "name": "Ngermid", "data_file": "PCC-CRE_Ngermid.json" },
    "PCC-CRE_Ngermeskang": { "latitude": 7.528665, "longitude": 134.560483, "name": "Ngermeskang", "data_file": "PCC-CRE_Ngermeskang.json" },
    "PCC-CRE_Ngerbeched": { "latitude": 7.3383877, "longitude": 134.4778042, "name": "Ngerbeched", "data_file": "PCC-CRE_Ngerbeched.json" },
    "PCC-CRE_Ulimang": { "latitude": 7.6233714, "longitude": 134.6411937, "name": "Ulimang", "data_file": "PCC-CRE_Ulimang.json" },
    "PCC-CRE_Angaur": { "latitude": 6.901022, "longitude": 134.137439, "name": "Angaur", "data_file": "PCC-CRE_Angaur.json" },
    "PCC-CRE_Kayangel": { "latitude": 8.0840262, "longitude": 134.7182361, "name": "Kayangel", "data_file": "PCC-CRE_Kayangel.json" }
};
const PCC_MAP_DATA = Object.entries(PCC_METADATA).map(([id, info]) => ({
    id,
    position: [info.longitude, info.latitude],
    ...info
}));

// --- Helper to check if a variable has a right-panel graph/image ---
const hasRightPanelGraph = (key) => {
    if (!key) return false;
    const noImageVariables = [
        '3.5.1.3', // Dry Days
        '3.6.2',   // APCC MME Precipitation
        '8.1'      // Ocean Acidification
    ];
    return !key.startsWith('3.7') && !noImageVariables.includes(key);
};

// --- Ngardok Lake Metadata ---
const NGARDOK_DATA = [{ id: 'ngardok', name: 'Lake Ngardok', position: [134.603611, 7.5125] }];

// --- AWS Metadata ---
const AWS_METADATA = {
    "PW74450": { "name": "Aimeliik AWS", "data_file": "PW74450.json", "latitude": 7.457433, "longitude": 134.504233, "altitude": 44.0 },
    "PW74021": { "name": "Peleliu AWS", "data_file": "PW74021.json", "latitude": 6.985262, "longitude": 134.220387, "altitude": 2.0 },
    "PW7134": { "name": "Ngeremlengui AWS", "data_file": "PW7134.json", "latitude": 7.524759, "longitude": 134.518949, "altitude": 91.0 },
    "PW74561": { "name": "Melekeok AWS", "data_file": "PW74561.json", "latitude": 7.504102, "longitude": 134.616063, "altitude": 55.0 },
    "PW74351": { "name": "Airai AWS", "data_file": "PW74351.json", "latitude": 7.36478, "longitude": 134.531292, "altitude": 60.0 }
};
const AWS_MAP_DATA = Object.entries(AWS_METADATA).map(([id, info]) => ({
    id,
    position: [info.longitude, info.latitude],
    ...info
}));

const hexToColor = (hex, alpha = 255) => {
    const value = String(hex || '').replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(value)) return null;
    return [
        parseInt(value.slice(0, 2), 16),
        parseInt(value.slice(2, 4), 16),
        parseInt(value.slice(4, 6), 16),
        alpha,
    ];
};

// --- Boundary Layers Configuration ---
const BOUNDARY_LAYERS_CONFIG = [
    // Environment
    { id: 'sanctuary', label: 'Palau National Marine Sanctuary', category: 'Environment', file: 'Palau_National_MarineSanctuary.json', color: [0, 191, 255] },
    { id: 'coral', label: 'Coral Reefs', category: 'Environment', file: 'pw_unepwcmc_all_coralreefs.json', color: [255, 127, 80] },
    { id: 'pan', label: 'Protected Areas Network', category: 'Environment', file: 'Palau_ProtectedAreasNetwork_PAN2026revNew.geojson', color: [50, 205, 50] },
    { id: 'parks', label: 'Parks', category: 'Environment', file: 'Palau_Parks_2025.geojson', color: [22, 163, 74], layerType: 'point-label' },
    { id: 'marine_lakes', label: 'Marine Lakes', category: 'Environment', file: 'Palau_MarineLakes_2024.geojson', color: [14, 165, 233] },
    { id: 'unesco', label: 'UNESCO RISL Site', category: 'Environment', file: 'Palau_UNESCOsite_RISL_2012.geojson', color: [234, 179, 8] },
    { id: 'endangered_plants', label: 'Endangered Plant Species', category: 'Environment', file: 'Palau_EndangeredPlantSpecies_2025.geojson', color: [132, 204, 22], layerType: 'point-label' },
    { id: 'artisanal', label: 'Artisanal Fishing Area', category: 'Boundaries', file: 'Palau_ArtisanalFishingArea_2019.geojson', color: [168, 85, 247] },
    { id: 'aquaculture', label: 'Aquaculture Farms', category: 'Infrastructure', file: 'Palau_AquacultureFarm_2015.geojson', color: [249, 115, 22] },
    { id: 'dive_spots', label: 'Dive Spots (2008)', category: 'Infrastructure', file: 'Palau_DiveSpots_2008.geojson', color: [34, 211, 238], layerType: 'point-label' },
    { id: 'reef_sites', label: 'Reef Monitoring Sites', category: 'Environment', file: 'Palau_ReefMonitoringSites.geojson', color: [244, 63, 94], layerType: 'point-label', radiusMinPixels: 14, radiusMaxPixels: 28, labelOffset: -32 },
    // { id: 'benthic', label: 'Benthic Habitats', category: 'Environment', file: 'pw_noaa_all_benthic_habitats.json', color: [46, 139, 87] },
    // { id: 'marsh', label: 'Marshlands', category: 'Environment', file: 'pw_plrs_all_marsh.json', color: [85, 107, 47] },
    // { id: 'protected', label: 'Protected Areas', category: 'Environment', file: 'pw_plrs_all_protected_areas.json', color: [50, 205, 50] },
    // { id: 'conserv_areas', label: 'Conservation Areas', category: 'Environment', file: 'pw_plrs_all_conservareas.json', color: [143, 188, 143] },
    // { id: 'conserv_sites', label: 'Conservation Sites', category: 'Environment', file: 'pw_plrs_all_conservsites.json', color: [0, 250, 154] },
    // // Infrastructure
    // { id: 'roads', label: 'Roads', category: 'Infrastructure', file: 'pw_plrs_all_roads.json', color: [226, 232, 240] },
    // { id: 'roads_compact', label: 'Roads (Compact)', category: 'Infrastructure', file: 'pw_plrs_all_roads_compact.json', color: [148, 163, 184] },
    // { id: 'airports', label: 'Airports', category: 'Infrastructure', file: 'pw_plrs_all_airports.json', color: [250, 204, 21] },
    // { id: 'docks', label: 'Docks', category: 'Infrastructure', file: 'pw_plrs_all_docks.json', color: [249, 115, 22] },
    // { id: 'schools', label: 'Schools', category: 'Infrastructure', file: 'pw_plrs_all_schools.json', color: [192, 132, 252] },
    // { id: 'tourist', label: 'Tourist Sites', category: 'Infrastructure', file: 'pw_plrs_all_touristsites.json', color: [244, 114, 182] },
    // { id: 'historic', label: 'Historic Sites', category: 'Infrastructure', file: 'pw_plrs_all_historicsites.json', color: [217, 119, 6] },
    // { id: 'dive', label: 'Dive Sites', category: 'Infrastructure', file: 'pw_plrs_all_divesites.json', color: [34, 211, 238] },
    // // Boundaries
    // { id: 'political', label: 'Political Boundaries', category: 'Boundaries', file: 'pw_plrs_all_political_bndry.json', color: [239, 68, 68] },
    // { id: 'political_line', label: 'Political Lines', category: 'Boundaries', file: 'pw_plrs_all_political_bndry_line.json', color: [220, 38, 38] },
    // { id: 'shoreline', label: 'Shoreline', category: 'Boundaries', file: 'pw_noaa_all_shoreline.json', color: [253, 230, 138] },
    // { id: 'sonsorol', label: 'Sonsorol Boundary', category: 'Boundaries', file: 'pw_plrs_sons_bndry.json', color: [168, 85, 247] },
    // Marshall Islands boundary schemes exported by RMI-boundary.ipynb.
    // Zone schemes are mutually exclusive; their divider lines can be added
    // independently for use over another map product.
    { id: 'rmi_chain_zones', label: 'Ratak / Ralik chains', category: 'Marshall Islands', file: 'rmi_eez_ratak_ralik.geojson', fallbackFile: 'mh_eez_split_ratak_ralik.geojson', color: [14, 165, 233], exclusiveGroup: 'rmi-zone-scheme', colorBy: 'chain', colorMap: { Ratak: '#0ea5e9', Ralik: '#f97316' }, tooltipProperties: ['chain', 'area_km2'] },
    { id: 'rmi_nwse_zones', label: 'Northwest / Southeast zones', category: 'Marshall Islands', file: 'rmi_eez_northwest_southeast.geojson', fallbackFile: 'mh_eez_split_nw_se.geojson', color: [168, 85, 247], exclusiveGroup: 'rmi-zone-scheme', colorBy: 'nw_se', colorMap: { Northwest: '#8b5cf6', Southeast: '#ec4899' }, tooltipProperties: ['nw_se', 'area_km2'] },
    { id: 'rmi_combined_zones', label: 'Chain + directional zones', category: 'Marshall Islands', file: 'rmi_eez_chain_directional.geojson', fallbackFile: 'mh_eez_split_chain_nwse.geojson', color: [34, 197, 94], exclusiveGroup: 'rmi-zone-scheme', colorBy: 'plot_color', tooltipProperties: ['zone', 'area_km2'] },
    { id: 'rmi_latitude_zones', label: 'Southern / Mid / Northern bands', category: 'Marshall Islands', file: 'rmi_eez_latitude_groups.geojson', color: [234, 179, 8], exclusiveGroup: 'rmi-zone-scheme', colorBy: 'lat_group', colorMap: { Southern: '#fdae61', Mid: '#ffffbf', Northern: '#abd9e9' }, tooltipProperties: ['lat_group', 'area_km2'] },
    { id: 'rmi_kwajalein_zones', label: 'North / South (Kwajalein)', category: 'Marshall Islands', file: 'rmi_eez_north_south_kwajalein.geojson', color: [45, 212, 191], exclusiveGroup: 'rmi-zone-scheme', colorBy: 'hemisphere', colorMap: { North: '#2dd4bf', South: '#fb7185' }, tooltipProperties: ['hemisphere', 'area_km2'] },
    { id: 'rmi_scheme_bands', label: 'Geographic scheme bands', category: 'Marshall Islands', file: 'rmi_eez_scheme_bands.geojson', color: [99, 102, 241], exclusiveGroup: 'rmi-zone-scheme', colorBy: 'band', tooltipProperties: ['band', 'area_km2'] },
    { id: 'rmi_undp_zones', label: 'UNDP/GCF drought zones (2019)', category: 'Marshall Islands', file: 'rmi_eez_undp_drought_zones.geojson', color: [239, 68, 68], exclusiveGroup: 'rmi-zone-scheme', outlineOnly: true, oisstScheme: 'undp', sectionProperty: 'zone', colorBy: 'zone', colorMap: { 'Zone 1': ATLAS_RMI_ZONE_COLORS.undp.North, 'Zone 2': ATLAS_RMI_ZONE_COLORS.undp.Central, 'Zone 3': ATLAS_RMI_ZONE_COLORS.undp.South }, tooltipProperties: ['zone', 'area_km2'] },
    { id: 'rmi_practical_belts', label: 'SPC/NOAA drought-operation belts', category: 'Marshall Islands', file: 'rmi_eez_practical_drought_belts.geojson', color: [190, 24, 93], exclusiveGroup: 'rmi-zone-scheme', outlineOnly: true, oisstScheme: 'practical', sectionProperty: 'belt', colorBy: 'belt', colorMap: { 'Northern drought belt': ATLAS_RMI_ZONE_COLORS.practical.North, Central: ATLAS_RMI_ZONE_COLORS.practical.Central, Southern: ATLAS_RMI_ZONE_COLORS.practical.South }, tooltipProperties: ['belt', 'area_km2'] },
    { id: 'rmi_pccsp_rainfall', label: 'PCCSP/BOM-CSIRO rainfall bands (2011)', category: 'Marshall Islands', file: 'rmi_eez_pccsp_rainfall_bands.geojson', color: [59, 130, 246], exclusiveGroup: 'rmi-zone-scheme', outlineOnly: true, oisstScheme: 'pccsp', sectionProperty: 'band', colorBy: 'band', colorMap: ATLAS_RMI_ZONE_COLORS.pccsp, tooltipProperties: ['band', 'area_km2'] },
    { id: 'rmi_chain_divider', label: 'Ratak / Ralik divider', category: 'Marshall Islands', file: 'rmi_boundary_ratak_ralik.geojson', fallbackFile: 'mh_eez_boundary_ratak_ralik.geojson', color: [125, 211, 252], lineOnly: true, tooltipProperties: ['label'] },
    { id: 'rmi_nwse_divider', label: 'Northwest / Southeast divider', category: 'Marshall Islands', file: 'rmi_boundary_northwest_southeast.geojson', fallbackFile: 'mh_eez_boundary_nw_se.geojson', color: [244, 114, 182], lineOnly: true, tooltipProperties: ['label'] },
    { id: 'rmi_atoll_reference', label: 'Atoll names and coastlines', category: 'Marshall Islands', color: [221, 117, 0], layerType: 'rmi-atolls' },
];

const OISST_BOUNDARY_SCHEMES = {
    undp: { label: 'UNDP/GCF drought zones (2019)', overlayId: 'rmi_undp_zones', sectionFromValue: value => ({ 'Zone 1': 'North', 'Zone 2': 'Central', 'Zone 3': 'South' }[value]) },
    practical: { label: 'SPC/NOAA drought-operation belts', overlayId: 'rmi_practical_belts', sectionFromValue: value => ({ 'Northern drought belt': 'North', Central: 'Central', Southern: 'South' }[value]) },
    pccsp: { label: 'PCCSP/BOM-CSIRO rainfall bands (2011)', overlayId: 'rmi_pccsp_rainfall', sectionFromValue: value => ({ North: 'North', Central: 'Central', South: 'South' }[value]) },
};

// --- Tutorial Configuration & Component ---
const isRmiPortal = () => {
    if (typeof window === 'undefined') return false;
    const preview = new URLSearchParams(window.location.search).get('portal');
    return window.location.hostname.toLowerCase() === 'rmi.ocean-portal.org' || preview === 'rmi';
};

const TUTORIAL_STEPS = [
    { target: null, title: "Welcome!", content: "Let's take a quick interactive tour of {{portalName}}. Click 'Next' to continue." },
    { target: '#tutorial-menu-btn', title: "Navigation Menu", content: "Explore different ocean and atmosphere variables. Atmosphere data currently contains our most comprehensive quantity of station datasets!" },
    { target: '#tutorial-info-panel', variable: '3.1.1.3', title: "Imputed Station Data", content: "Variables like 'Total Wet Days' derive from station observations (imputed data). The right panel shows technical descriptions and historical indicator plots. Projections and forecasts are coming soon!" },
    {
        target: '#tutorial-station-select',
        variable: '4.1',
        action: 'aws-station-airai',
        title: 'Automated weather stations',
        content: 'This is the Automated Weather Station dashboard. It starts at Airai AWS; select Next to move to another station.',
    },
    {
        target: '#tutorial-station-select',
        variable: '4.1',
        action: 'aws-station-aimeliik',
        title: 'Move to Aimeliik AWS',
        content: 'The station selector and map have moved to Aimeliik AWS. Select Next to compare another available weather station.',
    },
    {
        target: '#tutorial-station-select',
        variable: '4.1',
        action: 'aws-station-peleliu',
        title: 'Move to Peleliu AWS',
        content: 'Now the dashboard is showing Peleliu AWS. Use Back and Next to revisit each station at your own pace.',
    },
    { target: '#tutorial-variable-select', variable: '4.1', title: "Modifying Variables", content: "You can also use the Visualizing dropdown to modify the specific data variable being plotted on the chart below." },
    { target: '#tutorial-map-container', variable: '6.3.2.1', title: "Ocean Data: Waves", content: "Now let's look at the Ocean! Here is the historical wave hindcast. Notice the animated wind/wave arrows on the map? Dataset visualizations can include animations like this.", closeRightPanel: true },
    { target: '#tutorial-timeline-slider', variable: '7.1.3.1.5', title: "CMIP6 Climate Projections", content: "This is the CMIP6 SSP4-3.4 projection. CMIP6 values for all relevant variables (pH, oxygen, surface air temp, etc.) will soon be filled out. Use the timeline slider at the bottom to 'play' certain variables across time!", closeRightPanel: true },
    { target: null, variable: 'cfs.sst', title: "CFS Forecasts", content: "CFS seasonal forecasts are filed under each theme (SST, wind, rainfall, …). Open Ocean → Water Temperature → Forecasts for ocean temperature, or Atmosphere → Wind for winds.", closeRightPanel: true },
    { target: null, variable: '3.6.2', title: "Other Forecast Models", content: "Other forecast models, such as the APCC Multi-Model Ensemble and ACCESS-S Climate Forecasts, are currently being finalized and are a work in progress!", closeRightPanel: true },
    { target: '#tutorial-mhw-panel', variable: '7.2.1', title: "Marine Heat Waves", content: "Marine Heat Waves offer highly interactive features! Use the floating panel to toggle Geographical Masks, switch between Contour, Gridded, and Voronoi map types, and change time scales. We are expanding these features to the vast majority of map products!", closeRightPanel: true },
    { target: '#tutorial-info-panel', variable: '7.2.1', title: "MHW Historical Plots", content: "Opening the side panel reveals historical plots that toggle based on your active mask. Additionally, clicking any gridded cell on the map acts as a 'virtual station' to show localized heat wave history!", closeRightPanel: false },
    { target: '#tutorial-basemap-btn', variable: '6.1.1.3', title: "Basemap Toggle", content: "Let's explore some global map tools! For any map visualization, you can toggle the background between Satellite and Terrain views.", closeRightPanel: true },
    {
        target: '#tutorial-oisst-scope-btn',
        variable: '7.1.2.2',
        action: 'oisst-local-view',
        title: 'OISST country view',
        content: 'OISST opens in this portal’s country view. When you are ready, select Next to expand the same dataset to the Indo-Pacific.',
        closeRightPanel: true,
    },
    {
        target: '#tutorial-info-panel',
        variable: '6.1.1.3',
        action: 'regional-sea-level-overview',
        title: 'Historical regional sea level',
        content: 'Historical Regional Sea Level combines the gridded satellite record with the local Malakal tide-gauge comparison. Its time-series plot is open in the right panel.',
        closeRightPanel: false,
    },
    {
        target: '#tutorial-map-container',
        variable: '6.1.1.3',
        action: 'regional-sea-level-virtual-station',
        title: 'Grid tiles as virtual stations',
        content: 'A grid tile is now selected. Each tile acts as a virtual station: click another tile on the map to replace the right-panel plot with that location’s altimetry history.',
        closeRightPanel: false,
    },
    {
        target: '#tutorial-oisst-scope-btn',
        variable: '7.1.2.2',
        action: 'oisst-indo-pacific-view',
        title: 'OISST Indo-Pacific view',
        content: 'The extent control now shows the broader Indo-Pacific. Select Next to add the 29 °C convergence-zone diagnostic.',
        closeRightPanel: true,
    },
    {
        target: '#tutorial-convergence-toggle',
        variable: '7.1.2.2',
        action: 'oisst-convergence-overlay',
        title: 'Convergence zone (29 °C)',
        content: 'The Layers menu now shows the enabled 29 °C contour. The pink line is the selected OISST frame; the green line is that month’s historical climatology. Select Next when you are ready to animate it.',
        closeRightPanel: true,
    },
    {
        target: '#tutorial-timeline-slider',
        variable: '7.1.2.2',
        action: 'oisst-convergence-playback',
        title: 'Animate the convergence zone',
        content: 'The timeline is playing. OISST and its selected-frame 29 °C contour advance together until you select Next or Back.',
        closeRightPanel: true,
    },
    { target: '#tutorial-download-btn', variable: '6.1.1.3', title: "Download Data", content: "Use the download button to grab the underlying dataset as a GeoJSON or Shapefile for offline analysis.", closeRightPanel: true },
    { target: '#tutorial-screenshot-btn', variable: '6.1.1.3', title: "Capture Screenshot", content: "Use the camera icon to capture a high-quality screenshot of your current map view, including any active legends.", closeRightPanel: true },
    { target: '#tutorial-interactive-toggles', variable: '6.2.2.3', title: "Interactive Plots", content: "For variables like Flood Frequency and Duration, you can interactively modify the thresholds and plot parameters directly within the right-hand panel." },
    { target: '#tutorial-crw-dials', variable: '7.4.3.1.1', title: "Coral Reef Watch", content: "Check out the Coral Reef Watch dials at the top. Clicking on a gridded tile on the map will dynamically update the dials to reflect the bleaching alert for that specific location!", closeRightPanel: true },
    { target: '#tutorial-home-btn', title: "Home / Info", content: "You're all set! Return to the landing page at any time to see partner info or to restart this tutorial." },
    { target: '#tutorial-reset-view-btn', title: "Reset View", content: "If you ever get lost on the map, click this to return to the full regional view." }
];

const TutorialOverlay = ({ step, setStep, portalName }) => {
    const [targetRect, setTargetRect] = useState(null);
    const prefersReducedMotion = useReducedMotion();

    useEffect(() => {
        const updateRect = () => {
            if (step === 0 || step > TUTORIAL_STEPS.length) return;
            const currentStep = TUTORIAL_STEPS[step - 1];
            const targetSelector = currentStep?.target;
            if (targetSelector) {
                const el = document.querySelector(targetSelector);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    setTargetRect(prev => {
                        // Only update if it significantly changed to avoid infinite re-renders
                        if (!prev || Math.abs(prev.top - rect.top) > 1 || Math.abs(prev.left - rect.left) > 1 || Math.abs(prev.width - rect.width) > 1 || Math.abs(prev.height - rect.height) > 1) {
                            return { top: rect.top, left: rect.left, width: rect.width, height: rect.height, bottom: rect.bottom, right: rect.right };
                        }
                        return prev;
                    });
                } else {
                    setTargetRect(null);
                }
            } else {
                setTargetRect(null);
            }
        };

        updateRect();
        const currentStep = TUTORIAL_STEPS[step - 1];
        const targetSelector = currentStep?.target;
        const target = targetSelector ? document.querySelector(targetSelector) : null;
        const resizeObserver = target && typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updateRect)
            : null;
        const mutationObserver = typeof MutationObserver !== 'undefined'
            ? new MutationObserver(updateRect)
            : null;
        resizeObserver?.observe(target);
        mutationObserver?.observe(document.body, { childList: true, subtree: true });
        window.addEventListener('resize', updateRect);
        window.addEventListener('scroll', updateRect, true);

        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            window.removeEventListener('resize', updateRect);
            window.removeEventListener('scroll', updateRect, true);
        };
    }, [step]);

    if (step === 0 || step > TUTORIAL_STEPS.length) return null;

    const currentStep = TUTORIAL_STEPS[step - 1];
    const isLast = step === TUTORIAL_STEPS.length;
    const isSeaLevelVirtualStationStep = currentStep.action === 'regional-sea-level-virtual-station';
    const stepTitle = currentStep.title;
    const stepContent = currentStep.content.replace('{{portalName}}', portalName);

    return (
        <div className="fixed inset-0 z-[9999] pointer-events-none">
            <AnimatePresence>
                {targetRect && (
                    <motion.div 
                        key="highlight-box"
                        className="absolute border-[3px] border-cyan-400 rounded-xl pointer-events-none z-[10000]"
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: 1,
                            top: targetRect.top - 6,
                            left: targetRect.left - 6,
                            width: targetRect.width + 12,
                            height: targetRect.height + 12,
                            boxShadow: "0 0 16px rgba(34,211,238,0.5)"
                        }}
                        exit={{ opacity: 0 }}
                        transition={prefersReducedMotion ? { duration: 0 } : {
                            top: { type: "spring", stiffness: 400, damping: 30 },
                            left: { type: "spring", stiffness: 400, damping: 30 },
                            width: { type: "spring", stiffness: 400, damping: 30 },
                            height: { type: "spring", stiffness: 400, damping: 30 },
                            opacity: { duration: 0.15 }
                        }}
                    />
                )}
            </AnimatePresence>

            <motion.div 
                key={step}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={clsx(
                    'fixed left-4 right-4 w-auto max-w-sm bg-slate-900/95 border border-cyan-500/50 shadow-xl rounded-xl p-5 pointer-events-auto z-[10001] sm:left-6 sm:right-auto',
                    isSeaLevelVirtualStationStep ? 'top-20 sm:top-24' : 'bottom-4 sm:bottom-6',
                )}
            >
                <div className="mb-2 flex items-center justify-between gap-3">
                    <h3 className="text-cyan-300 font-semibold uppercase tracking-[0.12em] text-xs flex items-center gap-2">
                        <Info size={15} />
                        {stepTitle}
                    </h3>
                    <span className="shrink-0 text-[11px] text-slate-400 font-medium font-mono">{step} / {TUTORIAL_STEPS.length}</span>
                </div>
                <p className="text-slate-200 text-sm mb-5 leading-relaxed">{stepContent}</p>
                <div className="flex justify-between items-center border-t border-white/10 pt-3">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setStep(0)} className="px-1 py-1.5 text-xs text-slate-400 hover:text-white transition-colors">Skip tour</button>
                        <button
                            onClick={() => setStep(Math.max(1, step - 1))}
                            disabled={step === 1}
                            className="px-1 py-1.5 text-xs text-slate-400 hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Back
                        </button>
                    </div>
                    <button 
                        onClick={() => setStep(isLast ? 0 : step + 1)} 
                        className="px-4 py-2 text-xs font-semibold bg-cyan-400 text-[#06283f] rounded-lg hover:bg-cyan-300 transition-colors"
                    >
                        {isLast ? 'Finish' : 'Next'}
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

// The target ring moves only when layout changes; the reading controls remain
// anchored so the tutorial has a predictable spatial origin.

// --- DeckGL interpolation settings ---
const viewStateInterpolate = { // [cite: code-base/App.js]
  transitionDuration: 2500, // Adjusted duration
  transitionInterpolator: new LinearInterpolator(),
};

// --- Initial Map State & Layers (using initial VIEW_STATES) ---
// const initialMapViewState = VIEW_STATES.SECOND; // Use initial state from vars [cite: code-base/App.js]
const initialMap = {
    viewState: VIEW_STATES.SECOND,
    // Layers will be loaded dynamically by MapLoader now based on currentJSON
    layers: [], // Start with empty layers array
};

// The portal hostname/query is a geographic entry point, not merely a theme.
// Keep this shared camera target in one place so variables which still carry
// an older Palau default never strand an RMI visitor over the wrong country.
const RMI_PORTAL_VIEW = Object.freeze({
    latitude: 9.9,
    longitude: 166.5,
    zoom: 4.3,
    bearing: 0,
    pitch: 0,
});

const MAP_ZOOM_HINT_STORAGE_KEY = 'ocean-portal.map-zoom-hint-dismissed.v1';
const MAP_MIN_ZOOM = 0;
const MAP_MAX_ZOOM = 19;

const readMapZoomHintPreference = () => {
    if (typeof window === 'undefined') return true;
    try {
        return window.localStorage.getItem(MAP_ZOOM_HINT_STORAGE_KEY) !== '1';
    } catch {
        return true;
    }
};

const isPalauDefaultView = (view) => (
    view
    // Older products use both the original VIEW_STATES camera and a nearby
    // 7.5°N / 134.5°E reset camera. Treat that local Palau envelope as the
    // legacy default, while leaving basin-scale views untouched.
    && Number(view.latitude) >= 0
    && Number(view.latitude) <= 12
    && Number(view.longitude) >= 128
    && Number(view.longitude) <= 140
);

const CMIP6_VARIABLE_MENU = Object.freeze([
    { group: 'Ocean physics', items: [['Sea surface temperature', 'tos'], ['Sea surface salinity', 'sos'], ['Sea-surface height', 'zos']] },
    { group: 'Atmosphere', items: [['Near-surface air temperature', 'tas'], ['Surface skin temperature', 'ts'], ['Precipitation rate', 'pr'], ['Sea-level pressure', 'psl'], ['Surface air pressure', 'ps'], ['Near-surface wind speed', 'sfcWind']] },
    { group: 'Marine biogeochemistry', items: [['Total alkalinity', 'talk'], ['Sea-water pH', 'ph'], ['Nitrate', 'no3'], ['Phosphate', 'po4'], ['Dissolved oxygen', 'o2'], ['Chlorophyll', 'chl'], ['Primary production', 'pp'], ['Phytoplankton carbon', 'phyc'], ['Zooplankton carbon', 'zooc'], ['Dissolved organic carbon', 'doc']] },
]);

const CMIP6_SCENARIO_LABELS = Object.freeze({
    historical: 'Historical (1850–2014)',
    ssp126: 'SSP1-2.6',
    ssp434: 'SSP4-3.4',
    ssp460: 'SSP4-6.0',
    'ssp534-over': 'SSP5-3.4-over',
    ssp585: 'SSP5-8.5',
});

const CMIP6_SCENARIO_ORDER = Object.freeze([
    'historical', 'ssp126', 'ssp434', 'ssp460', 'ssp534-over', 'ssp585',
]);

const CMIP7_VARIABLE_MENU = Object.freeze([
    {
        group: 'Atmosphere',
        items: [
            ['Near-surface air temperature', 'tas'],
            ['Precipitation rate', 'pr'],
            ['Sea-level pressure', 'psl'],
            ['Surface air pressure', 'ps'],
            ['Near-surface wind speed', 'sfcWind'],
        ],
    },
]);

const CMIP7_SCENARIO_LABELS = Object.freeze({
    'esm-hist': 'ESM Historical',
    'esm-scen7-vl': 'ESM Very Low',
    'esm-scen7-h': 'ESM High',
});

const CMIP7_SCENARIO_ORDER = Object.freeze(['esm-hist', 'esm-scen7-vl', 'esm-scen7-h']);
// CMIP7 remains implemented behind a deliberate menu flag while its catalogue
// is still too incomplete for a public-facing portal product.
const SHOW_CMIP7_IN_MENU = false;

const cmip6ScenarioFromSelection = (value) => {
    const match = /^cmip[67]\.[^.]+\.(.+)$/.exec(value || '');
    if (match) return match[1];
    return ({
        '7.1.3.1.2': 'ssp126',
        '7.1.3.1.5': 'ssp434',
        '7.1.3.1.6': 'ssp460',
        '7.1.3.1.7': 'ssp534-over',
        '7.1.3.1.8': 'ssp585',
        '7.1.3.3': 'historical',
    })[value] || 'ssp126';
};

const isCmip6Selection = (value) => Boolean(value?.startsWith('7.1.3') || value?.startsWith('cmip6.'));
const isCmip7Selection = (value) => Boolean(value?.startsWith('cmip7.'));
const isCmip7CatalogueSelection = (value) => value === 'cmip7.catalogue';
const isCmip7PreviewSelection = (value) => isCmip7Selection(value) && !isCmip7CatalogueSelection(value);
const isCmipProjectionSelection = (value) => isCmip6Selection(value) || isCmip7PreviewSelection(value);
const cmip6VariableFromSelection = (value) => /^cmip[67]\./.test(value || '') ? value.split('.')[1] : 'tos';

const cmip6ScenarioMenuChildren = (variableKey, catalogue) => {
    const publishedScenarios = new Set((catalogue?.variables?.[variableKey]?.products || [])
        .map((product) => product.scenario)
        .filter(Boolean));
    return CMIP6_SCENARIO_ORDER
        .filter((scenario) => publishedScenarios.has(scenario))
        .map((scenario) => getItem(
            CMIP6_SCENARIO_LABELS[scenario] || scenario,
            `cmip6.${variableKey}.${scenario}`,
        ));
};

const cmip7ScenarioMenuChildren = (variableKey, catalogue) => {
    const publishedScenarios = new Set((catalogue?.variables?.[variableKey]?.products || [])
        .map((product) => product.scenario)
        .filter(Boolean));
    return CMIP7_SCENARIO_ORDER.map((scenario) => getItem(
        `${CMIP7_SCENARIO_LABELS[scenario]}${publishedScenarios.has(scenario) ? '' : ' · pending'}`,
        `cmip7.${variableKey}.${scenario}`,
    ));
};

const initialMapLayers = [ // Define initial layers separately [cite: code-base/App.js]
  new GeoJsonLayer({
    id:'palau', data: '/palau.json', getElevation: 5, stroke:true, filled: false, lineWidthScale: 20, lineWidthMinPixels: 2, getLineWidth: 2, getLineColor: [0,188,255], pickable: true, onClick: false
  }),
  new GeoJsonLayer({
    id:'eez', data: '/CMIPtas/palauEEZ.fgb', loaders: [FlatGeobufLoader], getElevation: 5, stroke:true, filled: false, lineWidthScale: 20, lineWidthMinPixels: 2, getLineWidth: 2, getFillColor: [0,188,255], pickable: false
  }),
];

// --- Helper to build the nested list structure (Glassmorphism) ---
function RenderMenuItems({ items, onItemClick, level = 0, openState, setOpenState }) {

    const handleClick = (key) => {
        setOpenState(prev => {
            const opening = !prev[key];
            if (level === 0) {
                // Ocean and Atmosphere are an accordion. Leaving both marked
                // open made the next top-level click close the destination
                // instead of revealing it (most visible after APCC -> CRW).
                return { ...prev, ocean: false, atm: false, [key]: opening };
            }
            return { ...prev, [key]: opening };
        });
    };

    return (
        <ul className={clsx("flex flex-col gap-1", level > 0 && "ml-4 border-l border-white/20 pl-2")}>
            {items.map((item) => {
                const Icon = item.icon || BarChart3;
                const hasChildren = item.children && item.children.length > 0;
                const isOpen = openState[item.key];

                return (
                    <li key={item.key}>
                        <button
                            type="button"
                            onClick={() => hasChildren ? handleClick(item.key) : onItemClick({ key: item.key })}
                            className={clsx(
                                "group flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                                // Glass Effects
                                "hover:bg-white/10 hover:backdrop-blur-md hover:shadow-sm",
                                "active:scale-[0.98]",
                                "text-blue-50 hover:text-white",
                                isOpen && "bg-white/5"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <Icon className={clsx("h-4 w-4 opacity-70 transition-opacity group-hover:opacity-100")} />
                                <span>{item.label}</span>
                            </div>
                            {hasChildren && (
                                <div className={clsx("transition-transform duration-200", isOpen && "rotate-180")}>
                                    <ChevronDown className="h-4 w-4 opacity-50" />
                                </div>
                            )}
                        </button>
                        
                        <AnimatePresence>
                            {hasChildren && isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                >
                                    <RenderMenuItems items={item.children} onItemClick={onItemClick} level={level + 1} openState={openState} setOpenState={setOpenState} />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </li>
                );
            })}
        </ul>
    );
}

// --- getItem function definition --- [cite: code-base/App.js]
function getItem(label, key, icon, children, type) {
  return { key, icon, children, label, type };
}

// --- Main App Component ---
function App() {
    // --- State Management ---
    const [state, dispatch] = useReducer(dashboardReducer, initialState);
    const rmiPortal = isRmiPortal();
    useEffect(() => {
        document.documentElement.dataset.portalVariant = rmiPortal ? 'rmi' : 'palau';
        return () => { delete document.documentElement.dataset.portalVariant; };
    }, [rmiPortal]);
    // Deck.gl holds layer callbacks across a render while an FGB layer is
    // loading. Read the current selection through a ref so a click never uses
    // the previous menu item's closure (notably CRW legacy alert vs daily).
    const selectedVariableRef = useRef(state.variableSelected);
    selectedVariableRef.current = state.variableSelected;
    const scatterplotVariableRef = useRef(state.scatterplotVariable);
    scatterplotVariableRef.current = state.scatterplotVariable;
    const isPlayingRef = useRef(state.isPlaying);
    isPlayingRef.current = state.isPlaying;
    const dateSelectedRef = useRef(state.dateSelected);
    dateSelectedRef.current = state.dateSelected;
    // Native 5 km BAA cells contain the finest current condition. Keep the
    // matching all-dial alert grid in a ref for the three outlook values used
    // by click and hover, without forcing deck.gl layer recreation.
    const crwNativeDialGridRef = useRef({});
    const mapContainerRef = useRef(null);
    
    // --- Draggable Legend State & Handlers ---
    const [legendPos, setLegendPos] = useState({ x: 15, y: 160, pushedDistance: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const legendContainerRef = useRef(null);
    const prevMobileOpen = useRef(null);

    const handleMouseDown = useCallback((e) => {
        e.stopPropagation(); // Prevent map click/drag when interacting with the legend
        
        // Prevent drag on interactive elements inside the legend like buttons or toggles
        if (e.target.closest('button, a, select, input')) return;
        
        if (legendContainerRef.current) {
            const rect = legendContainerRef.current.getBoundingClientRect();
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
            e.preventDefault();
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !legendContainerRef.current) return;

        const parentRect = legendContainerRef.current.parentElement.getBoundingClientRect();
        
        let newX = e.clientX - parentRect.left - dragOffset.x;
        // Y is calculated from top, but we store position from bottom.
        let newY = parentRect.height - (e.clientY - parentRect.top) - (legendContainerRef.current.offsetHeight - dragOffset.y);

        // Constrain within parent bounds
        newX = Math.max(0, Math.min(newX, parentRect.width - legendContainerRef.current.offsetWidth));
        newY = Math.max(0, Math.min(newY, parentRect.height - legendContainerRef.current.offsetHeight));

        setLegendPos(prev => ({ ...prev, x: newX, y: newY }));
    }, [isDragging, dragOffset]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const { unit } = useTemperatureUnit();

    const [tutorialStep, setTutorialStep] = useState(0);
    const handleStartTutorial = useCallback(() => {
        setLandingOpen(false);
        setTutorialStep(1);
    }, []);

    // UI State (kept separate as they are orthogonal to data selection)
    const [mapState, setMapState] = useState(() => (
        rmiPortal
            ? { ...initialMap, viewState: RMI_PORTAL_VIEW }
            : initialMap
    ));
    const [mapInteractionActive, setMapInteractionActive] = useState(false);
    const [showMapZoomHint, setShowMapZoomHint] = useState(readMapZoomHintPreference);

    useEffect(() => {
        const mapElement = mapContainerRef.current;
        if (!mapElement) return undefined;

        const containTrackpadPinch = (event) => {
            // Browsers expose a trackpad pinch as a ctrl+wheel gesture. Let
            // DeckGL consume it as map zoom, then suppress only the browser's
            // page-zoom default while the pointer is inside this map surface.
            if (event.ctrlKey && event.cancelable) event.preventDefault();
        };

        mapElement.addEventListener('wheel', containTrackpadPinch, { passive: false });
        return () => mapElement.removeEventListener('wheel', containTrackpadPinch);
    }, []);

    // --- New State for MUI UI ---
    const [mobileOpen, setMobileOpen] = useState(true); // Left drawer state
    const [rightDrawerOpen, setRightDrawerOpen] = useState(false); // Right drawer state
    const [pccStation, setPccStation] = useState('PCC-CRE_Angaur'); // PCC Station State
    const [awsStation, setAwsStation] = useState('PW74351'); // AWS Station State
    const [menuOpenState, setMenuOpenState] = useState({}); // Keep track of expanded menu items globally
    const [animationSpeed, setAnimationSpeed] = useState(1000);
    const [showWindParticles, setShowWindParticles] = useState(true);
    const [windRenderMode, setWindRenderMode] = useState('particles'); // 'particles' | 'vectors' | 'barbs'
    // Wake Island is an optional, explicitly labelled analysis area. It is
    // never merged silently into the official Republic of the Marshall
    // Islands EEZ: the switch replaces only the displayed boundary with the
    // combined RMI + Wake geometry. Product-specific RMI + Wake statistics
    // can opt in to the same state as they are published by the pipelines.
    const [rmiIncludeWake, setRmiIncludeWake] = useState(false);
    const [basemap, setBasemap] = useState('satellite'); // 'terrain' or 'satellite'
    const [landingOpen, setLandingOpen] = useState(true); // Landing Page State
    const [oisstMode, setOisstMode] = useState('gridded'); // 'gridded' or 'contour'
    // Geographic scope cycles: Palau → RMI → Indo-Pacific → Palau. The RMI
    // hostname starts country-capable products in its own EEZ by default.
    const [oisstViewScope, setOisstViewScope] = useState(() => (rmiPortal ? 'rmi' : 'palau')); // 'palau' | 'rmi' | 'indo-pacific'
    // RMI SST is intentionally always a regional comparison.  The selected
    // scheme controls the North/Central/South definitions used by the chart.
    const [oisstBoundaryScheme, setOisstBoundaryScheme] = useState('undp');
    const [oisstBoundarySection, setOisstBoundarySection] = useState('North');
    const oisstRegion = oisstViewScope === 'rmi' ? 'rmi' : 'palau';
    const oisstIndoPacific = oisstViewScope === 'indo-pacific';
    const activeOisstBoundary = OISST_BOUNDARY_SCHEMES[oisstBoundaryScheme];
    const oisstAverageCsvUrl = activeOisstBoundary && oisstRegion === 'rmi'
        ? `/jsonFiles/oisst/rmi/boundaries/${oisstBoundaryScheme}_${oisstBoundarySection.toLowerCase()}.csv`
        : oisstRegionMetadata[oisstRegion]?.averageCsvUrl;
    const oisstBoundaryComparisonCsvUrl = activeOisstBoundary && oisstRegion === 'rmi'
        ? `/jsonFiles/oisst/rmi/boundaries/${oisstBoundaryScheme}_comparison.csv`
        : null;
    const oisstRegionPlotLabel = activeOisstBoundary && oisstRegion === 'rmi'
        ? `${activeOisstBoundary.label} — ${oisstBoundarySection}`
        : (oisstIndoPacific ? 'Indo-Pacific' : oisstRegionMetadata[oisstRegion]?.label);
    const oisstViewScopeLabel = oisstViewScope === 'indo-pacific'
        ? 'Indo-Pacific'
        : (oisstViewScope === 'rmi' ? 'RMI' : 'Palau');
    useEffect(() => {
        // Also migrates an existing hot-reload/session state from the retired
        // single-EEZ mode to the RMI four-line comparison.
        if (oisstViewScope === 'rmi' && oisstBoundaryScheme === 'eez') {
            setOisstBoundaryScheme('undp');
        }
    }, [oisstViewScope, oisstBoundaryScheme]);
    const cycleOisstViewScope = useCallback(() => {
        setOisstViewScope((prev) => {
            if (!rmiPortal) return prev === 'indo-pacific' ? 'palau' : 'indo-pacific';
            if (prev === 'palau') return 'rmi';
            if (prev === 'rmi') return 'indo-pacific';
            return 'palau';
        });
    }, [rmiPortal]);
    // CFS / GFS share the same extent + style UX as OISST; variable comes from the menu leaf
    const [forecastMode, setForecastMode] = useState('gridded'); // 'gridded' | 'contour'
    const [forecastViewScope, setForecastViewScope] = useState(() => (rmiPortal ? 'rmi' : 'palau')); // 'palau' | 'rmi' | 'indo-pacific'
    const [waveViewScope, setWaveViewScope] = useState(() => (rmiPortal ? 'rmi' : 'palau'));
    // WaveWatch opens as a readable height grid with its animated direction
    // field above it. Contours remain an explicit alternate display.
    const [waveRenderMode, setWaveRenderMode] = useState('gridded');
    const [waveVectorMode, setWaveVectorMode] = useState('animated');
    // Monthly Ocean Color frames use the same map/timeline model as the wind
    // products.  The contour choice is an intentionally cheaper alternative
    // for older devices, while grid keeps per-cell inspection available.
    const [oceanColorMode, setOceanColorMode] = useState('gridded');
    // Only offer published extents.  A failed/partial publisher run must not
    // leave the RMI portal pointing at a path with no frames to render.
    const availableRegionalSeaLevelScopes = useMemo(
        () => ['palau', 'rmi', 'indo'].filter((scope) => (
            (rmiPortal || scope !== 'rmi') && regionalSeaLevelMetadata[scope]?.dates?.length
        )),
        [rmiPortal],
    );
    const [regionalSeaLevelScope, setRegionalSeaLevelScope] = useState(() => {
        const portalDefault = rmiPortal ? 'rmi' : 'palau';
        return regionalSeaLevelMetadata[portalDefault]?.dates?.length
            ? portalDefault
            : (availableRegionalSeaLevelScopes[0] || 'palau');
    });
    const [regionalSeaLevelMode, setRegionalSeaLevelMode] = useState('gridded');
    const [cmip6Catalog, setCmip6Catalog] = useState(null);
    const [cmip7ProjectionCatalog, setCmip7ProjectionCatalog] = useState(cmip7ProjectionCatalogFallback);
    const [cmip6Scope, setCmip6Scope] = useState(() => (rmiPortal ? 'rmi' : 'palau'));
    const [cmip6RenderMode, setCmip6RenderMode] = useState('native');
    const [cmip6Model, setCmip6Model] = useState('');
    const activeCmip6Variable = cmip6VariableFromSelection(state.variableSelected);
    const activeCmip6Scenario = cmip6ScenarioFromSelection(state.variableSelected);
    const activeProjectionCatalog = isCmip7PreviewSelection(state.variableSelected)
        ? cmip7ProjectionCatalog
        : cmip6Catalog;
    const activeCmip6Manifest = activeProjectionCatalog?.variables?.[activeCmip6Variable] || null;
    const effectiveCmip6Scope = rmiIncludeWake && cmip6Scope === 'rmi' ? 'rmi-wake' : cmip6Scope;
    const activeCmip6Modes = useMemo(() => [...new Set((activeCmip6Manifest?.products || [])
        .filter((product) => product.scope === effectiveCmip6Scope && product.scenario === activeCmip6Scenario)
        .flatMap((product) => Object.keys(product.modes || {})))], [activeCmip6Manifest, activeCmip6Scenario, effectiveCmip6Scope]);
    const activeProjectionScenarioOrder = isCmip7PreviewSelection(state.variableSelected)
        ? CMIP7_SCENARIO_ORDER
        : CMIP6_SCENARIO_ORDER;
    const activeProjectionScenarioLabels = isCmip7PreviewSelection(state.variableSelected)
        ? CMIP7_SCENARIO_LABELS
        : CMIP6_SCENARIO_LABELS;
    const activeCmip6Scenarios = useMemo(() => {
        const published = activeProjectionScenarioOrder.filter((scenario) => (
            activeCmip6Manifest?.products?.some((product) => product.scenario === scenario)
        ));
        return isCmip7PreviewSelection(state.variableSelected) && !published.length
            ? [...CMIP7_SCENARIO_ORDER]
            : published;
    }, [activeCmip6Manifest, activeProjectionScenarioOrder, state.variableSelected]);
    const regionalSeaLevelScopeLabel = regionalSeaLevelScope === 'indo'
        ? 'Indo-Pacific'
        : (regionalSeaLevelScope === 'rmi' ? 'Marshall Islands' : 'Palau');
    const cycleRegionalSeaLevelScope = useCallback(() => {
        setRegionalSeaLevelScope((previous) => {
            const currentIndex = availableRegionalSeaLevelScopes.indexOf(previous);
            return availableRegionalSeaLevelScopes[(currentIndex + 1) % availableRegionalSeaLevelScopes.length] || previous;
        });
    }, [availableRegionalSeaLevelScopes]);
    const waveViewScopeLabel = waveViewScope === 'rmi' ? 'Marshall Islands' : 'Palau';
    const forecastRegion = forecastViewScope === 'rmi' ? 'rmi' : 'palau';
    const forecastIndoPacific = forecastViewScope === 'indo-pacific';
    const forecastViewScopeLabel = forecastViewScope === 'indo-pacific'
        ? 'Indo-Pacific'
        : (forecastViewScope === 'rmi' ? 'RMI' : 'Palau');
    const cycleForecastViewScope = useCallback(() => {
        setForecastViewScope((prev) => {
            if (!rmiPortal) return prev === 'indo-pacific' ? 'palau' : 'indo-pacific';
            if (prev === 'palau') return 'rmi';
            if (prev === 'rmi') return 'indo-pacific';
            return 'palau';
        });
    }, [rmiPortal]);
    const forecastParsed = parseForecastId(state.variableSelected);
    const isForecastSelected = Boolean(forecastParsed);
    const forecastModel = forecastParsed?.model || 'cfs';
    const activeForecastVar = forecastParsed?.varKey || 'sst';
    const forecastVarMetaAll = forecastModel === 'gfs' ? gfsVariableMetadata : cfsVariableMetadata;
    const forecastRegionMetaAll = forecastModel === 'gfs' ? gfsRegionMetadata : cfsRegionMetadata;
    const activeForecastVarMeta = forecastVarMetaAll[activeForecastVar] || {};
    const isForecastWindSelected = state.variableSelected === 'gfs.wind' || state.variableSelected === 'cfs.wind';
    const [apccMode, setApccMode] = useState('gridded');
    const [apccViewScope, setApccViewScope] = useState(() => (rmiPortal ? 'rmi' : 'palau'));
    const [apccAggregation, setApccAggregation] = useState('monthly');
    const apccParsed = parseApccId(state.variableSelected);
    const isApccSelected = Boolean(apccParsed);
    const activeApccVariableMeta = apccParsed ? APCC_VARIABLES[apccParsed.variable] : null;
    const apccScopeLocked = Boolean(activeApccVariableMeta && !activeApccVariableMeta.local);
    const effectiveApccScope = apccScopeLocked ? 'indo-pacific' : apccViewScope;
    const apccAvailableAggregations = apccParsed
        ? getApccAvailableAggregations(apccParsed, effectiveApccScope, apccMode)
        : [];
    const cycleApccViewScope = useCallback(() => {
        setApccViewScope(previous => {
            if (!rmiPortal) return previous === 'indo-pacific' ? 'palau' : 'indo-pacific';
            if (previous === 'palau') return 'rmi';
            if (previous === 'rmi') return 'indo-pacific';
            return 'palau';
        });
    }, [rmiPortal]);
    const [crwPacific, setCrwPacific] = useState(false); // opt-in: whole-Pacific CRW view
    const [crwRes, setCrwRes] = useState('native');      // CRW display resolution tier
    const [crwScope, setCrwScope] = useState(() => (rmiPortal ? 'rmi' : 'palau'));   // daily CRW map scope
    // A daily-history request is opt-in: opening a CRW product never starts a
    // multi-decade download until the user selects an actual map cell.
    const [crwDailyHistoryKey, setCrwDailyHistoryKey] = useState(null);
    const [crwAlertMode, setCrwAlertMode] = useState('legacy'); // default to NOAA's pre-2023 five-level viewer
    // Open the bleaching alert on its archived weekly series so the timeline
    // is immediately available. Native 5 km remains an explicit current-only
    // option in the CRW controls.
    const [crwNativeAlert, setCrwNativeAlert] = useState(false);
    const [timelineCollapsed, setTimelineCollapsed] = useState(false); // Timeline collapsed state
    const [layersMenuAnchor, setLayersMenuAnchor] = useState(null); // Map Overlays Menu
    const [activeBoundaryLayers, setActiveBoundaryLayers] = useState(() => (
        rmiPortal ? ['rmi_atoll_reference'] : []
    )); // Active layer IDs
    const [boundaryLayerData, setBoundaryLayerData] = useState({});
    const [convergenceManifest, setConvergenceManifest] = useState(null);
    const [convergenceManifestError, setConvergenceManifestError] = useState(false);
    const [convergenceEnabled, setConvergenceEnabled] = useState(false);
    const [convergenceCurrentVisible, setConvergenceCurrentVisible] = useState(true);
    const [convergenceClimatologyVisible, setConvergenceClimatologyVisible] = useState(true);

    useEffect(() => {
        let cancelled = false;
        fetch('/jsonFiles/cmip6/manifest.json')
            .then(response => response.ok ? response.json() : null)
            .then(value => { if (!cancelled) setCmip6Catalog(value); })
            .catch(() => { if (!cancelled) setCmip6Catalog(null); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        fetch('/jsonFiles/cmip7/projections/manifest.json')
            .then(response => response.ok ? response.json() : null)
            .then(value => { if (!cancelled) setCmip7ProjectionCatalog(value || cmip7ProjectionCatalogFallback); })
            .catch(() => { if (!cancelled) setCmip7ProjectionCatalog(cmip7ProjectionCatalogFallback); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setConvergenceManifestError(false);
        fetch('/jsonFiles/convergence-zone/manifest.json')
            .then((response) => {
                if (!response.ok) throw new Error(`Convergence manifest returned ${response.status}`);
                return response.json();
            })
            .then((value) => { if (!cancelled) setConvergenceManifest(value); })
            .catch((error) => {
                console.warn('Convergence-zone overlay unavailable', error);
                if (!cancelled) setConvergenceManifestError(true);
            });
        return () => { cancelled = true; };
    }, []);

    const convergenceContext = useMemo(() => {
        const selectedValue = state.dateSelected?.[state.sliderValue] || '';
        const stamp = convergenceTimestamp(selectedValue);
        const isOisst = state.variableSelected === '7.1.2.2';
        const isForecastSst = forecastParsed?.varKey === 'sst'
            && ['cfs', 'gfs'].includes(forecastParsed?.model);
        const isEkman = isEkmanVariable(state.variableSelected);
        const eligible = isOisst || isForecastSst || isEkman;
        if (!eligible) return { eligible: false, paths: [] };
        if (!stamp || !convergenceManifest) {
            return { eligible: true, paths: [], selectedValue, status: stamp ? 'Loading contour catalogue…' : 'No dated frame selected.' };
        }

        let source = isOisst ? 'oisst' : forecastParsed?.model;
        let currentKey = stamp.date;
        if (state.variableSelected === 'ekman.metop') source = 'oisst';
        if (state.variableSelected === 'ekman.cfs') {
            source = 'cfs';
            currentKey = `${stamp.year}-${stamp.month}-01`;
        }
        if (state.variableSelected === 'ekman.gfs') {
            const exactSource = convergenceManifest.sources?.['ekman-gfs'];
            if (exactSource?.dates?.includes(stamp.exact)) {
                source = 'ekman-gfs';
                currentKey = stamp.exact;
            } else {
                // Existing Ekman archives predate the paired-temperature
                // publisher. A same-day GFS SST frame is used only when it is
                // explicitly present; otherwise the UI reports it unavailable.
                source = 'gfs';
                currentKey = stamp.date;
            }
        }

        const sourceMeta = convergenceManifest.sources?.[source];
        const hasCurrent = Boolean(sourceMeta?.dates?.includes(currentKey));
        const currentUrl = hasCurrent
            ? sourceMeta.url_template.replace('{date}', currentKey)
            : null;
        const climatology = convergenceManifest.climatology;
        const hasClimatology = Boolean(climatology?.months?.includes(stamp.month));
        const climatologyUrl = hasClimatology
            ? climatology.url_template.replace('{month}', stamp.month)
            : null;
        const paths = [
            convergenceEnabled && convergenceClimatologyVisible ? climatologyUrl : null,
            convergenceEnabled && convergenceCurrentVisible ? currentUrl : null,
        ].filter(Boolean);
        const currentLabel = currentUrl
            ? `${convergenceDateLabel(stamp, sourceMeta.temporal_resolution)} · ${sourceMeta.label}`
            : '';
        const climatologyLabel = climatologyUrl
            ? `${CONVERGENCE_MONTHS[Number(stamp.month) - 1]} climatology · ${climatology.baseline} ${climatology.source_label}`
            : '';
        const status = !currentUrl && !climatologyUrl
            ? 'No 29°C contour is published for this frame.'
            : (!currentUrl ? 'The current-source contour is not published for this frame.' : '');
        return {
            eligible,
            paths,
            source,
            sourceLabel: sourceMeta?.label || '',
            currentUrl,
            climatologyUrl,
            currentLabel,
            climatologyLabel,
            status,
        };
    }, [
        convergenceClimatologyVisible,
        convergenceCurrentVisible,
        convergenceEnabled,
        convergenceManifest,
        forecastParsed?.model,
        forecastParsed?.varKey,
        state.dateSelected,
        state.sliderValue,
        state.variableSelected,
    ]);

    useEffect(() => {
        if (isCmipProjectionSelection(state.variableSelected)) setCmip6Scope(rmiPortal ? 'rmi' : 'palau');
    }, [state.variableSelected, rmiPortal]);

    // Boundary assets are stored as FlatGeobuf when published.  Loading them
    // through getJSON lets a logical .geojson URL fall back to a legacy
    // GeoJSON asset while an FGB export is being regenerated, rather than
    // handing CRA's HTML 404 page to the FlatGeobuf worker.
    useEffect(() => {
        let cancelled = false;
        const configs = activeBoundaryLayers
            .map(id => BOUNDARY_LAYERS_CONFIG.find(config => config.id === id))
            .filter(config => config?.file);
        if (!configs.length) return undefined;
        Promise.all(configs.map(async config => {
            const primaryUrl = `/jsonFiles/boundary_layers/${config.file}`;
            try {
                return [config.id, await getJSON(primaryUrl)];
            } catch (error) {
                if (!config.fallbackFile) {
                    console.warn(`Boundary layer unavailable: ${config.file}`, error);
                    return [config.id, null];
                }
                return [config.id, await getJSON(`/jsonFiles/boundary_layers/${config.fallbackFile}`)];
            }
        })).then(entries => {
            if (!cancelled) {
                setBoundaryLayerData(previous => ({ ...previous, ...Object.fromEntries(entries.filter(([, data]) => data)) }));
            }
        }).catch(error => console.warn('Unable to load boundary overlays', error));
        return () => { cancelled = true; };
    }, [activeBoundaryLayers]);

    useEffect(() => {
        if (state.variableSelected !== '7.1.2.2') return;
        setActiveBoundaryLayers(previous => {
            const withoutRmiSchemes = previous.filter(id => BOUNDARY_LAYERS_CONFIG.find(layer => layer.id === id)?.exclusiveGroup !== 'rmi-zone-scheme');
            return oisstViewScope === 'rmi' && activeOisstBoundary
                ? [...withoutRmiSchemes, activeOisstBoundary.overlayId]
                : withoutRmiSchemes;
        });
    }, [state.variableSelected, oisstViewScope, activeOisstBoundary]);

    // Country-aware products always enter in the country represented by the
    // hostname/query portal. A previous visit to another scope must not leak
    // into a new OISST or CRW selection.
    useEffect(() => {
        if (state.variableSelected !== '7.1.2.2') return;
        setOisstViewScope(rmiPortal ? 'rmi' : 'palau');
        if (rmiPortal) {
            setOisstBoundaryScheme('undp');
            setOisstBoundarySection('North');
        }
    }, [state.variableSelected, rmiPortal]);

    useEffect(() => {
        if (!state.variableSelected?.startsWith('7.4.3') && !state.variableSelected?.startsWith('7.4.4')) return;
        setCrwScope(rmiPortal ? 'rmi' : 'palau');
        setCrwPacific(false);
    }, [state.variableSelected, rmiPortal]);

    useEffect(() => {
        // The wind chart and map must always use the same RMI zonation. The
        // selector therefore replaces any previous RMI-zone overlay with the
        // corresponding outline-only boundary layer.
        if (!isForecastWindSelected || forecastViewScope !== 'rmi' || !activeOisstBoundary) return;
        setActiveBoundaryLayers(previous => {
            const withoutRmiSchemes = previous.filter(id => (
                BOUNDARY_LAYERS_CONFIG.find(layer => layer.id === id)?.exclusiveGroup !== 'rmi-zone-scheme'
            ));
            return [...withoutRmiSchemes, activeOisstBoundary.overlayId];
        });
    }, [isForecastWindSelected, forecastViewScope, activeOisstBoundary]);

    // Forecast leaves start in the host portal's EEZ when it is available.
    // Tropical Cyclone and Ekman own their larger operational views through
    // their dedicated configurations.
    useEffect(() => {
        if (!isForecastSelected) return;
        setForecastViewScope(rmiPortal ? 'rmi' : 'palau');
        setForecastMode('gridded');
    }, [state.variableSelected, isForecastSelected, rmiPortal]);

    // The historical wave archive has the same two national scopes.  Start
    // each portal in its home EEZ and refresh when the user changes scope.
    useEffect(() => {
        if (!['6.3.2.1', '6.3.2.2', '6.3.2.3'].includes(state.variableSelected)) return;
        setWaveViewScope(rmiPortal ? 'rmi' : 'palau');
        setWaveRenderMode('gridded');
    }, [state.variableSelected, rmiPortal]);

    useEffect(() => {
        if (!isApccSelected) return;
        setApccMode('gridded');
        setApccAggregation('monthly');
        setApccViewScope(activeApccVariableMeta?.local ? (rmiPortal ? 'rmi' : 'palau') : 'indo-pacific');
    }, [state.variableSelected, isApccSelected, activeApccVariableMeta?.local, rmiPortal]);

    // --- Hurricane tracker (Windy-style model toggles) ---
    const hurricaneStorms = useMemo(() => hurricaneActiveStorms.filter(storm => (
        Number.isFinite(storm?.lat) && Number.isFinite(storm?.lon)
        && (Math.abs(storm.lat) > 0.05 || Math.abs(storm.lon) > 0.05)
        && Number.isFinite(storm?.vmax_kt)
    )), []);
    const [hurricaneStormId, setHurricaneStormId] = useState(hurricaneStorms[0]?.id || '');
    const [hurricaneSelectedModel, setHurricaneSelectedModel] = useState('ofcl');
    const [hurricaneSpinAngle, setHurricaneSpinAngle] = useState(0);
    const [hurricaneTimelinePoints, setHurricaneTimelinePoints] = useState([]);
    const [hurricaneDetailsOpen, setHurricaneDetailsOpen] = useState(false);
    const [hurricaneDetailsSnap, setHurricaneDetailsSnap] = useState('half');
    const [hurricaneLegendOpen, setHurricaneLegendOpen] = useState(false);
    const [hurricaneFocusMode, setHurricaneFocusMode] = useState(false);
    const isHurricaneSelected = state.variableSelected === 'hurricane.tracker';
    const activeHurricaneStorm = useMemo(
        () => hurricaneStorms.find(s => s.id === hurricaneStormId) || hurricaneStorms[0] || null,
        [hurricaneStormId, hurricaneStorms]
    );
    const availableHurricaneModels = useMemo(() => (
        Object.values(hurricaneModels).filter(model => (
            activeHurricaneStorm?.models?.includes(model.id)
        ))
    ), [activeHurricaneStorm]);
    const hurricaneUpdatedRelative = useMemo(() => formatRelativeTime(hurricaneUpdated), []);
    const hurricaneUpdatedExact = useMemo(() => {
        const timestamp = Date.parse(hurricaneUpdated || '');
        return Number.isFinite(timestamp) ? new Date(timestamp).toISOString().replace('.000Z', 'Z') : hurricaneUpdated;
    }, []);

    // The mobile tracker starts in a deliberately quiet overview: one storm,
    // one selected forecast and a collapsed timeline. Details remain one tap
    // away instead of competing with the track itself.
    useEffect(() => {
        if (!isHurricaneSelected) return;
        setHurricaneDetailsOpen(false);
        setHurricaneDetailsSnap('half');
        setHurricaneLegendOpen(false);
        setHurricaneFocusMode(false);
        setTimelineCollapsed(true);
    }, [isHurricaneSelected]);

    useEffect(() => {
        if (!isHurricaneSelected || !activeHurricaneStorm) {
            setHurricaneTimelinePoints([]);
            return undefined;
        }
        const controller = new AbortController();
        const base = `${hurricaneBasePath}/${activeHurricaneStorm.id}`;
        Promise.all(['points_history.geojson', `points_${hurricaneSelectedModel || 'ofcl'}.geojson`]
            .map(file => fetch(`${base}/${file}`, { signal: controller.signal }).then(r => r.ok ? r.json() : { features: [] })))
            .then(data => {
                if (!controller.signal.aborted) setHurricaneTimelinePoints(data.flatMap(d => d.features || [])
                    .filter(p => Number.isFinite(Date.parse(p?.properties?.valid)) && Array.isArray(p?.geometry?.coordinates))
                    .sort((a, b) => Date.parse(a.properties.valid) - Date.parse(b.properties.valid)));
            })
            .catch(error => { if (error?.name !== 'AbortError') console.warn('Hurricane timeline unavailable', error); });
        return () => controller.abort();
    }, [isHurricaneSelected, activeHurricaneStorm, hurricaneSelectedModel]);

    const hurricaneTimelineStatus = useMemo(() => {
        if (!hurricaneTimelinePoints.length) return null;
        const target = Date.parse(state.dateSelected?.[state.sliderValue] || activeHurricaneStorm?.latest_time || '');
        if (!Number.isFinite(target)) return hurricaneTimelinePoints.at(-1);
        const next = hurricaneTimelinePoints.findIndex(p => Date.parse(p.properties.valid) >= target);
        if (next <= 0) return hurricaneTimelinePoints[Math.max(0, next)];
        if (next < 0) return hurricaneTimelinePoints.at(-1);
        const a = hurricaneTimelinePoints[next - 1]; const b = hurricaneTimelinePoints[next];
        const ta = Date.parse(a.properties.valid); const tb = Date.parse(b.properties.valid);
        const f = Math.max(0, Math.min(1, (target - ta) / Math.max(1, tb - ta)));
        const [lonA, latA] = a.geometry.coordinates; const [lonB0, latB] = b.geometry.coordinates;
        const lonDelta = ((lonB0 - lonA + 540) % 360) - 180;
        const interpolate = key => {
            const va = Number(a.properties[key]); const vb = Number(b.properties[key]);
            return Number.isFinite(va) && Number.isFinite(vb) ? va + (vb - va) * f : (Number.isFinite(va) ? va : vb);
        };
        return { ...a, geometry: { ...a.geometry, coordinates: [lonA + lonDelta * f, latA + (latB - latA) * f] }, properties: { ...a.properties, valid: new Date(target).toISOString(), vmax_kt: interpolate('vmax_kt'), mslp_mb: interpolate('mslp_mb') } };
    }, [hurricaneTimelinePoints, state.dateSelected, state.sliderValue, activeHurricaneStorm?.latest_time]);

    // Animate only the live head layer.  The 30fps cap prevents the animation
    // from recreating the (potentially large) historical/forecast layers.
    useEffect(() => {
        if (!isHurricaneSelected) return;
        let rafId = 0;
        const startedAt = performance.now();
        let lastFrame = 0;
        const loop = () => {
            const now = performance.now();
            if (now - lastFrame >= 33) {
                const t = (now - startedAt) / 1000;
                // deck.gl positive icon angles are clockwise. Northern storms
                // are counter-clockwise; Southern storms are clockwise.
                const direction = (activeHurricaneStorm?.lat ?? 0) >= 0 ? -1 : 1;
                setHurricaneSpinAngle(direction * ((t * 150) % 360));
                lastFrame = now;
            }
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(rafId);
    }, [isHurricaneSelected, activeHurricaneStorm?.id, activeHurricaneStorm?.lat]);

    // Keep exactly one usable forecast model selected. Historical observations
    // are a separate layer and are never silently substituted for a forecast.
    useEffect(() => {
        if (!isHurricaneSelected || !activeHurricaneStorm) return;
        const avail = new Set(activeHurricaneStorm.models || []);
        const preferred = ['ofcl', 'ecmwf', 'ukm', 'jma', 'gfs', 'navgem', 'cwb'];
        setHurricaneSelectedModel(current => (
            avail.has(current) ? current : (preferred.find(model => avail.has(model)) || '')
        ));
    }, [isHurricaneSelected, activeHurricaneStorm]);

    // --- Dynamic SEO Title ---
    const pageTitle = useMemo(() => {
        const base = rmiPortal ? 'Marshall Islands Ocean Portal' : 'Palau Ocean Portal';
        if (landingOpen) return `${base} | Climate & Marine Data Explorer`;
        if (state.variableSelected === '7.2.1') return `Marine Heat Waves | ${base}`;
        if (state.variableSelected === '6.1.1.3') return `Historical Regional Sea Level | ${base}`;
        if (state.variableSelected === '6.2.2.3') return `Historical Flood Frequency | ${base}`;
        if (state.variableSelected === '6.3.2.1') return `Historical Wave Hindcast | ${base}`;
        if (state.variableSelected === '6.3.2.2') return `ERA5 Wave Reanalysis | ${base}`;
        if (state.variableSelected === '6.3.2.3') return `NOAA WW3 Wave Conditions & Forecast | ${base}`;
        if (state.variableSelected === '7.1.5' || state.variableSelected?.startsWith('cfs.')) {
            return `${forecastPageTitle(state.variableSelected) || 'CFS Forecast'} | ${base}`;
        }
        if (state.variableSelected === '7.1.6' || state.variableSelected?.startsWith('gfs.')) {
            return `${forecastPageTitle(state.variableSelected) || 'GFS Forecast'} | ${base}`;
        }
        if (isCmip7CatalogueSelection(state.variableSelected)) return `CMIP7 Availability Explorer | ${base}`;
        if (isCmip7PreviewSelection(state.variableSelected)) return `CMIP7 Projection | ${base}`;
        if (isCmip6Selection(state.variableSelected)) return `CMIP6 Climate Projections | ${base}`;
        if (isEkmanVariable(state.variableSelected)) return `Ekman Upwelling (Tuna Tool) | ${base}`;
        if (state.variableSelected === 'hurricane.tracker') return `Tropical Cyclone | ${base}`;
        if (state.variableSelected === '4.1') return `Automated Weather Stations | ${base}`;
        if (state.variableSelected === '2.1.2') return `PCC Probes | ${base}`;
        return `Interactive Explorer | ${base}`;
    }, [state.variableSelected, landingOpen, rmiPortal]);

    // --- Marine Heat Wave (MHW) State ---
    const [mhwTimescale, setMhwTimescale] = useState('monthly'); // 'monthly' | 'seasonal' | 'decadal'
    const [mhwMapType, setMhwMapType] = useState('gridded'); // 'contours' | 'gridded' | 'voronoi'
    const [mhwIsMasked, setMhwIsMasked] = useState(true); // boolean
    const [mhwScope, setMhwScope] = useState(() => (
        rmiPortal && mhwScopeMetadata.rmi?.monthly?.dates?.length ? 'rmi' : 'palau'
    ));
    const [mhwRes, setMhwRes] = useState('0p5deg');      // Pacific resolution tier
    const mhwPacific = mhwScope === 'indo';
    const setMhwPacific = useCallback((update) => {
        setMhwScope((current) => {
            const enabled = typeof update === 'function' ? update(current === 'indo') : update;
            return enabled ? 'indo' : (rmiPortal ? 'rmi' : 'palau');
        });
    }, [rmiPortal]);

    // Ref to prevent stale closures in map click handlers
    const mhwIsMaskedRef = useRef(mhwIsMasked);
    useEffect(() => {
        mhwIsMaskedRef.current = mhwIsMasked;
    }, [mhwIsMasked]);

    const mhwMapTypeRef = useRef(mhwMapType);
    useEffect(() => {
        mhwMapTypeRef.current = mhwMapType;
    }, [mhwMapType]);

    // Slider dates come from vars.js (refreshed by marine-heat-waves.ipynb)
    const mhwDateOptions = useMemo(() => {
        const published = mhwScopeMetadata[mhwScope]?.[mhwTimescale]?.dates;
        if (published?.length) return published;
        // RMI has no legacy notebook output. Palau and the existing
        // Indo-Pacific publication retain their legacy date lists until the
        // cron publisher has generated a manifest for them too.
        if (mhwScope === 'rmi') return [];
        if (mhwTimescale === 'monthly') return mhwMonthlyDates;
        if (mhwTimescale === 'seasonal') return mhwSeasonalDates;
        if (mhwTimescale === 'decadal') return mhwDecadalDates;
        return [];
    }, [mhwTimescale, mhwScope]);

    // Derived state replacing multiple useEffects to stop rapid flipping / crash loops
    const mhwCurrentIndex = (state.variableSelected === '7.2.1' && mhwDateOptions.length > 0)
        ? (state.sliderValue >= 0 && state.sliderValue < mhwDateOptions.length
            ? state.sliderValue
            : mhwDateOptions.length - 1)
        : 0;
    const mhwCurrentDate = mhwDateOptions[mhwCurrentIndex] || '';

    // When the MHW timescale changes, snap the slider to the latest available period.
    useEffect(() => {
        if (state.variableSelected !== '7.2.1' || !mhwDateOptions.length) return;
        const latestIndex = mhwDateOptions.length - 1;
        if (state.sliderValue !== latestIndex) {
            dispatch({ type: 'UPDATE_CONFIG', payload: { sliderValue: latestIndex } });
        }
    }, [mhwTimescale, state.sliderValue, state.variableSelected, mhwDateOptions.length]);

    // Guard against combinations that don't exist (voronoi cannot be masked)
    useEffect(() => {
        const canMask = mhwMapType !== 'voronoi' && mhwScope === 'palau';
        if (!canMask && mhwIsMasked) {
            setMhwIsMasked(false);
        }
    }, [mhwMapType, mhwIsMasked, mhwScope]);

    // The whole-Pacific MHW layer only ships gridded + contours (no voronoi), so
    // fall back to gridded if the Pacific toggle is enabled while on voronoi.
    useEffect(() => {
        if (mhwScope === 'indo' && mhwMapType === 'voronoi') {
            setMhwMapType('gridded');
        }
    }, [mhwScope, mhwMapType]);

    // --- Tooltip Hint State ---
    const [leftHintDismissed, setLeftHintDismissed] = useState(false);

    const toggleBasemap = useCallback(() => {
        setBasemap(prev => prev === 'terrain' ? 'satellite' : 'terrain');
    }, []);

    // --- Header Height Logic ---
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(64);

    useEffect(() => {
        if (!headerRef.current) return;
        const observer = new ResizeObserver(() => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        });
        observer.observe(headerRef.current);
        return () => observer.disconnect();
    }, []);
    // ---------------------------
    
    // --- Sync PCC Station from Dashboard Dropdown ---
    useEffect(() => {
        const handleStationChange = (e) => {
            const newStationId = e.detail;
            setPccStation(newStationId);
            const stationInfo = PCC_METADATA[newStationId];
            if (stationInfo) {
                dispatch({
                    type: 'SET_LOCATION',
                    payload: { lat: stationInfo.latitude, long: stationInfo.longitude, scatterplotVariable: 'pcc_probes' }
                });
            }
        };
        window.addEventListener('pccStationChange', handleStationChange);
        return () => window.removeEventListener('pccStationChange', handleStationChange);
    }, []);
    
    // --- Sync AWS Station from Dashboard Dropdown ---
    useEffect(() => {
        const handleStationChange = (e) => {
            const newStationId = e.detail;
            setAwsStation(newStationId);
            const stationInfo = AWS_METADATA[newStationId];
            if (stationInfo) {
                dispatch({
                    type: 'SET_LOCATION',
                    payload: { lat: stationInfo.latitude, long: stationInfo.longitude, scatterplotVariable: 'aws' }
                });
            }
        };
        window.addEventListener('awsStationChange', handleStationChange);
        return () => window.removeEventListener('awsStationChange', handleStationChange);
    }, []);

    // Sync body class with mode for global background
    useEffect(() => {
        document.body.className = 'light';
    }, []);

    // [PERFORMANCE] Local slider state.
    // Decouples the visual slider update (fast) from the heavy map update (slow).
    const [localSliderValue, setLocalSliderValue] = useState(0);

    // Sync local slider when global state changes (e.g. via animation loop)
    useEffect(() => {
        setLocalSliderValue(state.sliderValue);
    }, [state.sliderValue]);

    // --- ADD STATE FOR COLORBAR MODAL ---
    const [colorbarModalOpen, setColorbarModalOpen] = useState(false); // <-- Add this line
    const handleColorbarModalOpen = () => setColorbarModalOpen(true);
    const handleColorbarModalClose = () => setColorbarModalOpen(false);

    // --- ADD STATE FOR LOADING TIMEOUT ---
    const [isLoadTimedOut, setIsLoadTimedOut] = useState(false);

    // --- Use Custom Hook for Map Layers ---
    // --- ADD STATE FOR DOWNLOAD MENU ---
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
    const isDownloadMenuOpen = Boolean(downloadMenuAnchor);
    const [mobileActionsAnchor, setMobileActionsAnchor] = useState(null);
    const isMobileActionsOpen = Boolean(mobileActionsAnchor);

    const handleDownloadMenuOpen = (event) => {
        setDownloadMenuAnchor(event.currentTarget);
    };

    const handleDownloadMenuClose = () => {
        setDownloadMenuAnchor(null);
    };

    const handleMobileActionsClose = () => setMobileActionsAnchor(null);

    const handleLayersMenuOpen = (event) => setLayersMenuAnchor(event.currentTarget);
    const handleLayersMenuClose = () => setLayersMenuAnchor(null);

    const toggleBoundaryLayer = (id, isChecked) => {
        const config = BOUNDARY_LAYERS_CONFIG.find(layer => layer.id === id);
        setActiveBoundaryLayers(prev => {
            if (!isChecked) return prev.filter(layerId => layerId !== id);
            const withoutConflictingZoneSchemes = config?.exclusiveGroup
                ? prev.filter(layerId => BOUNDARY_LAYERS_CONFIG.find(layer => layer.id === layerId)?.exclusiveGroup !== config.exclusiveGroup)
                : prev;
            return [...withoutConflictingZoneSchemes.filter(layerId => layerId !== id), id];
        });
    };

    const { layers: deckLayers, isLoading: isMapLoading, data: mapData } = useMapLayers({
        filePath: state.currentJSON,
        onClick: state.currentOnClick,
        // A focused cyclone view reserves contrast for the selected cone and
        // track. The user can restore the live wind field from Details.
        showWindParticles: showWindParticles && !(isHurricaneSelected && hurricaneFocusMode),
        windRenderMode,
        waveVectorMode,
        mapZoom: mapState.viewState?.zoom,
        mapViewState: mapState.viewState,
    });
    const { layers: convergenceLayers, isLoading: isConvergenceLoading } = useMapLayers({
        filePath: convergenceContext.paths,
        onClick: [],
        showWindParticles: false,
        mapZoom: mapState.viewState?.zoom,
        mapViewState: mapState.viewState,
        // A line for the previous slider date is scientifically misleading;
        // clear it while the newly selected frame is loading.
        retainPreviousLayers: false,
    });

    useEffect(() => {
        const isAlert = state.crwEnabled && state.variableSelected?.startsWith('7.4.3');
        if (!isAlert) {
            crwNativeDialGridRef.current = {};
            return undefined;
        }

        if (crwScope === 'rmi') {
            let active = true;
            const suffix = crwAlertMode === 'modified' ? '' : '_legacy';
            Promise.all(CRW_DIAL_FIELDS.map(async field => [
                field,
                await getJSON(`/jsonFiles/CRW-BAA-RMI/${field}/latest${suffix}.fgb`),
            ])).then(entries => {
                if (active) crwNativeDialGridRef.current = Object.fromEntries(entries);
            }).catch(() => { if (active) crwNativeDialGridRef.current = {}; });
            return () => { active = false; };
        }

        const isNativeCurrentAlert = crwNativeAlert && state.variableSelected?.endsWith('.1');
        if (!isNativeCurrentAlert) {
            crwNativeDialGridRef.current = {};
            return undefined;
        }

        const dateCandidate = state.dateSelected?.[state.sliderValue] || state.dateSelected?.[0] || '';
        const date = String(dateCandidate).match(/\d{4}-\d{2}-\d{2}/)?.[0];
        if (!date) return undefined;

        let active = true;
        getJSON(`/jsonFiles/CRW-BAA/CRW_BAA_Current_Rounded/${date}.json`)
            .then(data => { if (active) crwNativeDialGridRef.current = { all: data }; })
            .catch(() => { if (active) crwNativeDialGridRef.current = {}; });
        return () => { active = false; };
    }, [state.crwEnabled, state.variableSelected, state.dateSelected, state.sliderValue, crwNativeAlert, crwScope, crwAlertMode]);

    const mapLoadLabel = useMemo(() => {
        if (!isMapLoading) return 'Updated';
        if (isHurricaneSelected) return 'Loading GFS wind…';
        if (isForecastWindSelected) return `Loading ${forecastModel.toUpperCase()} wind…`;
        if (state.variableSelected?.startsWith('7.4.')) return 'Loading Coral Reef Watch…';
        return 'Loading map…';
    }, [isMapLoading, isHurricaneSelected, isForecastWindSelected, forecastModel, state.variableSelected]);

    useEffect(() => {
        console.log("Current scatterplot variable:", state.scatterplotVariable);
    }, [state.scatterplotVariable]);

    // --- Loading Timeout Logic for Animation ---
    useEffect(() => {
        let timeoutId = null;
        // Only set a timeout if the animation is playing and a map is loading
        if (isMapLoading && state.isPlaying) {
            setIsLoadTimedOut(false); // Reset on new loading start
            timeoutId = setTimeout(() => {
                console.warn("Map loading timed out after 5 seconds. Advancing animation.");
                setIsLoadTimedOut(true);
            }, 5000); // 5-second timeout
        } else {
            // If loading completes or animation stops, reset the timeout state
            setIsLoadTimedOut(false);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isMapLoading, state.isPlaying]);

    // --- Effect to update CRW data when map data changes (e.g. slider movement) ---
    useEffect(() => {
        // The Marshall regional gauge is published as a manifest with all four
        // panel values; individual panel tiles intentionally carry one dial.
        if (crwScope === 'rmi') return;
        // Only run if CRW is enabled, we have map data, and a location is selected
        if (state.crwEnabled && mapData && mapData.length > 0 && state.lat && state.long) {
            // Assuming the first file in jsonPaths is the CRW data (as per variableConfig)
            const crwFeatureCollection = mapData[0];

            // Check if it looks like GeoJSON or array of features
            if (crwFeatureCollection && (crwFeatureCollection.features || Array.isArray(crwFeatureCollection))) {
                const features = crwFeatureCollection.features || crwFeatureCollection;

                const targetLat = parseFloat(state.lat);
                const targetLon = parseFloat(state.long);

                // Find the feature matching the current location
                // Using a small epsilon for float comparison
                const foundFeature = features.find(f => {
                    const p = f.properties;
                    if (!p) return false;
                    return Math.abs(p.lat - targetLat) < 0.0001 && Math.abs(p.lon - targetLon) < 0.0001;
                });

                if (foundFeature) {
                    const p = foundFeature.properties;
                    // The native 5 km BAA layer intentionally only carries a
                    // current-condition `value`. Its click handler merges
                    // that value with the matching all-dial alert grid. Do
                    // not let this generic map-sync effect overwrite the
                    // selected four-dial state with zeroes immediately after.
                    if (!CRW_DIAL_FIELDS.some(field => p[field] != null)) return;
                    const newCrwArray = [
                        p["CRW_BAA_Current_Rounded"] ?? 0,
                        p["CRW_BAA_Week_01-04"] ?? 0,
                        p["CRW_BAA_Week_05-08"] ?? 0,
                        p["CRW_BAA_Week_09-12"] ?? 0
                    ];

                    // Only dispatch if data has changed to prevent infinite loops
                    if (JSON.stringify(newCrwArray) !== JSON.stringify(state.crwArray)) {
                         dispatch({
                            type: 'SET_CRW_DATA',
                            payload: {
                                lat: state.lat,
                                long: state.long,
                                crwArray: newCrwArray
                            }
                        });
                    }
                }
            }
        }
    }, [mapData, state.crwEnabled, state.lat, state.long, state.crwArray, crwScope]);

    useEffect(() => {
        if (!state.crwEnabled || crwScope !== 'rmi') return undefined;
        const controller = new AbortController();
        fetch('/jsonFiles/CRW-BAA-RMI/rmi_dials.json', { signal: controller.signal })
            .then(response => {
                if (!response.ok) throw new Error(`RMI CRW dials: ${response.status}`);
                return response.json();
            })
            .then(payload => {
                const dials = crwAlertMode === 'modified'
                    ? (payload?.dials || {})
                    : (payload?.dialsLegacy || payload?.dials || {});
                const next = [
                    dials.CRW_BAA_Current_Rounded ?? 0,
                    dials['CRW_BAA_Week_01-04'] ?? 0,
                    dials['CRW_BAA_Week_05-08'] ?? 0,
                    dials['CRW_BAA_Week_09-12'] ?? 0,
                ];
                dispatch({ type: 'SET_CRW_DATA', payload: { lat: 9.9, long: 166.5, crwArray: next } });
            })
            .catch(error => { if (error.name !== 'AbortError') console.warn(error.message); });
        return () => controller.abort();
    }, [state.crwEnabled, crwScope, crwAlertMode]);

    // --- Theme ---
    const muiTheme = useMemo(
        () => createTheme({
            palette: {
                mode: 'light',
                primary: { main: '#176580' },
                secondary: { main: '#b9513d' },
                background: { default: '#f2f0e8', paper: '#faf9f4' },
                text: { primary: '#102b3d', secondary: '#4c6069' },
            },
            typography: {
                fontFamily: ATLAS_FONT_FAMILIES.ui,
                button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
            },
            components: { // Example: Customize Slider track/thumb color
                MuiSlider: {
                    styleOverrides: {
                        thumb: ({ theme }) => ({
                            backgroundColor: theme.palette.primary.main,
                        }),
                         track: ({ theme }) => ({
                            backgroundColor: theme.palette.primary.main,
                            border: 'none',
                        }),
                        rail: ({ theme }) => ({
                             opacity: 0.5,
                             backgroundColor: theme.palette.grey[500],
                        }),
                        markLabel: ({ theme }) => ({
                           color: theme.palette.text.secondary,
                           fontSize: '0.7rem',
                        }),
                        mark: ({ theme }) => ({
                            backgroundColor: theme.palette.grey[600],
                            height: 8,
                            width: 1,
                            marginTop: -3,
                        }),
                    }
                }
            }
        }),
        []
    );

    // --- Responsive check ---
    const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up('md'));
    // On compact screens drawers become direct-manipulation sheets. Keeping
    // independent controls lets a released drag inherit its current velocity
    // while settling back to its resting position.
    const leftSheetControls = useAnimationControls();
    const rightSheetControls = useAnimationControls();
    const hurricaneDetailsSheetControls = useAnimationControls();
    const timelineSheetControls = useAnimationControls();
    const leftSheetDragControls = useDragControls();
    const rightSheetDragControls = useDragControls();
    const hurricaneDetailsSheetDragControls = useDragControls();
    const timelineSheetDragControls = useDragControls();

    // --- Metadata Handler ---
    const handleMetadataChange = useCallback((meta) => {
        dispatch({ type: 'SET_METADATA', payload: meta });
    }, []);

    // --- Drawer Handlers ---
    const handleDrawerToggle = useCallback(() => setMobileOpen(prev => !prev), []);
    const handleRightDrawerToggle = useCallback(() => setRightDrawerOpen(prev => !prev), []);

    useEffect(() => {
        if (mobileOpen && !isLargeScreen) {
            leftSheetControls.start({ y: 0, transition: { type: 'spring', stiffness: 460, damping: 42 } });
        }
    }, [mobileOpen, isLargeScreen, leftSheetControls]);

    useEffect(() => {
        if (rightDrawerOpen && !isLargeScreen) {
            rightSheetControls.start({ y: 0, transition: { type: 'spring', stiffness: 460, damping: 42 } });
        }
    }, [rightDrawerOpen, isLargeScreen, rightSheetControls]);

    // Cyclone details and the timeline use the same direct-manipulation rule:
    // predict the release endpoint from velocity, then settle to the nearest
    // meaningful state rather than using a brittle drag-distance threshold.
    useEffect(() => {
        if (hurricaneDetailsOpen && !isLargeScreen) {
            setHurricaneDetailsSnap('half');
            hurricaneDetailsSheetControls.start({ y: Math.round(window.innerHeight * 0.28), opacity: 1, transition: { type: 'spring', stiffness: 460, damping: 42 } });
        }
    }, [hurricaneDetailsOpen, isLargeScreen, hurricaneDetailsSheetControls]);

    useEffect(() => {
        if (state.isSliderVisible && !timelineCollapsed && !isLargeScreen) {
            timelineSheetControls.start({ y: 0, opacity: 1, transition: { type: 'spring', stiffness: 460, damping: 42 } });
        }
    }, [state.isSliderVisible, timelineCollapsed, isLargeScreen, timelineSheetControls]);
    const resetCrwTileView = useCallback(() => {
        // Restore the national Coral Bleaching Alert map/dials and remove the
        // tile-specific chart state without altering the active alert dial.
        setCrwDailyHistoryKey(null);
        dispatch({
            type: 'SET_LOCATION',
            payload: {
                lat: state.lat,
                long: state.long,
                scatterplotVariable: 'CRW',
            },
        });
        setRightDrawerOpen(false);
    }, [state.lat, state.long]);

    // --- Download Handler ---
    const handleDownload = useCallback(async (format = 'json') => {
        setDownloadMenuAnchor(null); // Close menu
        if (!state.currentJSON) return;

        const files = Array.isArray(state.currentJSON) ? state.currentJSON : [state.currentJSON];
        
        // Filter out static/reference layers to provide only relevant data
        const relevantFiles = files.filter(path => 
            !path.toLowerCase().includes('eez') && 
            !path.toLowerCase().includes('palau.json') &&
            !path.toLowerCase().includes('outline') &&
            !path.toLowerCase().includes('region')
        );

        const filesToDownload = relevantFiles.length > 0 ? relevantFiles : files;

        for (const url of filesToDownload) {
            try {
                // Map tiles are stored as FlatGeobuf on disk; getJSON auto-resolves
                // the .fgb sibling when given a .json path and returns GeoJSON.
                const geojson = await getJSON(url);

                if (format === 'shp') {
                    const { zip } = await import('shp-write');
                    const zipBase64 = zip(geojson);

                    const link = document.createElement('a');
                    link.href = "data:application/zip;base64," + zipBase64;
                    const filename = url.split('/').pop().replace(/\.(json|geojson)$/i, '') + '.zip';
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    const blob = new Blob([JSON.stringify(geojson)], { type: 'application/geo+json' });
                    const blobUrl = window.URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = url.split('/').pop().replace(/\.fgb$/i, '.json');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                }
            } catch (error) {
                console.error("Download failed:", error);
            }
        }
    }, [state.currentJSON]);

    // A full Pacific native CRW file is several hundred MB. Link straight to
    // the server-side FlatGeobuf instead of parsing/rendering it in-browser.
    const handlePacificNativeCrwDownload = useCallback(() => {
        setDownloadMenuAnchor(null);
        const files = Array.isArray(state.currentJSON) ? state.currentJSON : [state.currentJSON];
        const displayedPath = files.find(path => typeof path === 'string' && path.includes('/coral-reef-watch/pacific/'));
        if (!displayedPath) return;
        const nativePath = displayedPath.replace(
            /\/pacific\/(?:native|0\.25deg|0\.5deg|1deg|2deg)\//,
            '/pacific/native/'
        );
        const link = document.createElement('a');
        link.href = nativePath;
        link.download = nativePath.split('/').pop() || 'coral-reef-watch-pacific-native.fgb';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [state.currentJSON]);

    // --- Screenshot Handler ---
    const handleScreenshot = useCallback(() => {
        if (!mapContainerRef.current) return;
        
        // Find all canvases within the map container (Base map + DeckGL overlay)
        const canvases = Array.from(mapContainerRef.current.querySelectorAll('canvas'));
        if (canvases.length === 0) return;

        // Identify Base Map (Mapbox) vs Overlays (DeckGL)
        // Mapbox canvas typically has class 'mapboxgl-canvas'
        const baseCanvas = canvases.find(c => c.className.includes('mapboxgl-canvas'));
        const overlayCanvases = canvases.filter(c => c !== baseCanvas);
        
        // Determine draw order: Base first, then overlays
        const orderedCanvases = baseCanvas ? [baseCanvas, ...overlayCanvases] : canvases;

        const mapWidth = orderedCanvases[0].width;
        const mapHeight = orderedCanvases[0].height;

        const files = Array.isArray(state.currentJSON) ? state.currentJSON : [state.currentJSON];
        const dataFile = files.find(path => typeof path === 'string' && !/eez|palau\.json|outline|region/i.test(path));
        const dateFromFile = dataFile?.match(/\d{4}-\d{2}-\d{2}/)?.[0];
        const selectedDate = state.dateSelected?.[state.sliderValue];
        const dataDate = dateFromFile || (typeof selectedDate === 'string' ? (selectedDate.match(/\d{4}-\d{2}-\d{2}/)?.[0] || selectedDate) : null);
        const crwProduct = state.scatterplotVariable === 'crw_daily'
            ? CRW_DAILY_PRODUCTS[crwProductFromVariable(state.variableSelected)]
            : null;

        // Find Colorbar Image
        const colorbarImg = mapContainerRef.current.querySelector('img[alt="Colorbar"]');
        
        // Calculate Sidebar Width (e.g. 20% of map width, min 200px)
        let sidebarWidth = 0;
        if (colorbarImg || crwProduct) {
            const rect = mapContainerRef.current.getBoundingClientRect();
            const pixelRatio = mapWidth / (rect.width || 1);
            sidebarWidth = 280 * pixelRatio; // Allocate room for a complete legend
        }

        const combinedCanvas = document.createElement('canvas');
        combinedCanvas.width = mapWidth + sidebarWidth;
        combinedCanvas.height = mapHeight;
        const ctx = combinedCanvas.getContext('2d');

        // Fill background white
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

        // Draw Map Canvases (shifted right)
        orderedCanvases.forEach(canvas => {
            ctx.drawImage(canvas, sidebarWidth, 0, mapWidth, mapHeight);
        });

        // Stamp the exact source date into every exported map image.
        if (dataDate) {
            const rect = mapContainerRef.current.getBoundingClientRect();
            const pixelRatio = mapWidth / (rect.width || 1);
            const fontSize = 14 * pixelRatio;
            const padding = 10 * pixelRatio;
            ctx.font = `600 ${fontSize}px Arial`;
            const label = `Data date: ${dataDate}`;
            const labelWidth = ctx.measureText(label).width;
            const x = sidebarWidth + (16 * pixelRatio);
            const y = 16 * pixelRatio;
            ctx.fillStyle = 'rgba(2, 6, 23, 0.82)';
            ctx.fillRect(x, y, labelWidth + (padding * 2), fontSize + (padding * 2));
            ctx.fillStyle = '#f8fafc';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, x + padding, y + padding + (fontSize / 2));
            ctx.textBaseline = 'alphabetic';
        }

        // CRW legends are DOM controls, not images. Recreate the complete
        // NOAA colour scale in the export so screenshots remain self-contained.
        if (crwProduct && sidebarWidth > 0) {
            const rect = mapContainerRef.current.getBoundingClientRect();
            const pixelRatio = mapWidth / (rect.width || 1);
            const padding = 18 * pixelRatio;
            const titleSize = 17 * pixelRatio;
            const textSize = 12 * pixelRatio;
            const rowHeight = 23 * pixelRatio;
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, 0, sidebarWidth, mapHeight);
            ctx.textAlign = 'left';
            ctx.fillStyle = '#67e8f9';
            ctx.font = `bold ${11 * pixelRatio}px Arial`;
            ctx.fillText('NOAA CORAL REEF WATCH', padding, padding + (11 * pixelRatio));
            ctx.fillStyle = '#ffffff';
            ctx.font = `bold ${titleSize}px Arial`;
            ctx.fillText(crwProduct.label, padding, padding + (11 * pixelRatio) + titleSize + (8 * pixelRatio));
            ctx.fillStyle = '#cbd5e1';
            ctx.font = `${textSize}px Arial`;
            ctx.fillText(dataDate ? `Product date: ${dataDate}` : 'Daily product', padding, padding + (11 * pixelRatio) + titleSize + textSize + (18 * pixelRatio));
            const startY = padding + (11 * pixelRatio) + titleSize + textSize + (42 * pixelRatio);
            crwProduct.legend.forEach(([label, color], index) => {
                const y = startY + (index * rowHeight);
                ctx.fillStyle = color;
                ctx.fillRect(padding, y, 18 * pixelRatio, 14 * pixelRatio);
                ctx.strokeStyle = 'rgba(255,255,255,0.55)';
                ctx.strokeRect(padding, y, 18 * pixelRatio, 14 * pixelRatio);
                ctx.fillStyle = '#e2e8f0';
                ctx.font = `${textSize}px Arial`;
                ctx.fillText(`${label} ${crwProduct.unit}`, padding + (28 * pixelRatio), y + (12 * pixelRatio));
            });
            ctx.fillStyle = '#94a3b8';
            ctx.font = `${10 * pixelRatio}px Arial`;
            ctx.fillText('NOAA CRW v3.1 daily 5 km source', padding, startY + (crwProduct.legend.length * rowHeight) + (18 * pixelRatio));
        // Draw image-based colorbars for the remaining product families.
        } else if (colorbarImg && sidebarWidth > 0) {
            try {
                const imgW = colorbarImg.naturalWidth || colorbarImg.width;
                const imgH = colorbarImg.naturalHeight || colorbarImg.height;
                const imgRatio = imgW / imgH;

                const rect = mapContainerRef.current.getBoundingClientRect();
                const pixelRatio = mapWidth / (rect.width || 1);
                const padding = 20 * pixelRatio;
                const maxW = sidebarWidth - (padding * 2);
                const maxH = mapHeight - (padding * 2);

                let drawW = maxW;
                let drawH = drawW / imgRatio;

                if (drawH > maxH) {
                    drawH = maxH;
                    drawW = drawH * imgRatio;
                }

                const x = (sidebarWidth - drawW) / 2;
                const y = (mapHeight - drawH) / 2;

                ctx.drawImage(colorbarImg, x, y, drawW, drawH);

                // Add Legend Title
                const fontSize = 24 * pixelRatio;
                ctx.fillStyle = '#000000';
                ctx.font = `bold ${fontSize}px Arial`;
                ctx.textAlign = 'center';
                ctx.fillText("Legend", sidebarWidth / 2, y - (fontSize * 0.5));
            } catch (e) {
                console.warn("Screenshot: Could not draw colorbar", e);
            }
        }

        // Determine filename based on current JSON layer
        let filename = `palau-map-${new Date().toISOString().slice(0, 10)}`;
        if (state.currentJSON) {
            const files = Array.isArray(state.currentJSON) ? state.currentJSON : [state.currentJSON];
            const relevantFiles = files.filter(path => 
                !path.toLowerCase().includes('eez') && 
                !path.toLowerCase().includes('palau.json') &&
                !path.toLowerCase().includes('outline') &&
                !path.toLowerCase().includes('region')
            );
            const mainFile = relevantFiles.length > 0 ? relevantFiles[0] : files[0];
            if (mainFile) {
                const nameParts = mainFile.split('/').pop().split('.');
                if (nameParts.length > 1) nameParts.pop(); // Remove extension
                filename = nameParts.join('.');
            }
        }

        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = combinedCanvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [state.currentJSON, state.dateSelected, state.scatterplotVariable, state.sliderValue, state.variableSelected]);

    // --- Existing Functions (Keep Internal Logic) ---

    // --- updateMapState Callback ---
    const updateMapViewState = useCallback((newViewState, options = {}) => {
       const drawerVisible = options.rightDrawerVisible ?? rightDrawerOpen;
       const canOffsetForDrawers = isLargeScreen
           && Number.isFinite(Number(newViewState?.longitude))
           && Number.isFinite(Number(newViewState?.zoom));
       const rightInset = drawerVisible ? 750 : 0;
       const leftInset = mobileOpen ? 280 : 0;
       const centerOffsetPx = canOffsetForDrawers ? (rightInset - leftInset) / 2 : 0;
       const longitudeOffset = centerOffsetPx
           ? centerOffsetPx * 360 / (512 * (2 ** Number(newViewState.zoom)))
           : 0;
       setMapState(prevState => ({
           ...prevState,
           viewState: {
               ...prevState.viewState,
               ...viewStateInterpolate, // Apply interpolator for smooth transitions
               ...newViewState,
               ...(longitudeOffset ? { longitude: Number(newViewState.longitude) + longitudeOffset } : {}),
           }
       }));
    }, [isLargeScreen, mobileOpen, rightDrawerOpen]);

    const dismissMapZoomHint = useCallback(() => {
        setShowMapZoomHint(false);
        try {
            window.localStorage.setItem(MAP_ZOOM_HINT_STORAGE_KEY, '1');
        } catch {
            // Storage can be unavailable in private browsing; the hint still
            // dismisses for the current session through component state.
        }
    }, []);

    const adjustMapZoom = useCallback((increment) => {
        setMapState(prevState => ({
            ...prevState,
            viewState: {
                ...prevState.viewState,
                ...viewStateInterpolate,
                transitionDuration: 220,
                zoom: Math.min(
                    MAP_MAX_ZOOM,
                    Math.max(MAP_MIN_ZOOM, Number(prevState.viewState.zoom || 0) + increment),
                ),
            },
        }));
        dismissMapZoomHint();
    }, [dismissMapZoomHint]);

    const handleMapViewStateChange = useCallback(({ viewState, oldViewState }) => {
        const previousZoom = Number(oldViewState?.zoom);
        if (Number.isFinite(previousZoom) && Number(viewState.zoom) !== previousZoom) {
            dismissMapZoomHint();
        }
        setMapState(prev => ({
            ...prev,
            viewState: {
                ...viewState,
                transitionDuration: 0,
            },
        }));
    }, [dismissMapZoomHint]);

    // A scope selection is also a geographic decision. Move to the published
    // product rather than leaving an RMI or Indo-Pacific layer centred on
    // Palau, while preserving the user's view for Palau itself.
    useEffect(() => {
        if (state.variableSelected !== '7.2.1') return;
        if (mhwScope === 'rmi') {
            updateMapViewState({ latitude: 9.5, longitude: 166.5, zoom: 4.15, bearing: 0, pitch: 0 });
        } else if (mhwScope === 'indo') {
            updateMapViewState({ latitude: 0, longitude: 185, zoom: 2.15, bearing: 0, pitch: 0 });
        }
    }, [mhwScope, state.variableSelected, updateMapViewState]);

    // GHCN-D stations are published daily, so their metadata is intentionally
    // supplied by the explorer rather than duplicated as a static App constant.
    // The same event is emitted by its dropdown and the map point layer.
    useEffect(() => {
        const handleGhcndStationChange = (event) => {
            const station = event.detail;
            if (!station || !Number.isFinite(Number(station.latitude)) || !Number.isFinite(Number(station.longitude))) return;
            dispatch({ type: 'SET_LOCATION', payload: { lat: Number(station.latitude), long: Number(station.longitude) } });
            updateMapViewState({
                latitude: Number(station.latitude),
                longitude: Number(station.longitude),
                zoom: station.country === 'Marshall Islands' ? 6.2 : 10.5,
                transitionDuration: 900,
            });
            setRightDrawerOpen(true);
        };
        window.addEventListener('ghcndStationChange', handleGhcndStationChange);
        return () => window.removeEventListener('ghcndStationChange', handleGhcndStationChange);
    }, [updateMapViewState]);

    // CRW regional controls change both the data clip and the appropriate map
    // focus.  Keeping this separate from the generic config update avoids a
    // timeline scrub unexpectedly recentering the map.
    const handleCrwScopeChange = useCallback((scope) => {
        if (scope === 'rmi' && !rmiPortal) return;
        setCrwScope(scope);
        setCrwDailyHistoryKey(null);
        const view = scope === 'rmi'
            ? { latitude: 9.9, longitude: 166.5, zoom: 4.1, bearing: 0, pitch: 0 }
            : scope === 'pacific'
                ? { latitude: 5, longitude: 205, zoom: 2.05, bearing: 0, pitch: 0 }
                : VIEW_STATES.SECOND;
        updateMapViewState(view);
    }, [rmiPortal, updateMapViewState]);

    // --- Fly to AWS Station when selected ---
    useEffect(() => {
        if (state.variableSelected === '4.1' && awsStation && AWS_METADATA[awsStation]) {
            const stationInfo = AWS_METADATA[awsStation];
            updateMapViewState({
                longitude: stationInfo.longitude,
                latitude: stationInfo.latitude,
                zoom: 14,
                transitionDuration: 1500
            });
        }
    }, [awsStation, state.variableSelected, updateMapViewState]);

    // --- Fly to PCC Station when selected ---
    useEffect(() => {
        if (state.variableSelected === '2.1.2' && pccStation && PCC_METADATA[pccStation]) {
            const stationInfo = PCC_METADATA[pccStation];
            updateMapViewState({
                longitude: stationInfo.longitude,
                latitude: stationInfo.latitude,
                zoom: 14,
                transitionDuration: 1500
            });
        }
    }, [pccStation, state.variableSelected, updateMapViewState]);

    // --- Fly to Ngardok Lake when selected ---
    useEffect(() => {
        if (state.variableSelected === '3.8.1') {
            updateMapViewState({
                longitude: 134.603611,
                latitude: 7.5125,
                zoom: 14,
                transitionDuration: 1500
            });
        }
    }, [state.variableSelected, updateMapViewState]);

    // --- getTooltip Function --- [cite: code-base/App.js]
    function getTooltip({ object, layer }) {
        // Your existing getTooltip logic [cite: code-base/App.js]
        var tooltipString = ""
        if(object){
          if(layer && String(layer.id).startsWith('hurricane-')) {
            const p = object?.properties ?? object ?? {};
            const validRaw = p.valid || p.time || p.latest_time || null;
            const vmaxKt = p.vmax_kt ?? p.vmax ?? null;
            const mslpMb = p.mslp_mb ?? p.mslp ?? null;
            const catMatch = String(p.sshws || '').match(/^Cat([1-5])$/);
            const catNum = catMatch ? parseInt(catMatch[1], 10) : null;
            const sshwsLabel = p.sshws_label || p.sshws || null;

            let whenStr = '—';
            if (validRaw) {
                const d = new Date(validRaw);
                if (!isNaN(d.getTime())) {
                    const now = new Date();
                    const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const startD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                    const dayDiff = Math.round((startNow - startD) / 86400000);
                    const dayPart = dayDiff === 0 ? 'Today' : dayDiff === 1 ? 'Yesterday' : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
                    const timePart = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true }).replace(':00', '');
                    whenStr = `${dayPart} - ${timePart}`;
                } else {
                    whenStr = String(validRaw);
                }
            }

            const inHg = mslpMb != null ? (mslpMb / 33.8638866667) : null;
            const speedPressureStr = `${vmaxKt != null ? vmaxKt : '—'}kt | ${inHg != null ? inHg.toFixed(3) : '—'}inHg`;
            const categoryStr = catNum != null ? `SSHWS category ${catNum}` : (sshwsLabel ? String(sshwsLabel) : '—');

            tooltipString = `
                <div><b>${whenStr}</b></div>
                <div>${speedPressureStr}</div>
                <div>${categoryStr}</div>
            `;
          } else if (Number.isFinite(Number(object.dp))) {
            const directionTo = Number(object.direction_to_deg ?? object.dp);
            const directionFrom = Number(object.direction_from_deg ?? ((directionTo + 180) % 360));
            const compass = (degrees) => ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(degrees / 45) % 8];
            const height = Number(object.wave_height_m);
            tooltipString = `<div><b>Wave direction</b></div><div>Toward ${compass(directionTo)} (${directionTo.toFixed(0)}°)</div><div>From ${compass(directionFrom)} (${directionFrom.toFixed(0)}°)</div><div>Wave angle: ${directionTo.toFixed(0)}°</div>${Number.isFinite(height) ? `<div>${height.toFixed(2)} m significant height</div>` : ''}`;
          }else if(object.properties){
            if (apccParsed && object.properties.value !== undefined) {
              const value = Number(object.properties.value);
              const valid = object.properties.time || state.dateSelected?.[state.sliderValue] || '';
              if (apccParsed.model === 'probabilistic') {
                const category = object.properties.category_label || 'Most likely tercile';
                tooltipString = `<div><b>${activeApccVariableMeta?.label || 'APCC MME'}</b></div><div>${category}: ${value.toFixed(1)}%</div><div style="margin-top:2px;color:#b8dce6;font-size:0.9em">Valid ${valid}</div>`;
              } else {
                tooltipString = `<div><b>${activeApccVariableMeta?.label || 'APCC MME'}</b></div><div>${value.toFixed(2)} ${activeApccVariableMeta?.unit || ''}</div><div style="margin-top:2px;color:#b8dce6;font-size:0.9em">Valid ${valid}</div>`;
              }
            }else if(object.properties.tos !== undefined || object.properties.sst !== undefined){
              let temp = Number(object.properties.tos !== undefined ? object.properties.tos : object.properties.sst);
              if (unit === 'F') temp = temp * 9 / 5 + 32;
              tooltipString = `<div><b>SST</b></div><div>${temp.toFixed(2)} °${unit}</div>`
            }else if(object.properties.heatwave_category !== undefined){
              tooltipString = `<div><b>Heatwave Category</b></div><div>${object.properties.heatwave_category}</div>`
            }else if(CRW_DIAL_FIELDS.some(field => object.properties[field] != null) || state.variableSelected?.startsWith('7.4.3')){
              const dials = crwDialsAtLocation(
                object.properties,
                Number(object.properties.lat ?? object.properties.latitude),
                Number(object.properties.lon ?? object.properties.longitude),
                crwNativeDialGridRef.current
              );
              const dialRows = dials.map((value, index) => `<div style="display:flex;justify-content:space-between;gap:16px"><span>${CRW_DIAL_LABELS[index]}</span><b>${value == null ? '—' : `Alert ${value}`}</b></div>`).join('');
              const resolution = object.properties.value != null && object.properties['CRW_BAA_Week_01-04'] == null
                ? 'Current: NOAA CRW native 5 km cell'
                : 'NOAA CRW alert-grid cell';
              tooltipString = `<div><b>Bleaching status</b></div>
                               <div style="margin:3px 0 6px;color:#b8dce6;font-size:0.9em">${resolution}</div>
                               ${dialRows}`
            }else if (state.variableSelected?.startsWith('7.4.4.') && object.properties.value !== undefined) {
                const product = CRW_DAILY_PRODUCTS[crwProductFromVariable(state.variableSelected)];
                const value = Number(object.properties.value);
                const displayValue = product?.unit === '°C' && unit === 'F'
                  ? (value * 9 / 5 + 32).toFixed(2)
                  : value.toFixed(product?.unit === '°C-weeks' ? 1 : 2);
                const displayUnit = product?.unit === '°C' && unit === 'F' ? '°F' : product?.unit;
                tooltipString = `<div><b>${product?.label || 'Coral Reef Watch'}</b></div><div>${displayValue} ${displayUnit || ''}</div><div style="margin-top:2px;color:#b8dce6;font-size:0.9em">NOAA CRW daily 5 km product</div>`;
            }else if ((state.variableSelected === '8.2.1' || state.variableSelected === '8.2.2' || state.variableSelected === '8.3.1') && object.properties.value !== undefined) {
                const isPhyto = state.variableSelected === '8.3.1';
                const value = Number(object.properties.value);
                const valid = object.properties.date || state.dateSelected?.[state.sliderValue] || '';
                tooltipString = `<div><b>${isPhyto ? 'Median phytoplankton size' : 'Chlorophyll-a'}</b></div><div>${value.toPrecision(4)} ${isPhyto ? 'µm' : 'mg m⁻³'}</div><div style="margin-top:2px;color:#b8dce6;font-size:0.9em">Monthly satellite composite · ${valid}</div><div style="margin-top:2px;color:#b8dce6;font-size:0.9em">Tap to inspect this 0.25° cell over time</div>`;
            }else if(object.properties['name']){
              tooltipString = `<div>${object.properties["name"]}</div>`
            }else if(object.properties['value'] !== undefined){
                if (state.variableSelected === '6.1.1.3') {
                    const seaLevel = Number(object.properties['value']);
                    const date = object.properties.date || state.dateSelected?.[state.sliderValue] || '';
                    const coastal = object.properties.coastal ? '<div style="margin-top:2px;color:#b8dce6;font-size:0.9em">Cell touches land within the selected EEZ</div>' : '';
                    tooltipString = `<div><b>Monthly mean sea-level anomaly</b></div><div>${seaLevel.toFixed(3)} m · ${(seaLevel * 100).toFixed(1)} cm</div><div style="margin-top:2px;color:#b8dce6;font-size:0.9em">Copernicus Marine altimetry · ${date}</div>${coastal}<div style="margin-top:2px;color:#b8dce6;font-size:0.9em">Tap for the daily record</div>`;
                } else if (state.variableSelected && state.variableSelected.startsWith('3.7')) {
                    tooltipString = `<div><b>Rainfall</b></div><div>${Number(object.properties['value']).toFixed(2)} mm</div>`;
                } else if (state.variableSelected === '7.1.2.2' || state.variableSelected === '7.1.2.1') {
                    let temp = Number(object.properties['value']);
                    if (unit === 'F') temp = temp * 9 / 5 + 32;
                    tooltipString = `<div><b>SST</b></div><div>${temp.toFixed(2)} °${unit}</div>`;
                } else {
                    tooltipString = `<div>Value: ${object.properties['value']}</div>`;
                }
            }else if(object.properties['color']){
              if(object.properties['color']  === '#000000'){
                 tooltipString = `<div><b>Δ Sea Level</b></div><div>N/A</div>`;
              }
            }
          } else if(layer && (layer.id.startsWith('boundary-') || layer.id.startsWith('boundary-points-'))) {
            const configId = layer.id.replace('boundary-', '').replace('boundary-points-', '');
            const config = BOUNDARY_LAYERS_CONFIG.find(c => c.id === configId);
            const featureName = config?.tooltipProperties
              ?.map(key => {
                const value = object.properties?.[key];
                return key === 'area_km2' && Number.isFinite(Number(value))
                  ? `${(Number(value) / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} thousand km²`
                  : value;
              })
              .filter(Boolean)
              .join(' · ')
              || object.properties.Name || object.properties.NAME || object.properties.Site_Name || object.properties.SITE_NAME || object.properties.Type || 'Feature';
            if (config) {
                tooltipString = `<div><b style="color: rgb(${config.color.join(',')})">${config.label || 'Boundary'}</b></div><div>${featureName}</div>`;
            }
          } else if(object.id && PCC_METADATA[object.id]){
             tooltipString = `<div><b>${PCC_METADATA[object.id].name}</b></div><div>PCC Probe Station</div>`
          } else if(object.id && AWS_METADATA[object.id]){
             tooltipString = `<div><b>${AWS_METADATA[object.id].name}</b></div><div>Automated Weather Station</div>`
          } else if(object.name){
             tooltipString = `<div>${object.name}</div>`
          }
        }
        // Basic styling for the tooltip
        return object && {
            html: `<div style="background-color: #334; color: white; padding: 5px; border-radius: 3px; font-size: 0.8em;">${tooltipString}</div>`,
            style: { // Prevent default browser tooltip
                 backgroundColor: 'transparent',
                 border: 'none',
                 boxShadow: 'none',
            }
        };
    } // [cite: code-base/App.js]

    

    // --- Map Interaction Handlers ---
    const tosClick = useCallback((info) => { // [cite: code-base/App.js]
        if (!info || !info.object || !info.object.properties) return;
        
        const props = info.object.properties;
        const rawLat = props.latitude !== undefined ? props.latitude : (props.lat !== undefined ? props.lat : null);
        const rawLon = props.longitude !== undefined ? props.longitude : (props.lon !== undefined ? props.lon : null);
        
        if (rawLat === null || rawLon === null) return;

        dispatch({ 
            type: 'SET_LOCATION', 
            payload: { 
                long: rawLon.toString(), 
                lat: rawLat.toString() 
            } 
        });
        setRightDrawerOpen(true);
        console.log("TOS Clicked: ", rawLon, rawLat);
    }, []); // [cite: code-base/App.js]

    const CRWClick = useCallback((info) => { // [cite: code-base/App.js]
        if (!info || !info.object) return;
        // loaders.gl FlatGeobuf features normally expose a GeoJSON
        // ``properties`` object. Keep the flattened-object fallback too: it
        // makes click-to-chart work consistently across native and resampled
        // CRW layers.
        const props = info.object.properties || info.object || {};
        const selectedVariable = selectedVariableRef.current;
        // Daily monitoring products open a cell history plot. Bleaching alert
        // products are deliberately excluded: their click action updates the
        // four regional-gauge dials for this selected grid cell instead.
        if (selectedVariable?.startsWith('7.4.4.')) {
            const rawLat = props.lat ?? props.latitude ?? info.coordinate?.[1];
            const rawLon = props.lon ?? props.longitude ?? info.coordinate?.[0];
            if (rawLat == null || rawLon == null) return;
            setCrwDailyHistoryKey(selectedVariable);
            setRightDrawerOpen(true);
            dispatch({
                type: 'SET_LOCATION',
                payload: {
                    long: String(rawLon),
                    lat: String(rawLat),
                    scatterplotVariable: 'crw_daily',
                },
            });
            return;
        }
        if (!selectedVariable?.startsWith('7.4.3')) return;

        const rawLat = Number(props.lat ?? props.latitude ?? info.coordinate?.[1]);
        const rawLon = Number(props.lon ?? props.longitude ?? info.coordinate?.[0]);
        if (!Number.isFinite(rawLat) || !Number.isFinite(rawLon)) return;

        // Native 5 km tiles supply the current value. Merge it with the
        // nearest matching all-dial alert record for the three outlooks.
        const dials = crwDialsAtLocation(props, rawLat, rawLon, crwNativeDialGridRef.current);
        dispatch({ 
            type: 'SET_CRW_DATA', 
            payload: { 
                long: rawLon.toString(),
                lat: rawLat.toString(),
                crwArray: dials.map(value => value ?? 0)
            } 
        });
    }, []); // state is accessed through refs because deck.gl can retain callbacks

    const oisstGridClick = useCallback((info) => {
        if (!info || !info.object) return;
        
        const props = info.object.properties || {};
        const rawLat = props.lat !== undefined ? props.lat : (props.latitude !== undefined ? props.latitude : (info.coordinate ? info.coordinate[1] : null));
        const rawLon = props.lon !== undefined ? props.lon : (props.longitude !== undefined ? props.longitude : (info.coordinate ? info.coordinate[0] : null));
        
        if (rawLat === null || rawLon === null) return;

        dispatch({
            type: 'SET_LOCATION',
            payload: {
                long: rawLon.toString(),
                lat: rawLat.toString(),
            }
        });
        setRightDrawerOpen(true);
        console.log("OISST Grid Clicked: ", rawLon, rawLat);
    }, []);

    const arrowClick = useCallback((info) => { // [cite: code-base/App.js]
         if (!info || !info.object) return;
        dispatch({ type: 'SET_LOCATION', payload: { long: info.object.longitude.toString(), lat: info.object.latitude.toString(), scatterplotVariable: "wave" } });
        setRightDrawerOpen(true);
    }, []); // [cite: code-base/App.js]

    const waveGridClick = useCallback((info) => {
        if (!info?.object) return;
        const props = info.object.properties || {};
        const rawLat = props.latitude ?? props.lat ?? info.coordinate?.[1];
        const rawLon = props.longitude ?? props.lon ?? info.coordinate?.[0];
        if (!Number.isFinite(Number(rawLat)) || !Number.isFinite(Number(rawLon))) return;
        dispatch({
            type: 'SET_LOCATION',
            payload: {
                long: String(rawLon),
                lat: String(rawLat),
                scatterplotVariable: 'wave',
            },
        });
        setRightDrawerOpen(true);
    }, []);

    const oceanColorGridClick = useCallback((info) => {
        if (!info?.object) return;
        const props = info.object.properties || {};
        const rawLat = Number(props.latitude ?? props.lat ?? info.coordinate?.[1]);
        const rawLon = Number(props.longitude ?? props.lon ?? info.coordinate?.[0]);
        if (!Number.isFinite(rawLat) || !Number.isFinite(rawLon)) return;
        const lat = (Math.round(rawLat * 4) / 4).toFixed(2);
        const lon = (Math.round(rawLon * 4) / 4).toFixed(2);
        const product = state.variableSelected === '8.3.1'
            ? 'phytoplankton_size'
            : (state.variableSelected === '8.2.1' ? 'chlorophyll_linear' : 'chlorophyll_logarithmic');
        const scope = rmiPortal ? 'rmi' : 'palau';
        dispatch({
            type: 'SET_LOCATION',
            payload: {
                lat,
                long: lon,
                scatterplotVariable: `ocean_color:${product}:${scope}:${lat}:${lon}`,
            },
        });
        setRightDrawerOpen(true);
    }, [rmiPortal, state.variableSelected]);

    const regionalSeaLevelGridClick = useCallback((info) => {
        if (!info?.object) return;
        const props = info.object.properties || {};
        const lat = Number(props.latitude ?? props.lat ?? info.coordinate?.[1]);
        const lon = Number(props.longitude ?? props.lon ?? info.coordinate?.[0]);
        if (!Number.isFinite(lat) || !Number.isFinite(lon) || !props.cell_id || !props.history_block) return;
        dispatch({
            type: 'SET_LOCATION',
            payload: {
                lat: String(lat),
                long: String(lon),
                scatterplotVariable: `regional_sea_level:cell:${regionalSeaLevelScope}:${props.history_block}:${props.cell_id}`,
            },
        });
        setRightDrawerOpen(true);
    }, [regionalSeaLevelScope]);

    const seaLevelStationClick = useCallback((info) => {
        if (!info?.object?.id) return;
        dispatch({
            type: 'SET_LOCATION',
            payload: {
                lat: String(info.object.latitude),
                long: String(info.object.longitude),
                scatterplotVariable: `regional_sea_level:station:${regionalSeaLevelScope}:${info.object.id}`,
            },
        });
        setRightDrawerOpen(true);
    }, [regionalSeaLevelScope]);

    const stationClick = useCallback((info) => { // [cite: code-base/App.js]
        if (!info || !info.object || !info.object.name) return;
        if (info.object.name === "NGARAARD, BABELDAOB, PALAU - 219") {
            dispatch({ type: 'UPDATE_CONFIG', payload: { scatterplotVariable: "219" } });
            setRightDrawerOpen(true);
            return;
        }
        if (/^(?:PS|RM)/.test(String(info.object.id || ''))) {
            window.dispatchEvent(new CustomEvent('ghcndStationChange', { detail: info.object }));
        }
    }, []); // [cite: code-base/App.js]

     const eezClick = useCallback((info) => { // [cite: code-base/App.js]
         if(info && info.object && info.object.properties && info.object.properties.x_1){
             // Use the updateMapViewState function for smooth transition
             updateMapViewState({
                 latitude: info.object.properties.y_1,
                 longitude: info.object.properties.x_1,
                 zoom: 6.5,
                 bearing: 0,
                 pitch: 0,
             });
         }
         console.log("EEZClick");
     }, [updateMapViewState]); // [cite: code-base/App.js]

    const mhwClick = useCallback((info) => {
        if (!info || !info.object) return;
        
        // Safely extract lat/lon (prioritize properties inside GeoJSON, fallback to raw deck.gl coordinates)
        const props = info.object.properties || {};
        const rawLat = props.lat !== undefined ? props.lat : (props.latitude !== undefined ? props.latitude : (info.coordinate ? info.coordinate[1] : null));
        const rawLon = props.lon !== undefined ? props.lon : (props.longitude !== undefined ? props.longitude : (info.coordinate ? info.coordinate[0] : null));
        
        if (rawLat === null || rawLon === null) return;

        const isCurrentlyMasked = mhwIsMaskedRef.current;

        // Round and store the coordinates inside the dashboard state
        dispatch({
            type: 'SET_LOCATION',
            payload: {
                lat: parseFloat(rawLat),
                long: parseFloat(rawLon),
                scatterplotVariable: isCurrentlyMasked ? 'mhw_masked' : 'mhw'
            }
        });
        setRightDrawerOpen(true);
        console.log(`MHW Grid Clicked: ${rawLon}, ${rawLat} | Masked: ${isCurrentlyMasked}`);
    }, []);

    // --- Handler Map for Config ---
    const CLICK_HANDLERS = useMemo(() => ({
        eez: eezClick,
        station: stationClick,
        arrow: arrowClick,
        wave_grid: waveGridClick,
        ocean_color: oceanColorGridClick,
        regional_sea_level: regionalSeaLevelGridClick,
        sea_level_station: seaLevelStationClick,
        tos: tosClick,
        crw: CRWClick,
        oisst_grid: oisstGridClick,
        forecast_grid: oisstGridClick, // same lat/lon → drawer behavior
        mhw: mhwClick,
        default: null
    }), [eezClick, stationClick, arrowClick, waveGridClick, oceanColorGridClick, regionalSeaLevelGridClick, seaLevelStationClick, tosClick, CRWClick, oisstGridClick, mhwClick]);

    // --- updateCMIPImmediate: Keep the extensive logic inside ---
    // Ensure all `set...` calls correctly update state. Use useCallback.
    const updateCMIPImmediate = useCallback((date, selectedVar) => {
        if (selectedVar === '7.2.1') {
            const isSwitchingToMHW = state.variableSelected !== '7.2.1';
            // For MHW, bypass getVariableConfig and let the dedicated MHW useEffect handle the URL mapping safely
            dispatch({ 
                type: 'UPDATE_CONFIG', 
                payload: { 
                    variableSelected: selectedVar, 
                    sliderValue: date,
                    currentOnClick: [CLICK_HANDLERS['mhw']],
                    ...(isSwitchingToMHW ? {
                        scatterplotVariable: mhwIsMaskedRef.current ? 'mhw_masked' : 'mhw',
                        lat: null, // Clear old coords so it defaults to Region
                        long: null
                    } : {})
                } 
            });
            return;
        }

        if (selectedVar === '3.8.1') {
            dispatch({
                type: 'UPDATE_CONFIG',
                payload: {
                    variableSelected: selectedVar,
                    currentJSON: false,
                    isSliderVisible: false,
                    scatterplotVariable: 'ngardok_lake',
                    crwEnabled: false
                }
            });
            return;
        }

        if (selectedVar === '2.1.2') {
            dispatch({
                type: 'UPDATE_CONFIG',
                payload: {
                    variableSelected: selectedVar,
                    currentJSON: false,
                    isSliderVisible: false,
                    scatterplotVariable: 'pcc_probes',
                    crwEnabled: false
                }
            });
            return;
        }

        if (selectedVar === '4.1') {
            dispatch({
                type: 'UPDATE_CONFIG',
                payload: {
                    variableSelected: selectedVar,
                    currentJSON: false,
                    isSliderVisible: false,
                    scatterplotVariable: 'aws',
                    crwEnabled: false
                }
            });
            return;
        }

        if (selectedVar.startsWith('3.7')) {
            const parts = selectedVar.split('.');
            const type = parts[2] === '1' ? 'median' : 'tertial';
            const periodMap = { '1': 'seasonal', '2': 'monthly', '3': 'fortnightly', '4': 'weekly' };
            const period = periodMap[parts[3]];
            const folderPath = `/jsonFiles/access/rain/${type}/${period}`;
            
            if (state.variableSelected === selectedVar && dateSelectedRef.current?.length > 0) {
                const filenames = dateSelectedRef.current;
                const safeDate = (date >= 0 && date < filenames.length) ? date : 0;
                const filename = filenames[safeDate];
                dispatch({ type: 'UPDATE_CONFIG', payload: { sliderValue: safeDate, currentJSON: [`${folderPath}/${filename}.json`] } });
            } else {
                fetch(`${folderPath}/index.json`)
                .then(res => {
                    if (!res.ok) throw new Error("index.json not found");
                    return res.json();
                })
                .then(filenames => {
                    filenames.sort();
                    const safeDate = 0;
                    const filename = filenames[safeDate];
                    
                    const marks = filenames.map((f, i) => {
                        const match = f.match(/\d{4}-\d{2}-\d{2}/);
                        const label = match ? match[0] : f;
                        if (i % Math.max(1, Math.floor(filenames.length / 5)) === 0 || i === filenames.length - 1) return { value: i, label };
                        return null;
                    }).filter(Boolean);

                    dispatch({
                        type: 'UPDATE_CONFIG',
                        payload: { variableSelected: selectedVar, sliderValue: safeDate, currentJSON: [`${folderPath}/${filename}.json`], isSliderVisible: true, dateSelected: filenames, markerSelected: marks, sliderTooltip: ({ value }) => {
                            const f = filenames[value];
                            const match = f?.match(/\d{4}-\d{2}-\d{2}/);
                            return match ? match[0] : (f || value);
                        }, scatterplotVariable: 'access-s', crwEnabled: false }
                    });
                })
                .catch(err => {
                    console.error("Missing index.json in", folderPath, err);
                    alert(`Error loading ACCESS-S files. Please ensure an index.json file containing an array of filenames (e.g. ["access2rainmonthly2025-06-16"]) exists at ${folderPath}`);
                });
            }
            return;
        }

        // 1. Get Configuration from variableConfig.js, passing extra options
        const config = getVariableConfig(selectedVar, date, {
            oisstMode,
            oisstRegion,
            oisstIndoPacific,
            crwPacific,
            crwRes,
            crwScope,
            crwAlertMode,
            crwNativeAlert,
            forecastMode,
            // RMI + Wake is a real separately-published forecast scope. It
            // uses the same selected date/render mode, not a visual overlay
            // on top of the RMI-only tile.
            forecastViewScope: rmiIncludeWake && forecastViewScope === 'rmi' ? 'rmi-wake' : forecastViewScope,
            forecastVar: activeForecastVar,
            apccMode,
            apccViewScope: effectiveApccScope,
            apccAggregation,
            waveViewScope,
            waveRenderMode,
            oceanColorMode,
            oceanColorScope: rmiPortal ? 'rmi' : 'palau',
            regionalSeaLevelScope,
            regionalSeaLevelMode,
            cmip6Catalog: activeProjectionCatalog,
            cmip6Scope: rmiIncludeWake && cmip6Scope === 'rmi' ? 'rmi-wake' : cmip6Scope,
            cmip6RenderMode,
            cmip6Model,
            portalScope: rmiPortal ? 'rmi' : 'palau',
        });

        // Most national products already carry the official RMI EEZ as a
        // second, non-pickable map layer. Keep the Wake option in that exact
        // layer slot rather than adding a second competing outline. This also
        // keeps the boundary correct when users change dates or render modes.
        if (rmiIncludeWake && Array.isArray(config.jsonPaths)) {
            config.jsonPaths = config.jsonPaths.map((path) => (
                typeof path === 'string' && /\/CMIPtas\/rmiEEZ(?:\.json|\.fgb)?$/.test(path)
                    ? '/CMIPtas/rmiEEZWithWake.json'
                    : path
            ));
        }

        // 2. Resolve Click Handlers based on keys
        const clickHandlers = config.handlerKeys.map(key => CLICK_HANDLERS[key] || null);
        const currentSeaLevelSelection = String(scatterplotVariableRef.current || '');
        const [, , currentSeaLevelScope] = currentSeaLevelSelection.split(':');
        const preserveSeaLevelSelection = selectedVar === '6.1.1.3'
            && state.variableSelected === selectedVar
            && currentSeaLevelSelection.startsWith('regional_sea_level:')
            && currentSeaLevelScope === regionalSeaLevelScope;

        // --- Update State ---
        const payload = {
            variableSelected: selectedVar,
            sliderValue: config.oisstDateIndex != null
                ? config.oisstDateIndex
                : (config.forecastDateIndex != null
                    ? config.forecastDateIndex
                    : (config.apccDateIndex != null
                        ? config.apccDateIndex
                        : (config.crwDateIndex != null
                            ? config.crwDateIndex
                            : (config.oceanColorDateIndex != null ? config.oceanColorDateIndex : date)))),
            scatterplotVariable: preserveSeaLevelSelection ? scatterplotVariableRef.current : config.scatterplotVar,
            currentJSON: config.jsonPaths,
            currentOnClick: clickHandlers,
            dateSelected: config.dateData,
            markerSelected: config.markerData,
            sliderTooltip: config.tooltip,
            colorbar: config.colorbar,
            crwEnabled: config.crwEnabled,
            isSliderVisible: config.sliderVisible,
            currentDial: config.currentDial,
        };

        // Only overwrite map coordinates when switching to a completely new variable.
        // This prevents timeline slider interactions from constantly resetting the user's selected location!
        const switchingVariable = state.variableSelected !== selectedVar;
        if (switchingVariable) {
            payload.lat = config.lat;
            payload.long = config.lon;
        }

        dispatch({
            type: 'UPDATE_CONFIG',
            payload: payload
        });

        // This also covers programmatic variable changes (tutorials, links,
        // and future deep links), which do not pass through the menu handler
        // below. Scope-aware products retain their own camera; only stale
        // Palau-only defaults are remapped for the RMI portal entry point.
        if (switchingVariable && rmiPortal && isPalauDefaultView(config.view)) {
            updateMapViewState({ ...RMI_PORTAL_VIEW, transitionDuration: 800 }, { rightDrawerVisible: hasRightPanelGraph(selectedVar) });
        }

    }, [CLICK_HANDLERS, state.variableSelected, oisstMode, oisstRegion, oisstIndoPacific, crwPacific, crwRes, crwScope, crwAlertMode, crwNativeAlert, forecastMode, forecastViewScope, activeForecastVar, apccMode, effectiveApccScope, apccAggregation, waveViewScope, waveRenderMode, oceanColorMode, regionalSeaLevelScope, regionalSeaLevelMode, activeProjectionCatalog, cmip6Scope, cmip6RenderMode, cmip6Model, rmiIncludeWake, rmiPortal, updateMapViewState]);

    // Re-run the CRW config when the Pacific toggle / resolution changes so the
    // map immediately swaps between the Palau-box and the whole-Pacific layer.
    useEffect(() => {
        if (state.variableSelected && (state.variableSelected.startsWith('7.4.3') || state.variableSelected.startsWith('7.4.4'))) {
            updateCMIPImmediate(state.sliderValue, state.variableSelected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crwPacific, crwRes, crwScope, crwAlertMode, crwNativeAlert]);

    useEffect(() => {
        if (['8.2.1', '8.2.2', '8.3.1'].includes(state.variableSelected)) {
            updateCMIPImmediate(state.sliderValue, state.variableSelected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oceanColorMode, rmiPortal]);

    // Re-resolve the active map product when the optional Wake boundary is
    // changed. The selected date, render mode and all other user choices stay
    // intact; only the RMI boundary asset is exchanged.
    useEffect(() => {
        if (!rmiPortal || !state.variableSelected) return;
        // Wake sits north-east of the core RMI EEZ.  When the optional
        // combined analysis area is enabled, frame the actual data extent so
        // the newly loaded Wake cells are not left offscreen behind the old
        // RMI-only camera position.
        if (rmiIncludeWake && forecastViewScope === 'rmi') {
            updateMapViewState({
                lat: 12.3,
                long: 166.7,
                zoom: 3.9,
                pitch: 0,
                bearing: 0,
                transitionDuration: 650,
            });
        }
        updateCMIPImmediate(state.sliderValue, state.variableSelected);
        // updateCMIPImmediate deliberately owns the broad configuration
        // dependency set. This effect is only the boundary-state bridge.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rmiIncludeWake]);

    useEffect(() => {
        if (state.variableSelected !== '6.1.1.3') return;
        const config = getVariableConfig('6.1.1.3', state.sliderValue ?? 0, {
            regionalSeaLevelScope,
            regionalSeaLevelMode,
        });
        const dates = config.dateData || [];
        const safeDate = Math.max(0, Math.min(state.sliderValue ?? 0, Math.max(dates.length - 1, 0)));
        updateCMIPImmediate(safeDate, '6.1.1.3');
        const tutorialAction = TUTORIAL_STEPS[tutorialStep - 1]?.action;
        if (config.view && tutorialAction !== 'regional-sea-level-virtual-station') {
            updateMapViewState({ ...config.view, transitionDuration: 800 });
        }
        dispatch({
            type: 'UPDATE_CONFIG',
            payload: { scatterplotVariable: config.scatterplotVar },
        });
    }, [regionalSeaLevelScope, regionalSeaLevelMode, tutorialStep, updateCMIPImmediate, updateMapViewState, state.sliderValue, state.variableSelected]);

    // --- Hook to automatically navigate to the variable tied to the current tutorial step ---
    const lastProcessedStep = useRef(-1);
    useEffect(() => {
        if (tutorialStep > 0 && tutorialStep <= TUTORIAL_STEPS.length) {
            if (lastProcessedStep.current !== tutorialStep) {
                lastProcessedStep.current = tutorialStep;
                const stepConfig = TUTORIAL_STEPS[tutorialStep - 1];
                
                if (stepConfig.variable) {
                    updateCMIPImmediate(0, stepConfig.variable);
                    if (stepConfig.closeRightPanel) {
                        setRightDrawerOpen(false);
                    } else if (hasRightPanelGraph(stepConfig.variable) || stepConfig.variable === '7.2.1') {
                        setRightDrawerOpen(true);
                    }
                    
                    const standardStationVars = ['2.1.2', '3.8.1', '4.1'];
                    const isOisstTutorialStep = stepConfig.action?.startsWith('oisst-');
                    if (!standardStationVars.includes(stepConfig.variable) && !isOisstTutorialStep) {
                        updateMapViewState({ longitude: 134.5, latitude: 7.5, zoom: 5.5, bearing: 0, pitch: 0, transitionDuration: 1500 });
                    }

                    // Auto-play timeline for CMIP6 step, auto-pause otherwise
                    if (stepConfig.variable === '7.1.3.1.5') {
                        if (!state.isPlaying) {
                            dispatch({ type: 'TOGGLE_PLAY' });
                        }
                    } else {
                        if (state.isPlaying) {
                            dispatch({ type: 'TOGGLE_PLAY' });
                        }
                    }
                } else {
                    // For steps without a specific variable (e.g. Welcome screen)
                    if (state.isPlaying) {
                        dispatch({ type: 'TOGGLE_PLAY' });
                    }
                }
            }
        } else if (tutorialStep === 0 && lastProcessedStep.current !== -1) {
            // Reset state and pause animation when tutorial is closed/finished
            lastProcessedStep.current = -1;
            if (state.isPlaying) {
                dispatch({ type: 'TOGGLE_PLAY' });
            }
        }
    }, [tutorialStep, updateCMIPImmediate, updateMapViewState, state.isPlaying]);

    // Move the tutorial through real AWS selections so the station dashboard
    // and map demonstrate the same interaction visitors use themselves.
    useEffect(() => {
        const tutorialAction = TUTORIAL_STEPS[tutorialStep - 1]?.action;
        const stationIdByAction = {
            'aws-station-airai': 'PW74351',
            'aws-station-aimeliik': 'PW74450',
            'aws-station-peleliu': 'PW74021',
        };
        const stationId = stationIdByAction[tutorialAction];
        const station = AWS_METADATA[stationId];
        if (!station) return;

        setAwsStation(stationId);
        dispatch({
            type: 'SET_LOCATION',
            payload: { lat: station.latitude, long: station.longitude, scatterplotVariable: 'aws' },
        });
    }, [tutorialStep]);

    // The sea-level tutorial first opens the gauge comparison, then switches
    // to a real gridded-cell history to demonstrate virtual stations.
    useEffect(() => {
        const tutorialAction = TUTORIAL_STEPS[tutorialStep - 1]?.action;
        if (!['regional-sea-level-overview', 'regional-sea-level-virtual-station'].includes(tutorialAction)) return;
        if (state.variableSelected !== '6.1.1.3') return;

        setRegionalSeaLevelScope('palau');
        setRegionalSeaLevelMode('gridded');
        setRightDrawerOpen(true);

        if (tutorialAction === 'regional-sea-level-overview') {
            setMapState(previous => ({
                ...previous,
                viewState: {
                    ...previous.viewState,
                    longitude: 134.48,
                    latitude: 7.34,
                    zoom: 5,
                    bearing: 0,
                    pitch: 0,
                    padding: { top: 0, right: 0, bottom: 0, left: 0 },
                    transitionDuration: 700,
                },
            }));
            dispatch({
                type: 'SET_LOCATION',
                payload: {
                    lat: '7.33',
                    long: '134.46',
                    scatterplotVariable: 'regional_sea_level:station:palau:7',
                },
            });
        } else {
            setMapState(previous => ({
                ...previous,
                viewState: {
                    ...previous.viewState,
                    // Offset the camera east so the selected cell sits in the
                    // uncovered map area beside the analysis drawer.
                    longitude: 135.75,
                    latitude: 7.125,
                    zoom: 6.8,
                    bearing: 0,
                    pitch: 0,
                    padding: { top: 0, right: 0, bottom: 0, left: 0 },
                    transitionDuration: 700,
                },
            }));
            dispatch({
                type: 'SET_LOCATION',
                payload: {
                    lat: '7.125',
                    long: '134.625',
                    scatterplotVariable: 'regional_sea_level:cell:palau:+00_130:7.125_134.625',
                },
            });
        }
    }, [state.variableSelected, tutorialStep]);

    // The OISST walkthrough is intentionally user-paced: each tutorial step
    // establishes one state and waits for the visitor to choose Next or Back.
    useEffect(() => {
        const tutorialAction = TUTORIAL_STEPS[tutorialStep - 1]?.action;
        if (!tutorialAction?.startsWith('oisst-')) {
            setLayersMenuAnchor(null);
            return;
        }

        setTimelineCollapsed(false);
        setOisstMode('gridded');
        setConvergenceCurrentVisible(true);
        setConvergenceClimatologyVisible(true);

        if (tutorialAction === 'oisst-local-view') {
            setLayersMenuAnchor(null);
            setConvergenceEnabled(false);
            setOisstViewScope(rmiPortal ? 'rmi' : 'palau');
            setMapState(previous => ({
                ...previous,
                viewState: {
                    ...previous.viewState,
                    ...(rmiPortal ? RMI_PORTAL_VIEW : { longitude: 134.5, latitude: 7.5, zoom: 5.5, bearing: 0, pitch: 0 }),
                    transitionDuration: 700,
                },
            }));
        } else if (tutorialAction === 'oisst-indo-pacific-view') {
            setLayersMenuAnchor(null);
            setConvergenceEnabled(false);
            setOisstViewScope('indo-pacific');
        } else if (tutorialAction === 'oisst-convergence-overlay') {
            setOisstViewScope('indo-pacific');
            setConvergenceEnabled(true);
            const layersButton = document.getElementById('tutorial-layers-btn');
            if (layersButton) setLayersMenuAnchor(layersButton);
        } else if (tutorialAction === 'oisst-convergence-playback') {
            setLayersMenuAnchor(null);
            setOisstViewScope('indo-pacific');
            setConvergenceEnabled(true);
            if (!isPlayingRef.current) dispatch({ type: 'TOGGLE_PLAY' });
        }
    }, [rmiPortal, tutorialStep]);

    // --- Effect: Refresh map when OISST mode/scope toggles (not on every slider tick) ---
    useEffect(() => {
        if (state.variableSelected !== '7.1.2.2') return;
        const dates = oisstIndoPacific
            ? oisstIndoPacificDates
            : (oisstRegion === 'rmi' ? oisstRmiDates : oisstPalauDates);
        const safeDate = Math.max(0, Math.min(state.sliderValue ?? 0, Math.max(dates.length - 1, 0)));
        updateCMIPImmediate(safeDate, '7.1.2.2');
        const config = getVariableConfig('7.1.2.2', safeDate, {
            oisstMode,
            oisstRegion,
            oisstIndoPacific
        });
        if (config.view) {
            updateMapViewState({
                ...config.view,
                transitionDuration: 800
            });
        }
        // Clear point selection in the same style of update that never omits currentJSON.
        if (oisstIndoPacific || oisstMode === 'contour') {
            dispatch({
                type: 'SET_LOCATION',
                payload: { lat: null, long: null }
            });
        }
    }, [oisstMode, oisstRegion, oisstIndoPacific, updateCMIPImmediate, updateMapViewState, state.sliderValue, state.variableSelected]);

    // --- Effect: Refresh map when CFS/GFS mode/scope toggles ---
    useEffect(() => {
        if (!isForecastVariable(state.variableSelected) && !isEkmanVariable(state.variableSelected)) return;
        const config = getVariableConfig(state.variableSelected, state.sliderValue ?? 0, {
            forecastMode,
            forecastViewScope,
            forecastVar: activeForecastVar,
        });
        const dates = config.dateData || [];
        const safeDate = Math.max(0, Math.min(state.sliderValue ?? 0, Math.max(dates.length - 1, 0)));
        updateCMIPImmediate(safeDate, state.variableSelected);
        if (config.view) {
            updateMapViewState({
                ...config.view,
                transitionDuration: 800
            });
        }
        if (forecastViewScope === 'indo-pacific' || forecastMode === 'contour') {
            dispatch({
                type: 'SET_LOCATION',
                payload: { lat: null, long: null }
            });
        }
    }, [forecastMode, forecastViewScope, activeForecastVar, updateCMIPImmediate, updateMapViewState, state.sliderValue, state.variableSelected]);

    // --- Effect: Refresh CMIP6 data and camera when scope/render/model changes ---
    useEffect(() => {
        if (!isCmipProjectionSelection(state.variableSelected)) return;
        const effectiveScope = effectiveCmip6Scope;
        if (activeCmip6Modes.length && !activeCmip6Modes.includes(cmip6RenderMode)) {
            const fallbackMode = effectiveScope === 'indo' && activeCmip6Modes.includes('standard-grid')
                ? 'standard-grid'
                : (['native', 'eez-grid', 'standard-grid', 'contour'].find((mode) => activeCmip6Modes.includes(mode)) || activeCmip6Modes[0]);
            setCmip6RenderMode(fallbackMode);
            return;
        }
        const config = getVariableConfig(state.variableSelected, state.sliderValue ?? 0, {
            cmip6Catalog: activeProjectionCatalog,
            cmip6Scope: effectiveScope,
            cmip6RenderMode,
            cmip6Model,
        });
        const dates = config.dateData || [];
        const safeDate = Math.max(0, Math.min(state.sliderValue ?? 0, Math.max(dates.length - 1, 0)));
        dispatch({
            type: 'UPDATE_CONFIG',
            payload: {
                variableSelected: state.variableSelected,
                sliderValue: safeDate,
                scatterplotVariable: config.scatterplotVar,
                currentJSON: config.jsonPaths,
                currentOnClick: config.handlerKeys.map((key) => CLICK_HANDLERS[key] || null),
                dateSelected: dates,
                markerSelected: config.markerData,
                sliderTooltip: config.tooltip,
                colorbar: config.colorbar,
                crwEnabled: config.crwEnabled,
                isSliderVisible: config.sliderVisible,
            },
        });
        if (config.view) {
            updateMapViewState({ ...config.view, transitionDuration: 800 });
        }
        if (effectiveScope === 'indo') {
            dispatch({ type: 'SET_LOCATION', payload: { lat: null, long: null } });
        }
    }, [activeCmip6Modes, activeProjectionCatalog, CLICK_HANDLERS, cmip6Model, cmip6RenderMode, effectiveCmip6Scope, state.sliderValue, state.variableSelected, updateMapViewState]);

    useEffect(() => {
        if (!['6.3.2.1', '6.3.2.2', '6.3.2.3'].includes(state.variableSelected)) return;
        const config = getVariableConfig(state.variableSelected, state.sliderValue ?? 0, { waveViewScope, waveRenderMode });
        const dates = config.dateData || [];
        // ERA5/NOAA entries intentionally remain empty until their own source
        // has published frames. Do not dispatch an identical zero-date update:
        // that would continuously re-run this effect and eventually crash the
        // page with React's maximum-update-depth warning.
        if (!dates.length) {
            if (config.view) updateMapViewState({ ...config.view, transitionDuration: 800 });
            return;
        }
        const safeDate = Math.max(0, Math.min(state.sliderValue ?? 0, Math.max(dates.length - 1, 0)));
        updateCMIPImmediate(safeDate, state.variableSelected);
        if (config.view) updateMapViewState({ ...config.view, transitionDuration: 800 });
    }, [waveViewScope, waveRenderMode, updateCMIPImmediate, updateMapViewState, state.sliderValue, state.variableSelected]);

    // --- Effect: Refresh APCC map when aggregation, display, or scope changes ---
    useEffect(() => {
        if (!parseApccId(state.variableSelected)) return;
        const config = getVariableConfig(state.variableSelected, state.sliderValue ?? 0, {
            apccMode,
            apccViewScope: effectiveApccScope,
            apccAggregation,
        });
        const dates = config.dateData || [];
        const safeDate = Math.max(0, Math.min(state.sliderValue ?? 0, Math.max(dates.length - 1, 0)));
        updateCMIPImmediate(safeDate, state.variableSelected);
        if (config.view) {
            updateMapViewState({ ...config.view, transitionDuration: 800 });
        }
        if (effectiveApccScope === 'indo-pacific' || apccMode === 'contour') {
            dispatch({ type: 'SET_LOCATION', payload: { lat: null, long: null } });
        }
    }, [apccMode, effectiveApccScope, apccAggregation, updateCMIPImmediate, updateMapViewState, state.sliderValue, state.variableSelected]);

    // --- Logic: Build Dynamic MHW URL ---
    const buildDataUrl = useCallback(() => {
        // Opt-in whole-Pacific layer: gridded + contours only, multi-resolution,
        // no masked variant. getJSON auto-prefers the .fgb sibling for speed.
        if (mhwScope === 'indo') {
            const mapType = mhwMapType === 'voronoi' ? 'gridded' : mhwMapType;
            const res = mhwRes || '0p5deg';
            return `/jsonFiles/mhw-Pacific/${res}/${mhwTimescale}_${mapType}/mhw_${mapType}_${mhwCurrentDate}.json`;
        }
        const rmi = mhwScope === 'rmi';
        const folder = `mhw_${mhwTimescale}_${mhwMapType}${mhwIsMasked && !rmi ? '_masked' : ''}`;
        const filename = `mhw_${mhwMapType}_${mhwCurrentDate}.json`;
        return `/jsonFiles/mhw/${rmi ? 'rmi/' : ''}${folder}/${filename}`;
    }, [mhwTimescale, mhwMapType, mhwIsMasked, mhwCurrentDate, mhwScope, mhwRes]);

    // Override map data exclusively when Marine Heat Waves is selected
    useEffect(() => {
        if (state.variableSelected === '7.2.1') {
            const newUrl = buildDataUrl();

            // Pre-condition guard: Break infinite loops by ONLY dispatching if the state actually needs an update
            if (state.currentJSON?.[0] !== newUrl || state.sliderValue !== mhwCurrentIndex) {
                // Format slider ticks cleanly (every 5 years for dense sets, all ticks for decadal)
                const mhwMarks = mhwDateOptions.map((date, index) => {
                    if (mhwTimescale === 'decadal') return { value: index, label: date };
                    if (mhwTimescale === 'seasonal' && date.endsWith('Q1') && parseInt(date.substring(0,4)) % 5 === 0) return { value: index, label: date.substring(0,4) };
                    if (mhwTimescale === 'monthly' && date.endsWith('-01') && parseInt(date.substring(0,4)) % 5 === 0) return { value: index, label: date.substring(0,4) };
                    return null;
                }).filter(Boolean);

                dispatch({ 
                    type: 'UPDATE_CONFIG', 
                    payload: { 
                        currentJSON: [newUrl],
                        isSliderVisible: true,
                        dateSelected: mhwDateOptions,
                        markerSelected: mhwMarks,
                        sliderValue: mhwCurrentIndex,
                        sliderTooltip: (val) => mhwDateOptions[val] || val
                    } 
                });
            }
        }
    }, [state.variableSelected, buildDataUrl, mhwDateOptions, mhwTimescale, mhwCurrentIndex, state.currentJSON, state.sliderValue]);

    // --- Animation Logic ---
    useAnimationLoop({
        isPlaying: state.isPlaying,
        isSliderVisible: state.isSliderVisible,
        sliderValue: state.sliderValue,
        dateSelected: state.dateSelected,
        variableSelected: state.variableSelected,
        updateCMIPImmediate,
        isMapLoading,
        isLoadTimedOut,
        animationSpeed
    });

    // --- Landing Page Handler ---
    const handleLandingEnter = useCallback((key) => {
        setLandingOpen(false);
        if (key && typeof key === 'string') {
            // Load the specific variable requested by the landing page card
            updateCMIPImmediate(0, key);
            if (hasRightPanelGraph(key) && isLargeScreen) {
                setRightDrawerOpen(true);
            }
        }
    }, [updateCMIPImmediate, isLargeScreen]);

    // --- menuBarOnClick (Placeholder - logic moved to App.js or handled by Gauge component itself) ---
    const menuBarOnClick = useCallback((e) => {
        console.log('CRW Menu Bar Click:', e.key);
        
        // Make sure a variable is already selected and it's a CRW variable
        if (state.variableSelected && state.variableSelected.startsWith("7.4.3")) {
            // Determine if we are looking at 'Current' or 'Historical' outlooks
            const baseKey = state.variableSelected.substring(0, 8); // e.g., "7.4.3.1" or "7.4.3.3"
    
            // Map the dial key to the correct final digit
            let newVar;
            switch (e.key) {
                case '2': newVar = `${baseKey}1`; break;
                case '3': newVar = `${baseKey}2`; break;
                case '4': newVar = `${baseKey}3`; break;
                case '5': newVar = `${baseKey}4`; break;
                default: return; // Do nothing if the key is unexpected
            }
            
            // Call updateCMIPImmediate to refresh the map and data
            // It uses the existing sliderValue (date) but with the new variable
            updateCMIPImmediate(state.sliderValue, newVar);
            if (isLargeScreen) {
                setRightDrawerOpen(true);
            }
        }
    }, [state.variableSelected, state.sliderValue, updateCMIPImmediate, isLargeScreen]); // Add dependencies
    // --- Menu items structure --- [cite: code-base/App.js]
    // Using React.useMemo to avoid recreating on every render
    const menuItems = useMemo(() => [
        
         // --- Ocean ---
          getItem('Ocean', 'ocean', Settings, [
              getItem('Sea Level', '6', Waves, [
                  getItem('Regional/Local', '6.1', null, [ getItem('Historical', '6.1.1.3'), ]),
                  getItem('Flood', '6.2', null, [ getItem('Flood Frequency - Historical', '6.2.2.3'),
                                                getItem('Flood Duration - Historical', '6.2.3.3'),
                                                // getItem('Flood Events - Historical', '6.2.4.3')
                   ]),
                  getItem('Waves', '6.3', null, [
                      getItem('Historical hindcast — PACCSAP/CAWCR', '6.3.2.1'),
                      getItem('Reanalysis — ERA5', '6.3.2.2'),
                      getItem('Conditions & forecast — NOAA WW3', '6.3.2.3'),
                  ]),
                  getItem('Forecasts', '6.5', null, [
                      getItem(FORECAST_ENTRIES['cfs.ssh'].label, 'cfs.ssh'),
                  ]),
              ]),
              getItem('Water Temperature', '7', Thermometer, [
                   getItem('Mean SST', '7.1', null, [
                       getItem('Monthly OISST', '7.1.2.2'),
                       getItem('CMIP6 Projection', '7.1.3', null, [
                           getItem('Projection', '7.1.3.1', null, [
                                getItem('ssp126', '7.1.3.1.2'),
                                getItem('ssp434', '7.1.3.1.5'),
                                getItem('ssp460', '7.1.3.1.6'),
                                getItem('ssp534-over', '7.1.3.1.7'),
                                getItem('ssp585', '7.1.3.1.8'),
                            ]),
                            getItem('Historical', '7.1.3.3'),
                       ]),
                       getItem('CMIP6 variable catalogue', 'cmip6.catalogue', null,
                           CMIP6_VARIABLE_MENU.map(({ group, items }) => getItem(
                               group,
                               `cmip6.group.${group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                               null,
                               items.map(([label, key]) => {
                                   const scenarios = cmip6ScenarioMenuChildren(key, cmip6Catalog);
                                   return scenarios.length
                                       ? getItem(label, `cmip6.variable.${key}`, null, scenarios)
                                       : getItem(`${label} · pending`, `cmip6.${key}.ssp126`);
                               }),
                           )),
                       ),
                       getItem('Forecasts', '7.1.forecasts', null, [
                           getItem(FORECAST_ENTRIES['cfs.sst'].label, 'cfs.sst'),
                           getItem(FORECAST_ENTRIES['gfs.sst'].label, 'gfs.sst'),
                           getItem('APCC MME', 'apcc.sst', null, apccMenuChildren('sst')),
                       ]),
                  ]),
                   getItem('Marine Heat Waves', '7.2', null, [
                       getItem('Historical', '7.2.1'),
                   ]),
                   getItem('Coral Reef Watch Products', '7.4', null, [
                        getItem('Degree Heating Week', '7.4.4.1'),
                        getItem('HotSpot', '7.4.4.2'),
                        getItem('CoralTemp SST', '7.4.4.3'),
                        getItem('SST Anomaly', '7.4.4.4'),
                        getItem('SST Trend (7-day)', '7.4.4.5'),
                        getItem('Coral Bleaching Alert', '7.4.3', null, [
                           getItem('Current Outlook', '7.4.3.1', null, [
                             getItem('Current', '7.4.3.1.1'),
                              getItem('Weeks 1-4', '7.4.3.1.2'),
                              getItem('Weeks 5-8', '7.4.3.1.3'),
                              getItem('Weeks 9-12', '7.4.3.1.4'),
                            ]),
                           getItem('Historical Outlook', '7.4.3.3', null, [
                                getItem('Current', '7.4.3.3.1'),
                                getItem('Weeks 1-4', '7.4.3.3.2'),
                                getItem('Weeks 5-8', '7.4.3.3.3'),
                                getItem('Weeks 9-12', '7.4.3.3.4'),
                            ]),
                        ]),
                   ]),
              ]),
              getItem('Salinity', '9', Droplets, [
                  getItem('Forecasts', '9.1', null, [
                      getItem(FORECAST_ENTRIES['cfs.sss'].label, 'cfs.sss'),
                  ]),
              ]),
              getItem('Currents', '10', Activity, [
                  getItem('Forecasts', '10.1', null, [
                      getItem(FORECAST_ENTRIES['cfs.current'].label, 'cfs.current'),
                  ]),
              ]),
               getItem('Biochemistry', '8', Activity, [
                    getItem('Chlorophyll', '8.2', null, [
                        getItem('Linear', '8.2.1'),
                        getItem('Logarithmic', '8.2.2')
                    ]),
                    getItem('Phytoplankton Size', '8.3', null, [
                         getItem('Oceanwatch', '8.3.1')
                     ]),
                    getItem('Ekman Upwelling (Tuna Tool)', '8.4', null, [
                        getItem(FORECAST_ENTRIES['ekman.metop'].label, 'ekman.metop'),
                        getItem(FORECAST_ENTRIES['ekman.cfs'].label, 'ekman.cfs'),
                        getItem(FORECAST_ENTRIES['ekman.gfs'].label, 'ekman.gfs'),
                    ]),
               ]),
          ]),
         // --- Atmosphere ---
         getItem('Atmosphere', 'atm', Wind, [
             ...(SHOW_CMIP7_IN_MENU ? [getItem('CMIP7 model output', 'cmip7.root', null, [
                 getItem('Availability explorer', 'cmip7.catalogue'),
                 getItem('Projection products', 'cmip7.projections', null,
                     CMIP7_VARIABLE_MENU.map(({ group, items }) => getItem(
                         group,
                         `cmip7.group.${group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                         null,
                         items.map(([label, key]) => getItem(
                             label,
                             `cmip7.variable.${key}`,
                             null,
                             cmip7ScenarioMenuChildren(key, cmip7ProjectionCatalog),
                         )),
                     )),
                 ),
             ])] : []),
             getItem('Surface Temperature', '2', Thermometer, [
                 getItem('Regional/Local Mean', '2.1', null, [ 
                     getItem('Historical', '2.1.1.3'),
                 ]),
                 getItem('Hot Days', '2.2', null, [ getItem('Historical', '2.2.1.3'), ]),
                 getItem('Cold Nights', '2.3', null, [ getItem('Historical', '2.3.1.3'), ]),
                 getItem('Forecasts', '2.5', null, [
                     getItem(FORECAST_ENTRIES['cfs.airtmp'].label, 'cfs.airtmp'),
                     getItem(FORECAST_ENTRIES['gfs.airtmp'].label, 'gfs.airtmp'),
                     getItem('APCC MME', 'apcc.t2m', null, apccMenuChildren('t2m')),
                 ]),
             ]),
              getItem('Rainfall', '3', Droplets, [
                   getItem('Total Wet-day', '3.1', null, [ getItem('Historical', '3.1.1.3'), ]),
                   getItem('Consecutive Dry Days', '3.2', null, [ getItem('Historical', '3.2.1.3'), ]),
                   getItem('Heavy Rainfall', '3.3', null, [ getItem('Historical', '3.3.1.3'), ]),
                   getItem('Wet Days', '3.4', null, [ getItem('Historical', '3.4.1.3'), ]),
                   getItem('Dry Days', '3.5', null, [ getItem('Historical', '3.5.1.3'), ]),
                   getItem('ACCESS-S Climate Forecast', '3.7', null, [
                       getItem('Median', '3.7.1', null, [
                           getItem('Weekly', '3.7.1.4'),
                           getItem('Fortnightly', '3.7.1.3'),
                           getItem('Monthly', '3.7.1.2'),
                           getItem('Seasonal', '3.7.1.1')
                       ]),
                       getItem('Tertial', '3.7.2', null, [
                           getItem('Weekly', '3.7.2.4'),
                           getItem('Fortnightly', '3.7.2.3'),
                           getItem('Monthly', '3.7.2.2'),
                           getItem('Seasonal', '3.7.2.1')
                       ])
             ]),
                   getItem('NWP Forecasts', '3.9', null, [
                       getItem(FORECAST_ENTRIES['cfs.precip'].label, 'cfs.precip'),
                       getItem(FORECAST_ENTRIES['gfs.precip'].label, 'gfs.precip'),
                   ]),
                   getItem('Seasonal Forecasts', '3.apcc', null, [
                       getItem('APCC MME', 'apcc.prec', null, apccMenuChildren('prec')),
                   ]),
              ]),
             getItem('Wind', '5', Wind, [
                 getItem('Forecasts', '5.1', null, [
                     getItem(FORECAST_ENTRIES['cfs.wind'].label, 'cfs.wind'),
                     getItem(FORECAST_ENTRIES['gfs.wind'].label, 'gfs.wind'),
                 ]),
                 getItem('Tropical Cyclone', 'hurricane.tracker'),
             ]),
             getItem('Pressure', '11', Activity, [
                 getItem('Forecasts', '11.1', null, [
                     getItem(FORECAST_ENTRIES['cfs.pressure'].label, 'cfs.pressure'),
                     getItem(FORECAST_ENTRIES['gfs.pressure'].label, 'gfs.pressure'),
                 ]),
                 getItem('Seasonal Forecasts', '11.apcc', null, [
                     getItem('APCC MME', 'apcc.slp', null, apccMenuChildren('slp')),
                 ]),
             ]),
             getItem('Upper Atmosphere', '13', Activity, [
                 getItem('850 hPa Temperature', '13.1', null, [
                     getItem('APCC MME', 'apcc.t850', null, apccMenuChildren('t850')),
                 ]),
                 getItem('500 hPa Geopotential Height', '13.2', null, [
                     getItem('APCC MME', 'apcc.z500', null, apccMenuChildren('z500')),
                 ]),
             ]),
             getItem('Humidity', '12', Droplets, [
                 getItem('Forecasts', '12.1', null, [
                     getItem(FORECAST_ENTRIES['cfs.spfh'].label, 'cfs.spfh'),
                     getItem(FORECAST_ENTRIES['gfs.spfh'].label, 'gfs.spfh'),
                 ]),
             ]),
             getItem('Automated Weather Stations', '4', Monitor, [
                 getItem('Historical', '4.1')
             ]),
             getItem('PCC Probes', '2.1.2', Monitor),
             getItem('Ngardok Lake', '3.8.1', Droplets),
         ]),
    ], [cmip6Catalog, cmip7ProjectionCatalog]);


     // --- Menu click handler ---
     const handleMenuItemClick = useCallback((info) => {
        console.log('Menu Item Clicked:', info.key);
        // Call updateCMIPImmediate with the key and *current* slider value for that key's context.
        // We reset the slider to 0 here (earliest date) for most variables; updateCMIPImmediate
        // will set the correct date range. For Coral Reef Watch (current & weeks 1-12 outlooks)
        // default to the most recent date instead, since users expect to see the latest outlook.
        let initialDate = 0;
        if (typeof info.key === 'string' && info.key.startsWith('7.4.3')) {
            const isHistorical = info.key.startsWith('7.4.3.3');
            const crwDates = isHistorical ? historicCRWYears : currentCRWYears;
            initialDate = Math.max(0, (crwDates?.length || 1) - 1);
        } else if (info.key === '7.2.1') {
            initialDate = Math.max(0, (mhwMonthlyDates?.length || 1) - 1);
        } else if (info.key === '7.1.2.2') {
            const portalOisstDates = rmiPortal ? oisstRmiDates : oisstPalauDates;
            initialDate = Math.max(0, (portalOisstDates?.length || 1) - 1);
        } else if (['8.2.1', '8.2.2', '8.3.1'].includes(info.key)) {
            const cfg = getVariableConfig(info.key, 0, {
                oceanColorScope: rmiPortal ? 'rmi' : 'palau',
                oceanColorMode,
            });
            initialDate = Math.max(0, (cfg.dateData?.length || 1) - 1);
        } else if (info.key === '6.1.1.3') {
            const cfg = getVariableConfig(info.key, 0, {
                regionalSeaLevelScope,
                regionalSeaLevelMode,
            });
            initialDate = Math.max(0, (cfg.dateData?.length || 1) - 1);
        } else if (isForecastVariable(info.key)) {
            const parsed = parseForecastId(info.key);
            const datesMod = parsed?.model === 'gfs' ? gfsDates_sst : cfsDates_sst;
            // Prefer variable-specific dates from getVariableConfig
            const cfg = getVariableConfig(info.key, 0, { forecastVar: parsed?.varKey });
            const dates = cfg.dateData?.length ? cfg.dateData : datesMod;
            initialDate = Math.max(0, (dates?.length || 1) - 1);
        }
        updateCMIPImmediate(initialDate, info.key);
        // A variable's geographic scope is part of its identity.  This makes
        // the national Ocean Color products (and any future scoped product)
        // arrive at their intended map extent rather than inheriting the
        // previous variable's country view.
        const selectedConfig = getVariableConfig(info.key, initialDate, {
            oceanColorScope: rmiPortal ? 'rmi' : 'palau',
            oceanColorMode,
            oisstMode,
            oisstRegion,
            oisstIndoPacific,
            crwPacific,
            crwRes,
            crwScope,
            crwAlertMode,
            crwNativeAlert,
            waveViewScope,
            waveRenderMode,
            forecastViewScope,
            forecastMode,
            forecastVar: activeForecastVar,
            apccMode,
            apccViewScope: effectiveApccScope,
            apccAggregation,
            regionalSeaLevelScope,
            regionalSeaLevelMode,
            cmip6Catalog: activeProjectionCatalog,
            cmip6Scope: rmiIncludeWake && cmip6Scope === 'rmi' ? 'rmi-wake' : cmip6Scope,
            cmip6RenderMode,
            cmip6Model,
            portalScope: rmiPortal ? 'rmi' : 'palau',
        });
        const destination = rmiPortal && isPalauDefaultView(selectedConfig.view)
            ? RMI_PORTAL_VIEW
            : selectedConfig.view;
        const opensRightPanel = info.key !== 'gfs.wind' && hasRightPanelGraph(info.key) && isLargeScreen;
        if (destination) updateMapViewState({ ...destination, transitionDuration: 800 }, { rightDrawerVisible: opensRightPanel });
         if (opensRightPanel) {
             setRightDrawerOpen(true);
         }

         // Automatically close the left menu on mobile so the user can see the map changes
         if (!isLargeScreen && mobileOpen) {
             setMobileOpen(false);
         }
    }, [mobileOpen, updateCMIPImmediate, updateMapViewState, isLargeScreen, rmiPortal, waveViewScope, waveRenderMode, oceanColorMode, oisstMode, oisstRegion, oisstIndoPacific, crwPacific, crwRes, crwScope, crwAlertMode, crwNativeAlert, forecastViewScope, forecastMode, activeForecastVar, apccMode, effectiveApccScope, apccAggregation, regionalSeaLevelScope, regionalSeaLevelMode, activeProjectionCatalog, cmip6Scope, cmip6RenderMode, cmip6Model, rmiIncludeWake]); // Add mobileOpen and isLargeScreen to dependencies

    // This effect handles the automatic "pop out" positioning of the legend
    useEffect(() => {
        const menuWidth = 280;
        const safeX = mobileOpen && isLargeScreen ? menuWidth + 15 : 15;
        const safeY = (state.isSliderVisible && !timelineCollapsed) ? 140 : 30;
        
        const wasMobileOpen = prevMobileOpen.current;

        setLegendPos(currentPos => {
            const newPos = { ...currentPos };
            newPos.y = safeY; // Always adjust vertical position to avoid slider
            
            if (isLargeScreen) {
                // Menu just opened (or initial load where mobileOpen is true)
                if (mobileOpen && wasMobileOpen !== true) {
                    if (currentPos.x < safeX) {
                        newPos.pushedDistance = safeX - currentPos.x;
                        newPos.x = safeX;
                    } else {
                        newPos.pushedDistance = 0;
                    }
                } 
                // Menu just closed
                else if (!mobileOpen && wasMobileOpen === true) {
                    if (currentPos.pushedDistance > 0) {
                        newPos.x = Math.max(15, currentPos.x - currentPos.pushedDistance);
                        newPos.pushedDistance = 0; // Reset
                    }
                }
                // Handle case where legend is dragged under the open menu
                else if (mobileOpen && currentPos.x < safeX) {
                     newPos.x = safeX;
                }
            } else {
                // Force the legend back to the left edge on mobile devices
                newPos.x = 15;
                newPos.pushedDistance = 0;
            }
            return newPos;
        });
        
        prevMobileOpen.current = mobileOpen;
    }, [mobileOpen, isLargeScreen, state.isSliderVisible, timelineCollapsed]);

    // --- Drawer Content ---
    const drawerContent = useMemo(() => (
        <div className="flex h-full min-h-0 flex-col">
            {/* Glass Header Spacer */}
            <motion.div 
                initial={false}
                animate={{ height: headerHeight }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className={clsx("flex shrink-0 items-center border-b border-white/10 px-6 backdrop-blur-md", !isLargeScreen && "hidden")}
            />
            <nav className="mobile-navigation-scroll min-h-0 flex-1 overflow-y-auto px-3 py-4">
            <RenderMenuItems items={menuItems} onItemClick={handleMenuItemClick} openState={menuOpenState} setOpenState={setMenuOpenState} />
            </nav>
        </div>
    ), [menuItems, handleMenuItemClick, headerHeight, menuOpenState, isLargeScreen]); // Dependencies for memoization


    // ... inside function App() ...

    // --- PCC Probes Layer Configuration ---
    const pccLayer = new ScatterplotLayer({
        id: 'pcc-probes-layer',
        data: PCC_MAP_DATA,
        pickable: true,
        opacity: 0.8,
        stroked: true,
        filled: true,
        radiusScale: 6,
        radiusMinPixels: 6,
        radiusMaxPixels: 15,
        lineWidthMinPixels: 2,
        getPosition: d => d.position,
        getFillColor: d => d.id === pccStation ? [255, 222, 0] : [34, 211, 238],
        getLineColor: [255, 255, 255],
        onClick: (info) => {
            if (info.object) {
                setPccStation(info.object.id);
                dispatch({
                    type: 'SET_LOCATION',
                    payload: { lat: info.object.latitude, long: info.object.longitude, scatterplotVariable: 'pcc_probes' }
                });
                setRightDrawerOpen(true);
            }
        },
        updateTriggers: {
            getFillColor: [pccStation]
        }
    });

    const pccLabelLayer = new TextLayer({
        id: 'pcc-label-layer',
        data: PCC_MAP_DATA,
        getPosition: d => d.position,
        getText: d => d.name,
        getColor: [255, 255, 255, 255],
        getSize: 12,
        getPixelOffset: [0, -25],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        fontFamily: 'sans-serif',
        fontWeight: 'bold',
        background: true,
        getBackgroundColor: [15, 23, 42, 200],
        getBorderColor: [34, 211, 238, 255],
        getBorderWidth: 1,
        backgroundPadding: [6, 3],
        pickable: false,
    });

    // --- AWS Layer Configuration ---
    const awsLayer = new ScatterplotLayer({
        id: 'aws-layer',
        data: AWS_MAP_DATA,
        pickable: true,
        opacity: 0.8,
        stroked: true,
        filled: true,
        radiusScale: 6,
        radiusMinPixels: 6,
        radiusMaxPixels: 15,
        lineWidthMinPixels: 2,
        getPosition: d => d.position,
        getFillColor: d => d.id === awsStation ? [255, 222, 0] : [236, 72, 153], // Pink color to distinguish from PCC cyan
        getLineColor: [255, 255, 255],
        onClick: (info) => {
            if (info.object) {
                setAwsStation(info.object.id);
                dispatch({
                    type: 'SET_LOCATION',
                    payload: { lat: info.object.latitude, long: info.object.longitude, scatterplotVariable: 'aws' }
                });
                setRightDrawerOpen(true);
            }
        },
        updateTriggers: {
            getFillColor: [awsStation]
        }
    });

    const awsLabelLayer = new TextLayer({
        id: 'aws-label-layer',
        data: AWS_MAP_DATA,
        getPosition: d => d.position,
        getText: d => d.name,
        getColor: [255, 255, 255, 255],
        getSize: 12,
        getPixelOffset: [0, -25],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        fontFamily: 'sans-serif',
        fontWeight: 'bold',
        background: true,
        getBackgroundColor: [15, 23, 42, 200],
        getBorderColor: [236, 72, 153, 255],
        getBorderWidth: 1,
        backgroundPadding: [6, 3],
        pickable: false,
    });

    // --- Ngardok Lake Layer Configuration ---
    const ngardokMarkerLayer = new ScatterplotLayer({
        id: 'ngardok-marker',
        data: NGARDOK_DATA,
        pickable: true,
        opacity: 1,
        stroked: true,
        filled: true,
        radiusScale: 6,
        radiusMinPixels: 6,
        radiusMaxPixels: 15,
        lineWidthMinPixels: 2,
        getPosition: d => d.position,
        getFillColor: [34, 211, 238],
        getLineColor: [255, 255, 255],
        onClick: () => {
            setRightDrawerOpen(true);
        }
    });

    const ngardokLabelLayer = new TextLayer({
        id: 'ngardok-label',
        data: NGARDOK_DATA,
        getPosition: d => d.position,
        getText: d => d.name,
        getColor: [255, 255, 255, 255],
        getSize: 12,
        getPixelOffset: [0, -25],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        fontFamily: 'sans-serif',
        fontWeight: 'bold',
        background: true,
        getBackgroundColor: [15, 23, 42, 200],
        getBorderColor: [34, 211, 238, 255],
        getBorderWidth: 1,
        backgroundPadding: [6, 3],
        pickable: false,
    });

    // --- ACCESS-S Layer Configuration ---
    const accessSFeatures = useMemo(() => {
        if (!state.variableSelected?.startsWith('3.7') || !mapData || !mapData[0]) return [];
        
        const data = mapData[0];
        if (!data || !data.geometry_y) return [];
        
        const features = [];
        const keys = Object.keys(data.geometry_y);
        
        for (const key of keys) {
            const wkt = data.geometry_y[key];
            
            // Use `rain` value, or gracefully fallback to other values if testing probability/tertials
            let value = data.rain ? data.rain[key] : null;
            if (value === null || value === undefined) {
                const possibleKeys = Object.keys(data).filter(k => 
                    !['time', 'lat', 'lon', 'geometry_x', 'geometry_y', 'point', 'area', 'parea', 'forecast_period'].includes(k)
                );
                if (possibleKeys.length > 0) {
                    value = data[possibleKeys[0]][key];
                }
            }
            
            if (!wkt || typeof wkt !== 'string' || (!wkt.startsWith('POLYGON') && !wkt.startsWith('MULTIPOLYGON'))) continue;
            
            const isMulti = wkt.startsWith('MULTIPOLYGON');
            const finalCoords = [];
            
            // Parse WKT format like: POLYGON ((lon lat, lon lat...)) or MULTIPOLYGON (((...)), ((...)))
            const ringMatches = wkt.match(/\(([^()]+)\)/g);
            if (ringMatches) {
                 for (const rm of ringMatches) {
                      const coordsString = rm.replace(/^\(/, '').replace(/\)$/, '');
                      const points = coordsString.split(',').map(p => {
                          const [lon, lat] = p.trim().split(/\s+/).map(Number);
                          return [lon, lat];
                      });
                      if (isMulti) {
                          // In a MultiPolygon, each matched ring corresponds to a separate polygon
                          finalCoords.push([points]);
                      } else {
                          finalCoords.push(points);
                      }
                 }
            }
            
            features.push({
                type: 'Feature',
                geometry: {
                    type: isMulti ? 'MultiPolygon' : 'Polygon',
                    coordinates: finalCoords
                },
                properties: {
                    value: value,
                    rain: value
                }
            });
        }
        return features;
    }, [state.variableSelected, mapData]);

    const accessSLayer = useMemo(() => {
        if (!state.variableSelected?.startsWith('3.7') || accessSFeatures.length === 0) return null;

        const hexToRgb = (hex) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16), 200] : [0, 0, 0, 200];
        };

        const getColor = (d) => {
            const val = d.properties.value;
            if (val === null || val === undefined) return [0, 0, 0, 0];
            if (val <= 0) return [255, 255, 255, 0];
            if (val < 2) return hexToRgb('#e0f3db');
            if (val < 5) return hexToRgb('#ccece6');
            if (val < 10) return hexToRgb('#99d8c9');
            if (val < 15) return hexToRgb('#66c2a4');
            if (val < 20) return hexToRgb('#41ae76');
            if (val < 30) return hexToRgb('#238b45');
            if (val < 40) return hexToRgb('#006d2c');
            return hexToRgb('#00441b');
        };

        return new GeoJsonLayer({
            id: 'access-s-layer',
            data: accessSFeatures,
            pickable: true,
            stroked: true,
            getLineColor: [255, 255, 255, 80],
            lineWidthMinPixels: 1,
            filled: true,
            getFillColor: getColor,
            updateTriggers: {
                getFillColor: [state.variableSelected]
            }
        });
    }, [accessSFeatures, state.variableSelected]);

    const tutorialSeaLevelCellLayers = useMemo(() => {
        const tutorialAction = TUTORIAL_STEPS[tutorialStep - 1]?.action;
        if (tutorialAction !== 'regional-sea-level-virtual-station') return [];

        const halfCell = 0.125;
        const longitude = 134.625;
        const latitude = 7.125;
        return [new GeoJsonLayer({
            id: 'tutorial-regional-sea-level-virtual-station',
            data: {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    properties: { label: 'Selected virtual station' },
                    geometry: {
                        type: 'Polygon',
                        coordinates: [[
                            [longitude - halfCell, latitude - halfCell],
                            [longitude + halfCell, latitude - halfCell],
                            [longitude + halfCell, latitude + halfCell],
                            [longitude - halfCell, latitude + halfCell],
                            [longitude - halfCell, latitude - halfCell],
                        ]],
                    },
                }],
            },
            pickable: false,
            filled: true,
            stroked: true,
            getFillColor: [34, 211, 238, 100],
            getLineColor: [255, 255, 255, 255],
            getLineWidth: 3,
            lineWidthMinPixels: 3,
            lineWidthMaxPixels: 5,
        }), new ScatterplotLayer({
            id: 'tutorial-regional-sea-level-virtual-station-marker',
            data: [{ position: [longitude, latitude] }],
            pickable: false,
            stroked: true,
            filled: true,
            getPosition: item => item.position,
            getRadius: 13000,
            radiusMinPixels: 16,
            radiusMaxPixels: 24,
            getFillColor: [250, 204, 21, 230],
            getLineColor: [255, 255, 255, 255],
            lineWidthMinPixels: 2,
        })];
    }, [tutorialStep]);

    // --- Dynamic Boundary Layers Mapping ---
    const dynamicBoundaryLayers = useMemo(() => {
        return activeBoundaryLayers.map(layerId => {
            const config = BOUNDARY_LAYERS_CONFIG.find(c => c.id === layerId);
            if (!config) return null;

            if (config.layerType === 'rmi-atolls') {
                const coastlineData = '/CMIPtas/rmiLand.json';
                const zoom = Number(mapState.viewState?.zoom ?? RMI_PORTAL_VIEW.zoom);
                // Keep the country view legible, then progressively reveal the
                // complete gazetteer as the geographic spacing grows on zoom.
                const minimumLabelPriority = zoom < 4.6
                    ? 60
                    : (zoom < 5.3 ? 50 : (zoom < 6 ? 35 : 0));
                const visibleLabels = RMI_ATOLL_LABELS.filter(
                    label => label.priority >= minimumLabelPriority,
                );
                return [
                    new GeoJsonLayer({
                        id: 'boundary-rmi-atoll-coastline-casing',
                        data: coastlineData,
                        pickable: false,
                        stroked: true,
                        filled: false,
                        lineWidthUnits: 'pixels',
                        getLineWidth: 3.5,
                        lineWidthMinPixels: 2.5,
                        lineWidthMaxPixels: 4,
                        getLineColor: [0, 30, 77, 225],
                    }),
                    new GeoJsonLayer({
                        id: 'boundary-rmi-atoll-coastline',
                        data: coastlineData,
                        pickable: false,
                        stroked: true,
                        filled: false,
                        lineWidthUnits: 'pixels',
                        getLineWidth: 1.25,
                        lineWidthMinPixels: 1,
                        lineWidthMaxPixels: 2,
                        getLineColor: [255, 255, 255, 245],
                    }),
                    new TextLayer({
                        id: 'boundary-rmi-atoll-labels',
                        data: visibleLabels,
                        pickable: false,
                        getPosition: d => d.position,
                        getText: d => d.name,
                        getColor: [255, 255, 255, 255],
                        getSize: 12,
                        sizeUnits: 'pixels',
                        sizeMinPixels: 11,
                        sizeMaxPixels: 14,
                        fontWeight: 600,
                        fontFamily: 'sans-serif',
                        getTextAnchor: 'middle',
                        getAlignmentBaseline: 'center',
                        background: true,
                        getBackgroundColor: [0, 30, 77, 225],
                        getBorderColor: [221, 117, 0, 235],
                        getBorderWidth: 1,
                        backgroundPadding: [5, 3],
                        parameters: { depthTest: false },
                    }),
                ];
            }

            if (config.layerType === 'point-label') {
                const dataUrl = `/jsonFiles/boundary_layers/${config.file}`;
                const radiusMin = config.radiusMinPixels ?? 6;
                const radiusMax = config.radiusMaxPixels ?? 15;
                const labelOffset = config.labelOffset ?? -25;
                return [
                    new ScatterplotLayer({
                        id: `boundary-points-${config.id}`,
                        data: dataUrl,
                        dataTransform: d => d.features || d,
                        pickable: true,
                        opacity: 0.85,
                        stroked: true,
                        filled: true,
                        radiusScale: 6,
                        radiusMinPixels: radiusMin,
                        radiusMaxPixels: radiusMax,
                        lineWidthMinPixels: 2,
                        getPosition: d => d.geometry ? d.geometry.coordinates : [0, 0],
                        getFillColor: config.color,
                        getLineColor: [255, 255, 255]
                    }),
                    new TextLayer({
                        id: `boundary-labels-${config.id}`,
                        data: dataUrl,
                        dataTransform: d => d.features || d,
                        pickable: false,
                        getPosition: d => d.geometry ? d.geometry.coordinates : [0, 0],
                        getText: d => d.properties.Name || d.properties.NAME || d.properties.Site_Name || 'Spot',
                        getColor: [255, 255, 255, 255],
                        getSize: 12,
                        fontWeight: 'bold',
                        getAngle: 0,
                        getTextAnchor: 'middle',
                        getAlignmentBaseline: 'center',
                        getPixelOffset: [0, labelOffset],
                        fontFamily: 'sans-serif',
                        background: true,
                        getBackgroundColor: [15, 23, 42, 200],
                        getBorderColor: [...config.color, 255],
                        getBorderWidth: 1,
                        backgroundPadding: [6, 3],
                    })
                ];
            }

            const featureColor = feature => {
                const value = config.colorBy && feature?.properties?.[config.colorBy];
                return hexToColor(config.colorMap?.[value] || value, config.fillOpacity ?? 55)
                    || [...config.color, config.fillOpacity ?? 45];
            };
            return new GeoJsonLayer({
                id: `boundary-${config.id}`,
                data: boundaryLayerData[config.id] || { type: 'FeatureCollection', features: [] },
                pickable: true,
                stroked: true,
                filled: !(config.lineOnly || config.outlineOnly),
                lineWidthMinPixels: config.outlineOnly ? 4 : 1,
                lineWidthMaxPixels: config.outlineOnly ? 7 : 4,
                getLineColor: feature => {
                    const fill = featureColor(feature);
                    return [...fill.slice(0, 3), 255];
                },
                getFillColor: featureColor,
                onClick: info => {
                    if (!rmiPortal || state.variableSelected !== '7.1.2.2' || !config.oisstScheme || !info.object) return;
                    const section = OISST_BOUNDARY_SCHEMES[config.oisstScheme]
                        ?.sectionFromValue(info.object.properties?.[config.sectionProperty]);
                    if (section) {
                        // A boundary click is an explicit RMI OISST request;
                        // switch the data scope as well as the overlay state.
                        setOisstViewScope('rmi');
                        setOisstBoundaryScheme(config.oisstScheme);
                        setOisstBoundarySection(section);
                    }
                },
                getLineWidth: config.outlineOnly ? 3 : 2,
                autoHighlight: !config.outlineOnly,
                highlightColor: [255, 255, 255, 100]
            });
        }).flat().filter(Boolean);
    }, [activeBoundaryLayers, boundaryLayerData, mapState.viewState?.zoom, rmiPortal, state.variableSelected]);

    // --- Hurricane tracker deck.gl layers (cone + model tracks + intensity points) ---
    const getSshwsCatNum = (sshws) => {
        if (!sshws) return null;
        if (sshws === 'TD') return 0;
        if (sshws === 'TS') return 0; // tropical storm
        const m = String(sshws).match(/^Cat([1-5])$/);
        if (m) return parseInt(m[1], 10);
        return null;
    };
    const hurricaneLayers = useMemo(() => {
        if (!isHurricaneSelected || !activeHurricaneStorm) return [];
        const sid = activeHurricaneStorm.id;
        const base = `${hurricaneBasePath}/${sid}`;
        const layers = [];

        const selectedModel = hurricaneSelectedModel;

        // Cone for selected model (cone_{model}.geojson is generated by the script;
        // we fall back to cone.geojson to keep things robust during rollout).
        const coneUrl = `${base}/cone_${selectedModel}.geojson`;
        const coneFallbackUrl = `${base}/cone.geojson`;
        const coneDataUrl = selectedModel === 'ofcl' ? coneFallbackUrl : coneUrl;

        // Cone outline (dotted/dashed look via PathStyleExtension if supported)
        if (selectedModel) {
            layers.push(new GeoJsonLayer({
                id: `hurricane-cone-${sid}`,
                data: coneDataUrl,
                pickable: true,
                stroked: true,
                filled: true,
                getFillColor: [125, 211, 252, 28],
                getLineColor: [186, 230, 253, 170],
                getLineWidth: 2,
                lineWidthMinPixels: 1,
                extensions: [new PathStyleExtension({ dash: true, highPrecisionDash: true })],
                dash: true,
                getDashArray: [4, 4],
                parameters: { depthTest: false },
            }));
        }

        // The OFCL asset is an observed, chronological best track.  Keep it
        // separate from projected guidance so the two paths never join.
        layers.push(new GeoJsonLayer({
            id: `hurricane-history-${sid}`,
            data: `${base}/track_history.geojson`,
            pickable: true,
            stroked: true,
            filled: false,
            getLineColor: [15, 23, 42, 220],
            getLineWidth: 3,
            lineWidthMinPixels: 2,
            lineJointRounded: true,
            lineCapRounded: true,
            parameters: { depthTest: false },
        }));

        // Historical intensity badges are always tied to the selected storm,
        // not to the active forecast model.
        layers.push(new IconLayer({
            id: `hurricane-history-points-${sid}`,
            data: `${base}/points_history.geojson`,
            dataTransform: d => {
                const features = d.features || d;
                return isLargeScreen ? features : features.filter((_, index) => index === 0 || index % 3 === 0);
            },
            pickable: true,
            billboard: true,
            getPosition: d => d.geometry?.coordinates || [0, 0],
            getIcon: d => {
                const iconCat = Math.max(0, Math.min(5, getSshwsCatNum(d?.properties?.sshws) ?? 0));
                return { url: `/jsonFiles/hurricanes/hcat${iconCat}.svg`, width: 128, height: 128, anchorX: 64, anchorY: 64 };
            },
            sizeUnits: 'pixels', getSize: 30, alphaCutoff: 0.05,
            parameters: { depthTest: false },
        }));

        if (selectedModel && hurricaneModels[selectedModel]) {
            const model = selectedModel;
            const meta = hurricaneModels[model];
            layers.push(new GeoJsonLayer({
                id: `hurricane-forecast-${sid}-${model}`,
                data: `${base}/track_${model}.geojson`,
                pickable: false,
                stroked: true,
                filled: false,
                getLineColor: [...meta.color, 230],
                getLineWidth: 3,
                lineWidthMinPixels: 2,
                lineJointRounded: true,
                lineCapRounded: true,
                extensions: [new PathStyleExtension({ dash: true, highPrecisionDash: true })],
                dash: true,
                getDashArray: [3, 5],
                parameters: { depthTest: false },
            }));

            const pointsUrl = `${base}/points_${model}.geojson`;

            // Point icons use category-labelled SVGs from public/jsonFiles/hurricanes/.
            layers.push(new IconLayer({
                id: `hurricane-pts-icon-${sid}-${model}`,
                data: pointsUrl,
                dataTransform: d => {
                    const features = d.features || d;
                    // Phone views keep forecast milestones legible without
                    // turning the projected path into a row of badges.
                    return isLargeScreen ? features : features.filter((_, index, all) => (
                        index === 0 || index === all.length - 1 || index % 3 === 0
                    ));
                },
                pickable: true,
                billboard: true,
                getPosition: d => d.geometry?.coordinates || [0, 0],
                getIcon: d => {
                    const cat = getSshwsCatNum(d?.properties?.sshws);
                    const iconCat = cat != null ? Math.max(0, Math.min(5, cat)) : 0;
                    return {
                        url: `/jsonFiles/hurricanes/hcat${iconCat}.svg`,
                        width: 128,
                        height: 128,
                        anchorX: 64,
                        anchorY: 64,
                    };
                },
                sizeUnits: 'pixels',
                getSize: 34,
                getAngle: 0,
                alphaCutoff: 0.05,
                parameters: { depthTest: false },
            }));
        }

        return layers;
    }, [isHurricaneSelected, activeHurricaneStorm, hurricaneSelectedModel, isLargeScreen]);

    // Kept independent from hurricaneLayers so spinning the eye never rebuilds
    // cone, path, or node layers. SVGs retain the category number at the core.
    const hurricaneLiveLayers = useMemo(() => {
        if (!isHurricaneSelected || !activeHurricaneStorm) return [];
        const sid = activeHurricaneStorm.id;
        const base = `${hurricaneBasePath}/${sid}`;
        const liveFix = hurricaneTimelineStatus;
        const iconCat = Math.max(0, Math.min(5, getSshwsCatNum(liveFix?.properties?.sshws || activeHurricaneStorm.sshws) ?? 0));
        const pulse = 38;
        const centerData = liveFix ? [liveFix] : `${base}/center.geojson`;
        return [
            new ScatterplotLayer({
                id: `hurricane-live-glow-${sid}`,
                data: centerData,
                dataTransform: d => d.features || d,
                getPosition: d => d.geometry?.coordinates || [0, 0],
                getRadius: pulse,
                radiusUnits: 'pixels',
                getFillColor: [...activeHurricaneStorm.color, hurricaneFocusMode ? 34 : 52],
                stroked: false,
                pickable: false,
                parameters: { depthTest: false },
            }),
            new IconLayer({
                id: `hurricane-center-icon-${sid}`,
                data: centerData,
                dataTransform: d => d.features || d,
                pickable: true,
                billboard: true,
                getPosition: d => d.geometry?.coordinates || [0, 0],
                getIcon: () => ({
                    url: `/jsonFiles/hurricanes/hcat${iconCat}.svg`,
                    width: 128, height: 128, anchorX: 64, anchorY: 64,
                }),
                sizeUnits: 'pixels',
                getSize: 46,
                getAngle: hurricaneSpinAngle,
                alphaCutoff: 0.05,
                parameters: { depthTest: false },
            }),
        ];
    }, [isHurricaneSelected, activeHurricaneStorm, hurricaneSpinAngle, hurricaneTimelineStatus, hurricaneFocusMode]);

    // Fly to selected hurricane when entering the tracker / changing storm
    useEffect(() => {
        if (!isHurricaneSelected || !activeHurricaneStorm) return;
        if (activeHurricaneStorm.lat == null || activeHurricaneStorm.lon == null) return;
        updateMapViewState({
            latitude: activeHurricaneStorm.lat,
            longitude: activeHurricaneStorm.lon,
            zoom: 4,
            bearing: 0,
            pitch: 0,
            transitionDuration: 800,
        });
        setHurricaneStormId(activeHurricaneStorm.id);
    }, [isHurricaneSelected, activeHurricaneStorm, updateMapViewState]);


    // --- Slider Marks generation ---
    // Keep time navigation legible for every variable. Dense daily/monthly
    // datasets use a small, evenly distributed set of labels; the selected
    // timestamp remains exact in the value label and adjacent time readout.
    const markerSelected = state.markerSelected;
    const sliderTooltip = state.sliderTooltip;
    const timelineMarkLimit = isLargeScreen ? 8 : 4;
    const sliderMarks = useMemo(() => {
        return compactTimelineMarks(markerSelected, state.dateSelected, timelineMarkLimit);
    }, [markerSelected, state.dateSelected, timelineMarkLimit]);

    // --- Slider Tooltip Formatter ---
    const getSliderTooltip = useCallback((value) => {
        const fallback = state.dateSelected?.[value] ?? value;
        if (!sliderTooltip) return String(fallback);

        let result;
        if (typeof sliderTooltip === 'function') {
            // Handle function (some expect object {value}, based on variableConfig usage)
            result = sliderTooltip({ value });
        } else if (typeof sliderTooltip === 'object' && typeof sliderTooltip.formatter === 'function') {
            // Handle object with formatter (from vars.js usage)
            result = sliderTooltip.formatter(value);
        }
        
        return (typeof result === 'string' || typeof result === 'number') ? result : String(fallback);
    }, [sliderTooltip, state.dateSelected]);

    // --- JSX Structure ---
    return (
        <HelmetProvider>
        <Helmet>
            <title>{pageTitle}</title>
            <meta name="title" content={pageTitle} />
            <meta name="description" content={`Currently viewing ${pageTitle.split(' | ')[0]} on the Palau Ocean Portal interactive map.`} />

            {/* Open Graph / Facebook */}
            <meta property="og:site_name" content="Palau Ocean Portal" />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://palau.ocean-portal.org/'} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={`Currently viewing ${pageTitle.split(' | ')[0]} on the Palau Ocean Portal interactive map.`} />
            <meta property="og:image" content={typeof window !== 'undefined' ? `${window.location.origin}/social-preview.png` : 'https://palau.ocean-portal.org/social-preview.png'} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta itemProp="image" content={typeof window !== 'undefined' ? `${window.location.origin}/social-preview.png` : 'https://palau.ocean-portal.org/social-preview.png'} />
            <meta name="thumbnail" content={typeof window !== 'undefined' ? `${window.location.origin}/social-preview.png` : 'https://palau.ocean-portal.org/social-preview.png'} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={typeof window !== 'undefined' ? window.location.href : 'https://palau.ocean-portal.org/'} />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={`Currently viewing ${pageTitle.split(' | ')[0]} on the Palau Ocean Portal interactive map.`} />
            <meta name="twitter:image" content={typeof window !== 'undefined' ? `${window.location.origin}/social-preview.png` : 'https://palau.ocean-portal.org/social-preview.png'} />

            {/* JSON-LD Structured Data for Google Knowledge Graph */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebApplication",
                    "name": "Palau Ocean Portal",
                    "url": "https://palau.ocean-portal.org/",
                    "description": "Interactive marine and climate data explorer for the Republic of Palau. Monitor sea surface temperatures, coral bleaching alerts, and extreme weather events.",
                    "applicationCategory": "ScienceApplication",
                    "operatingSystem": "Any",
                    "image": "https://palau.ocean-portal.org/social-preview.png",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "USD"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Palau Ocean Portal",
                        "url": "https://palau.ocean-portal.org/",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://palau.ocean-portal.org/palau-flag-512.png"
                        }
                    }
                })}
            </script>
        </Helmet>
        <MotionConfig reducedMotion="user">
        <ThemeProvider theme={muiTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'transparent' }}> {/* Prevent body scroll */}
                {/* --- App Bar (Refactored to Glassmorphism) --- */}
                <header
                    ref={headerRef}
                    className={clsx(
                        "app-header-shell fixed top-0 left-0 z-[60] grid w-screen max-w-[100vw] grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-4 py-1 border-b",
                        "material-nav"
                    )}
                >
                    {/* Left: Menu & Title */}
                    <div className="portal-brand col-start-1 row-start-1 flex min-w-0 items-center gap-3 shrink-0">
                        <IconButton
                            id="tutorial-menu-btn"
                            onClick={handleDrawerToggle}
                            className={clsx(
                                "transition-colors duration-200",
                                "text-blue-50 hover:bg-white/10 hover:text-white"
                            )}
                        >
                            <MenuIconLucide className="h-6 w-6" />
                        </IconButton>
                        
                        {/* Country flag mark: a flat, durable identifier rather than a
                            decorative glass object. */}
                        <div className="flex items-center gap-3 select-none cursor-default">
                            {/* Logo Container */}
                            <div className="relative flex h-10 w-16 overflow-hidden rounded-lg border border-white/20 bg-[#4AADD6]">
                                {rmiPortal ? (
                                    <img className="h-full w-full object-cover" src="/rmi-flag.svg" alt="Flag of the Republic of the Marshall Islands" />
                                ) : (
                                    <div className="absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FFDE00]" style={{ left: '43.75%' }} />
                                )}
                            </div>

                            {/* Typography */}
                            <div className="portal-wordmark hidden sm:flex flex-col justify-center">
                                <div className="flex flex-col items-start">
                                    <span className="text-2xl font-extrabold uppercase leading-none tracking-wider text-white">
                                        {rmiPortal ? 'Marshall Islands' : 'Palau'}
                                    </span>
                                    <span className="pl-0.5 text-[0.6rem] font-light uppercase tracking-[0.35em] text-cyan-100/85">
                                        Ocean Portal
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Center: CRWMenuBar */}
                    {state.crwEnabled && (
                        <Box id="tutorial-crw-dials" className="col-span-2 col-start-1 row-start-2 min-w-0 pb-1 md:col-span-1 md:col-start-2 md:row-start-1 md:pb-0" sx={{ minWidth: 0, display: 'flex', justifyContent: 'center', px: 2, color: 'white' }}>
                            <Suspense fallback={<Box sx={{ width: 100, height: 64 }} />}>
                                <CRWMenuBar outlook={state.crwArray} menuBarOnClick={menuBarOnClick} currentDial={state.currentDial} enabled={state.crwEnabled} pacific={crwPacific} resolution={crwRes} onPacificToggle={(next) => { setCrwPacific(next); if (next && crwRes === 'native') setCrwRes('1deg'); }} onResChange={setCrwRes} scope={crwScope} onScopeChange={handleCrwScopeChange} allowRmi={rmiPortal} alertMode={crwAlertMode} onAlertModeChange={setCrwAlertMode} nativeAlert={crwNativeAlert} onNativeAlertChange={setCrwNativeAlert} />
                            </Suspense>
                        </Box>
                    )}
                    {/* Daily CRW products have their own scope/resolution control.
                        Alert tiles reuse the same chart renderer after a click,
                        but retain the alert toolbar; never mount both controls. */}
                    {state.scatterplotVariable === 'crw_daily' && !state.crwEnabled && (
                        <Box id="tutorial-crw-daily-controls" className="col-span-2 col-start-1 row-start-2 min-w-0 pb-1 md:col-span-1 md:col-start-2 md:row-start-1 md:pb-0" sx={{ minWidth: 0, display: 'flex', justifyContent: 'center', px: 2, color: 'white' }}>
                            <Suspense fallback={null}>
                                <CRWDailyScopeControl scope={crwScope} resolution={crwRes} onScopeChange={handleCrwScopeChange} onResolutionChange={setCrwRes} allowRmi={rmiPortal} />
                            </Suspense>
                        </Box>
                    )}

                    {/* Right: navigation/context leads; occasional map utilities recede. */}
                    <div className="header-actions material-control col-start-2 row-start-1 flex shrink-0 items-center gap-1.5 rounded-[var(--atlas-radius-panel)] border p-1.5 md:col-start-3">
                        <motion.button
                            id="tutorial-home-btn"
                            type="button"
                            onClick={() => setLandingOpen(true)}
                            title="Home / Info"
                            aria-label="Return to Home and Information"
                            whileTap={{ scale: 0.95 }}
                            className="header-navigation-action flex h-10 w-10 items-center justify-center gap-2 rounded-lg xl:w-auto xl:px-3"
                        >
                            <Home className="h-5 w-5 stroke-[1.5]" />
                            <span className="hidden text-xs font-medium xl:inline">Home</span>
                        </motion.button>

                        <motion.button
                            id="tutorial-basemap-btn"
                            type="button"
                            onClick={toggleBasemap}
                            title={basemap === 'terrain' ? "Switch to Satellite" : "Switch to Terrain"}
                            aria-label="Toggle Basemap View"
                            whileTap={{ scale: 0.95 }}
                            className="header-navigation-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                        >
                            {basemap === 'terrain' ? <Globe className="h-5 w-5 stroke-[1.5]" /> : <MapIcon className="h-5 w-5 stroke-[1.5]" />}
                            <span className="hidden text-xs font-medium sm:inline-block">
                                {basemap === 'terrain' ? 'Terrain' : 'Satellite'}
                            </span>
                        </motion.button>

                        {rmiPortal && (
                            <motion.button
                                type="button"
                                onClick={() => setRmiIncludeWake(enabled => !enabled)}
                                title={rmiIncludeWake
                                    ? 'Wake Island boundary included in the RMI analysis view. Select to return to the official RMI EEZ outline.'
                                    : 'Add the optional Wake Island U.S. EEZ boundary to the RMI analysis view.'}
                                aria-label={rmiIncludeWake
                                    ? 'Wake Island boundary included. Return to the official Marshall Islands EEZ outline.'
                                    : 'Add Wake Island boundary to the Marshall Islands analysis view.'}
                                aria-pressed={rmiIncludeWake}
                                whileTap={{ scale: 0.95 }}
                                className={clsx(
                                    'header-primary-action flex h-10 items-center justify-center gap-2 rounded-lg px-2.5 text-xs font-medium sm:px-3',
                                    rmiIncludeWake && 'bg-amber-300/15 text-amber-100 ring-1 ring-amber-200/45'
                                )}
                            >
                                <MapIcon className="h-5 w-5 shrink-0 stroke-[1.5]" />
                                <span className="hidden min-[420px]:inline">{rmiIncludeWake ? 'Wake on' : 'Add Wake'}</span>
                            </motion.button>
                        )}

                        {/* --- OISST Controls --- */}
                        {state.variableSelected === '7.1.2.2' && (
                            <>
                                <motion.button
                                    type="button"
                                    onClick={() => setOisstMode(prev => prev === 'contour' ? 'gridded' : 'contour')}
                                    title={oisstMode === 'contour' ? "Switch to Gridded cells" : "Switch to Contour fill"}
                                    aria-label="Toggle OISST map style"
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    {oisstMode === 'contour' ? <Waves className="h-5 w-5 stroke-[1.5]" /> : <Activity className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">
                                        {oisstMode === 'contour' ? 'Contour' : 'Gridded'}
                                    </span>
                                </motion.button>
                                <motion.button
                                    id="tutorial-oisst-scope-btn"
                                    type="button"
                                    onClick={cycleOisstViewScope}
                                    title={rmiPortal ? 'Cycle map extent: Marshall Islands → Indo-Pacific → Palau' : 'Switch map extent: Palau ↔ Indo-Pacific'}
                                    aria-label="Cycle OISST map extent"
                                    whileTap={{ scale: 0.95 }}
                                    className={clsx(
                                        "header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3",
                                        oisstIndoPacific && "bg-cyan-400/20"
                                    )}
                                >
                                    {oisstIndoPacific ? <Globe className="h-5 w-5 stroke-[1.5]" /> : <MapIcon className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">
                                        {oisstViewScopeLabel}
                                    </span>
                                </motion.button>
                                {oisstViewScope === 'rmi' && <select
                                    value={oisstBoundaryScheme}
                                    onChange={event => {
                                        // Choosing a Marshall Islands method must also select
                                        // its data extent, otherwise the continuum would retain
                                        // the Palau/RMI-EEZ fallback series.
                                        setOisstViewScope('rmi');
                                        setOisstBoundaryScheme(event.target.value);
                                        setOisstBoundarySection('North');
                                    }}
                                    title="OISST regional average boundary"
                                    className="hidden h-10 max-w-[190px] rounded-lg border border-white/15 bg-slate-900 px-3 text-xs text-cyan-100 outline-none lg:block"
                                >
                                    {Object.entries(OISST_BOUNDARY_SCHEMES).map(([key, scheme]) => <option key={key} value={key}>{scheme.label}</option>)}
                                </select>}
                                {oisstViewScope === 'rmi' && activeOisstBoundary && (
                                    <select
                                        value={oisstBoundarySection}
                                        onChange={event => setOisstBoundarySection(event.target.value)}
                                        title="OISST boundary section"
                                        className="hidden h-10 rounded-lg border border-white/15 bg-slate-900 px-3 text-xs text-cyan-100 outline-none lg:block"
                                    >
                                        {['North', 'Central', 'South'].map(section => <option key={section} value={section}>{section}</option>)}
                                    </select>
                                )}
                            </>
                        )}

                        {/* --- CFS / GFS Forecast Controls --- */}
                        {isForecastSelected && (
                            <>
                                <motion.button
                                    type="button"
                                    onClick={() => setForecastMode(prev => prev === 'contour' ? 'gridded' : 'contour')}
                                    title={forecastMode === 'contour' ? "Switch to Gridded cells" : "Switch to Contour fill"}
                                    aria-label="Toggle forecast map style"
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    {forecastMode === 'contour' ? <Waves className="h-5 w-5 stroke-[1.5]" /> : <Activity className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">
                                        {forecastMode === 'contour' ? 'Contour' : 'Gridded'}
                                    </span>
                                </motion.button>
                                <motion.button
                                    type="button"
                                    onClick={cycleForecastViewScope}
                                    title={rmiPortal ? 'Cycle map extent: Marshall Islands → Indo-Pacific → Palau' : 'Switch map extent: Palau ↔ Indo-Pacific'}
                                    aria-label="Cycle forecast map extent"
                                    whileTap={{ scale: 0.95 }}
                                    className={clsx(
                                        "header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3",
                                        forecastIndoPacific && "bg-cyan-400/20"
                                    )}
                                >
                                    {forecastIndoPacific ? <Globe className="h-5 w-5 stroke-[1.5]" /> : <MapIcon className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">
                                        {forecastViewScopeLabel}
                                    </span>
                                </motion.button>
                                {isForecastWindSelected && forecastViewScope === 'rmi' && (
                                    <select
                                        value={oisstBoundaryScheme}
                                        onChange={event => setOisstBoundaryScheme(event.target.value)}
                                        title="Marshall Islands wind-average zonation"
                                        aria-label="Marshall Islands wind-average zonation"
                                        className="hidden h-10 max-w-[220px] rounded-lg border border-white/15 bg-slate-900 px-3 text-xs text-cyan-100 outline-none lg:block"
                                    >
                                        {Object.entries(OISST_BOUNDARY_SCHEMES).map(([key, scheme]) => (
                                            <option key={key} value={key}>{scheme.label}</option>
                                        ))}
                                    </select>
                                )}
                            </>
                        )}

                        {['8.2.1', '8.2.2', '8.3.1'].includes(state.variableSelected) && (
                            <motion.button
                                type="button"
                                onClick={() => setOceanColorMode(previous => previous === 'contour' ? 'gridded' : 'contour')}
                                title="Switch between inspectable monthly grid cells and a lower-cost contour mosaic"
                                aria-label={`Ocean Color display: ${oceanColorMode}. Switch display.`}
                                whileTap={{ scale: 0.95 }}
                                className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                            >
                                {oceanColorMode === 'contour' ? <Waves className="h-5 w-5 stroke-[1.5]" /> : <Activity className="h-5 w-5 stroke-[1.5]" />}
                                <span className="hidden text-xs font-medium sm:inline-block">{oceanColorMode === 'contour' ? 'Contours' : 'Grid'}</span>
                            </motion.button>
                        )}

                        {state.variableSelected === '6.1.1.3' && (
                            <>
                                <motion.button
                                    type="button"
                                    onClick={cycleRegionalSeaLevelScope}
                                    title={rmiPortal ? 'Cycle historical sea-level extent: Marshall Islands → Indo-Pacific → Palau' : 'Switch historical sea-level extent: Palau ↔ Indo-Pacific'}
                                    aria-label={`Historical sea-level extent: ${regionalSeaLevelScopeLabel}. Switch extent.`}
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    {regionalSeaLevelScope === 'indo' ? <Globe className="h-5 w-5 stroke-[1.5]" /> : <MapIcon className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">{regionalSeaLevelScopeLabel}</span>
                                </motion.button>
                                <motion.button
                                    type="button"
                                    onClick={() => setRegionalSeaLevelMode(previous => previous === 'contour' ? 'gridded' : 'contour')}
                                    title="Switch between inspectable monthly cells and colour-band contours"
                                    aria-label={`Historical sea-level display: ${regionalSeaLevelMode}. Switch display.`}
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    {regionalSeaLevelMode === 'contour' ? <Waves className="h-5 w-5 stroke-[1.5]" /> : <Activity className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">{regionalSeaLevelMode === 'contour' ? 'Contours' : 'Grid'}</span>
                                </motion.button>
                            </>
                        )}

                        {isCmipProjectionSelection(state.variableSelected) && (
                            <>
                                <select
                                    value={activeCmip6Scenario}
                                    onChange={event => updateCMIPImmediate(0, `${isCmip7PreviewSelection(state.variableSelected) ? 'cmip7' : 'cmip6'}.${activeCmip6Variable}.${event.target.value}`)}
                                    disabled={!activeCmip6Scenarios.length}
                                    title={`${isCmip7PreviewSelection(state.variableSelected) ? 'CMIP7' : 'CMIP6'} experiment and scenario`}
                                    aria-label={`${isCmip7PreviewSelection(state.variableSelected) ? 'CMIP7' : 'CMIP6'} experiment and scenario`}
                                    className="header-primary-action h-10 max-w-[175px] rounded-lg px-2 text-xs"
                                >
                                    {activeCmip6Scenarios.map((scenario) => (
                                        <option key={scenario} value={scenario}>
                                            {activeProjectionScenarioLabels[scenario] || scenario}
                                        </option>
                                    ))}
                                </select>
                                <motion.button
                                    type="button"
                                    onClick={() => setCmip6Scope(previous => {
                                        if (!rmiPortal) return previous === 'indo' ? 'palau' : 'indo';
                                        return previous === 'palau' ? 'rmi' : (previous === 'rmi' ? 'indo' : 'palau');
                                    })}
                                    title={rmiPortal ? 'Cycle projection preview extent: Marshall Islands → Indo-Pacific → Palau' : 'Switch projection preview extent: Palau ↔ Indo-Pacific'}
                                    aria-label={`Projection preview map extent: ${cmip6Scope}. Switch extent.`}
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    {cmip6Scope === 'indo' ? <Globe className="h-5 w-5 stroke-[1.5]" /> : <MapIcon className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">{cmip6Scope === 'rmi' ? 'Marshall Islands' : (cmip6Scope === 'indo' ? 'Indo-Pacific' : 'Palau')}</span>
                                </motion.button>
                                {!activeCmip6Manifest?.products?.length && (
                                    <span
                                        role="status"
                                        aria-live="polite"
                                        className="hidden rounded-md border border-amber-300/30 bg-amber-200/10 px-2.5 py-1.5 text-[11px] font-medium text-amber-100 sm:inline-flex"
                                    >
                                        Data pending publication
                                    </span>
                                )}
                                <select
                                    value={cmip6RenderMode}
                                    onChange={event => setCmip6RenderMode(event.target.value)}
                                    disabled={!activeCmip6Manifest?.products?.length}
                                    title="Projection rendering mode"
                                    aria-label="Projection rendering mode"
                                    className="header-primary-action h-10 max-w-[130px] rounded-lg px-2 text-xs"
                                >
                                    <option value="native" disabled={activeCmip6Modes.length > 0 && !activeCmip6Modes.includes('native')}>Native cells</option>
                                    <option value="eez-grid" disabled={activeCmip6Modes.length > 0 && !activeCmip6Modes.includes('eez-grid')}>EEZ grid</option>
                                    <option value="standard-grid" disabled={activeCmip6Modes.length > 0 && !activeCmip6Modes.includes('standard-grid')}>1° grid</option>
                                    <option value="contour" disabled={activeCmip6Modes.length > 0 && !activeCmip6Modes.includes('contour')}>Contours</option>
                                </select>
                                <select
                                    value={cmip6Model}
                                    onChange={event => setCmip6Model(event.target.value)}
                                    disabled={!activeCmip6Manifest?.products?.length}
                                    title={`${isCmip7PreviewSelection(state.variableSelected) ? 'CMIP7' : 'CMIP6'} source model`}
                                    aria-label={`${isCmip7PreviewSelection(state.variableSelected) ? 'CMIP7' : 'CMIP6'} source model`}
                                    className="header-primary-action hidden h-10 max-w-[175px] rounded-lg px-2 text-xs lg:block"
                                >
                                    <option value="">Available model</option>
                                    {[...new Map((activeCmip6Manifest?.products || [])
                                        .filter(product => product.scope === (rmiIncludeWake && cmip6Scope === 'rmi' ? 'rmi-wake' : cmip6Scope))
                                        .map(product => {
                                            const source = product.source || {};
                                            const key = `${source.source_id}:${source.variant_label}:${source.grid_label}`;
                                            return [key, source];
                                        })).entries()].map(([key, source]) => (
                                            <option key={key} value={key}>{source.source_id} · {source.variant_label}</option>
                                        ))}
                                </select>
                            </>
                        )}

                        {['6.3.2.1', '6.3.2.2', '6.3.2.3'].includes(state.variableSelected) && (
                            <>
                                {rmiPortal && <motion.button
                                    type="button"
                                    onClick={() => setWaveViewScope(previous => previous === 'palau' ? 'rmi' : 'palau')}
                                    title="Switch historical wave hindcast between Palau and Marshall Islands"
                                    aria-label={`Wave hindcast extent: ${waveViewScopeLabel}. Switch extent.`}
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    <MapIcon className="h-5 w-5 stroke-[1.5]" />
                                    <span className="hidden text-xs font-medium sm:inline-block">{waveViewScopeLabel}</span>
                                </motion.button>}
                                <motion.button
                                    type="button"
                                    onClick={() => setWaveRenderMode(previous => previous === 'contour' ? 'gridded' : 'contour')}
                                    title="Switch between solid wave-height contours and gridded cells"
                                    aria-label={`Wave height display: ${waveRenderMode}. Switch display.`}
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    {waveRenderMode === 'contour' ? <Waves className="h-5 w-5 stroke-[1.5]" /> : <Activity className="h-5 w-5 stroke-[1.5]" />}
                                    <span className="hidden text-xs font-medium sm:inline-block">{waveRenderMode === 'contour' ? 'Contours' : 'Grid'}</span>
                                </motion.button>
                                <motion.button
                                    type="button"
                                    onClick={() => setWaveVectorMode(previous => previous === 'animated' ? 'arrows' : 'animated')}
                                    title="Switch between animated wave flow and static wave vectors"
                                    aria-label={`Wave direction display: ${waveVectorMode}. Switch display.`}
                                    whileTap={{ scale: 0.95 }}
                                    className="header-primary-action flex h-10 w-10 items-center justify-center rounded-lg sm:w-auto sm:gap-2 sm:px-3"
                                >
                                    <Wind className="h-5 w-5 stroke-[1.5]" />
                                    <span className="hidden text-xs font-medium sm:inline-block">{waveVectorMode === 'animated' ? 'Animated' : 'Static vectors'}</span>
                                </motion.button>
                            </>
                        )}

                        {/* --- APCC MME controls: aggregation is a view toggle,
                            while lead/method remain explicit menu variables. --- */}
                        {isApccSelected && (
                            <>
                                <div className="header-primary-action flex h-10 items-center rounded-lg px-2">
                                    <Activity className="mr-1.5 h-4 w-4 text-cyan-300" aria-hidden="true" />
                                    <select
                                        value={apccMode}
                                        onChange={event => setApccMode(event.target.value)}
                                        aria-label="APCC map display"
                                        title="APCC map display"
                                        className="max-w-[112px] bg-transparent text-xs font-medium text-cyan-100 outline-none [&>option]:bg-slate-900"
                                    >
                                        <option value="gridded">Gridded</option>
                                        <option value="voronoi">Voronoi</option>
                                        <option value="contour">Contour</option>
                                    </select>
                                </div>
                                <motion.button
                                    type="button"
                                    onClick={cycleApccViewScope}
                                    disabled={apccScopeLocked}
                                    title={apccScopeLocked
                                        ? 'This atmospheric field is available in the Indo-Pacific view only'
                                        : (rmiPortal ? 'Cycle APCC extent: Marshall Islands → Indo-Pacific → Palau' : 'Switch APCC extent: Palau ↔ Indo-Pacific')}
                                    aria-label="Cycle APCC map extent"
                                    whileTap={apccScopeLocked ? undefined : { scale: 0.95 }}
                                    className="header-primary-action flex h-10 items-center justify-center gap-2 rounded-lg px-3 text-xs font-medium text-cyan-100 disabled:cursor-default disabled:opacity-80"
                                >
                                    {effectiveApccScope === 'indo-pacific' ? <Globe className="h-4 w-4" /> : <MapIcon className="h-4 w-4" />}
                                    <span>{effectiveApccScope === 'indo-pacific' ? 'Indo-Pacific' : (effectiveApccScope === 'rmi' ? 'RMI' : 'Palau')}</span>
                                </motion.button>
                                <div className="header-primary-action flex h-10 items-center rounded-lg px-2">
                                    <select
                                        value={apccAggregation}
                                        onChange={event => setApccAggregation(event.target.value)}
                                        aria-label="APCC time aggregation"
                                        title="APCC time aggregation"
                                        className="max-w-[108px] bg-transparent text-xs font-medium text-cyan-100 outline-none [&>option]:bg-slate-900"
                                    >
                                        <option value="monthly" disabled={!apccAvailableAggregations.includes('monthly')}>Monthly</option>
                                        <option value="seasonal" disabled={!apccAvailableAggregations.includes('seasonal')}>Seasonal</option>
                                    </select>
                                </div>
                            </>
                        )}

                        {(isForecastWindSelected || isHurricaneSelected) && (
                            <div className="header-primary-action flex h-10 items-center gap-2 rounded-lg px-3 text-xs text-cyan-100">
                                <Wind className="h-4 w-4 text-cyan-300" />
                                <select
                                    value={windRenderMode}
                                    onChange={event => setWindRenderMode(event.target.value)}
                                    title="Wind rendering mode"
                                    aria-label="Wind rendering mode"
                                    className="max-w-[168px] bg-transparent text-xs font-medium text-cyan-100 outline-none [&>option]:bg-slate-900"
                                >
                                    <option value="particles">Animated particles</option>
                                    <option value="vectors">Static vectors</option>
                                    <option value="barbs">NOAA wind barbs</option>
                                </select>
                                <Switch
                                    size="small"
                                    checked={showWindParticles}
                                    onChange={event => setShowWindParticles(event.target.checked)}
                                    inputProps={{ 'aria-label': `Toggle ${windRenderMode === 'particles' ? 'animated wind particles' : windRenderMode === 'barbs' ? 'NOAA wind barbs' : 'static wind vectors'}` }}
                                    sx={{
                                        ml: -0.5,
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#67e8f9' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#06b6d4' },
                                    }}
                                />
                            </div>
                        )}

                        <div className="mx-1 h-6 w-px bg-white/10" aria-hidden="true" />

                        <motion.button
                            id="tutorial-layers-btn"
                            type="button"
                            onClick={handleLayersMenuOpen}
                            title="Toggle Map Overlays"
                            aria-label="Toggle Map Overlay Layers"
                            aria-pressed={Boolean(layersMenuAnchor)}
                            whileTap={{ scale: 0.95 }}
                            className={clsx(
                                "header-navigation-action flex h-10 w-10 items-center justify-center gap-2 rounded-lg lg:w-auto lg:px-3",
                                Boolean(layersMenuAnchor) && "bg-cyan-400/20"
                            )}
                        >
                            <Layers className="h-5 w-5 stroke-[1.5]" />
                            <span className="hidden text-xs font-medium lg:inline">Layers</span>
                        </motion.button>

                        <div className="header-utility-cluster flex items-center gap-1">
                            <motion.button
                                id="tutorial-download-btn"
                                type="button"
                                onClick={handleDownloadMenuOpen}
                                disabled={!state.currentJSON}
                                title="Download Data"
                                aria-label="Download Dataset Options"
                                whileTap={!state.currentJSON ? {} : { scale: 0.95 }}
                                className={clsx(
                                    "header-utility-action hidden h-10 items-center justify-center gap-2 px-2 sm:flex lg:px-3",
                                    !state.currentJSON && "opacity-30 cursor-not-allowed"
                                )}
                            >
                                <Download className="h-5 w-5 stroke-[1.5]" />
                                <span className="hidden text-xs font-medium xl:inline">Export</span>
                            </motion.button>

                            <Menu
                            anchorEl={downloadMenuAnchor}
                            open={isDownloadMenuOpen}
                            onClose={handleDownloadMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                className: "export-menu material-panel mt-2 overflow-hidden rounded-lg border text-white",
                                sx: {
                                    backgroundImage: 'none',
                                    boxShadow: '0 12px 28px rgba(2, 8, 23, 0.28)',
                                }
                            }}
                            MenuListProps={{
                                className: "p-1"
                            }}
                        >
                            <MenuItem 
                                onClick={() => handleDownload('json')}
                                className="rounded-md text-sm font-medium text-slate-100 transition-colors hover:bg-white/10 focus:bg-white/10"
                            >
                                Download GeoJSON
                            </MenuItem>
                            <MenuItem 
                                onClick={() => handleDownload('shp')}
                                className="rounded-md text-sm font-medium text-slate-100 transition-colors hover:bg-white/10 focus:bg-white/10"
                            >
                                Download Shapefile
                            </MenuItem>
                            {state.scatterplotVariable === 'crw_daily' && crwScope === 'pacific' && (
                                <MenuItem
                                    onClick={handlePacificNativeCrwDownload}
                                    className="rounded-md text-sm font-medium text-slate-100 transition-colors hover:bg-white/10 focus:bg-white/10"
                                >
                                    Download Pacific 0.05° FlatGeobuf (large)
                                </MenuItem>
                            )}
                            </Menu>

                        {/* --- Map Layers / Overlays Menu --- */}
                            <Menu
                            anchorEl={layersMenuAnchor}
                            open={Boolean(layersMenuAnchor)}
                            onClose={handleLayersMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                className: "material-panel mt-2 w-[320px] overflow-hidden rounded-lg border text-white",
                                sx: { backgroundImage: 'none', backgroundColor: 'rgba(15, 23, 42, 0.95)' }
                            }}
                            MenuListProps={{ sx: { backgroundColor: 'transparent', padding: 0 } }}
                        >
                            <div className="p-4 max-h-[60vh] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}>
                                <h3 className="text-lg font-light tracking-wide mb-4 flex items-center gap-2 text-cyan-400">
                                    <Layers size={18} /> Map Tools &amp; Overlays
                                </h3>
                                {!isLargeScreen && isHurricaneSelected && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            updateMapViewState({ ...(rmiPortal ? RMI_PORTAL_VIEW : { longitude: 134.5, latitude: 7.5, zoom: 5.5, bearing: 0, pitch: 0 }), transitionDuration: 1500 });
                                            handleLayersMenuClose();
                                        }}
                                        className="map-control mb-4 flex w-full items-center gap-2 rounded-lg border border-cyan-300/25 bg-cyan-500/10 px-3 py-2 text-left text-sm font-medium text-cyan-100"
                                    >
                                        <Maximize size={16} /> Reset cyclone map view
                                    </button>
                                )}
                                {convergenceContext.eligible && (
                                    <div className="mb-5">
                                        <h4 className="mb-2 border-b border-white/10 pb-1 text-xs font-semibold text-slate-300">Ocean diagnostics</h4>
                                        <label id="tutorial-convergence-toggle" className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-2 transition-colors hover:bg-white/5">
                                            <div className="min-w-0 pr-3">
                                                <span className="block text-sm font-semibold text-slate-100">Convergence zone (29°C)</span>
                                                <span className="mt-0.5 block text-xs leading-4 text-slate-400">Current SST boundary and monthly OISST reference</span>
                                            </div>
                                            <Switch
                                                size="small"
                                                checked={convergenceEnabled}
                                                disabled={!convergenceManifest || convergenceManifestError}
                                                onChange={(event) => setConvergenceEnabled(event.target.checked)}
                                                inputProps={{ 'aria-label': 'Show 29 degree convergence zone overlay' }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#22d3ee' },
                                                }}
                                            />
                                        </label>
                                        {convergenceEnabled && (
                                            <div className="ml-2 mt-1 border-t border-white/10 pt-1">
                                                <label className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5">
                                                    <span className="flex items-center gap-2 text-sm text-slate-200">
                                                        <span className="h-[3px] w-6 rounded-sm bg-[#f472b6]" aria-hidden="true" />
                                                        Selected frame
                                                    </span>
                                                    <Switch
                                                        size="small"
                                                        checked={convergenceCurrentVisible && Boolean(convergenceContext.currentUrl)}
                                                        disabled={!convergenceContext.currentUrl}
                                                        onChange={(event) => setConvergenceCurrentVisible(event.target.checked)}
                                                        inputProps={{ 'aria-label': 'Show selected frame convergence contour' }}
                                                    />
                                                </label>
                                                <label className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5">
                                                    <span className="flex items-center gap-2 text-sm text-slate-200">
                                                        <span className="h-[3px] w-6 rounded-sm bg-[#4ade80]" aria-hidden="true" />
                                                        Monthly climatology
                                                    </span>
                                                    <Switch
                                                        size="small"
                                                        checked={convergenceClimatologyVisible && Boolean(convergenceContext.climatologyUrl)}
                                                        disabled={!convergenceContext.climatologyUrl}
                                                        onChange={(event) => setConvergenceClimatologyVisible(event.target.checked)}
                                                        inputProps={{ 'aria-label': 'Show monthly convergence-zone climatology' }}
                                                    />
                                                </label>
                                                {(convergenceManifestError || convergenceContext.status) && (
                                                    <p className="px-2 pb-1 pt-1 text-xs leading-4 text-amber-200">
                                                        {convergenceManifestError
                                                            ? 'Contour catalogue could not be loaded. Refresh after the publisher completes.'
                                                            : convergenceContext.status}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {['Environment', 'Infrastructure', 'Boundaries', ...(rmiPortal ? ['Marshall Islands'] : [])].map(category =>
                                    BOUNDARY_LAYERS_CONFIG.some(l => l.category === category) && (
                                    <div key={category} className="mb-5 last:mb-0">
                                        <h4 className="mb-2 border-b border-white/10 pb-1 text-xs font-semibold text-slate-300">{category}</h4>
                                        <div className="flex flex-col gap-1">
                                            {BOUNDARY_LAYERS_CONFIG.filter(l => l.category === category).map(layer => (
                                                <label key={layer.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors group">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-3 h-3 rounded-sm shadow-inner border border-white/20" style={{ backgroundColor: `rgb(${layer.color.join(',')})` }} />
                                                        <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">{layer.label}</span>
                                                    </div>
                                                    <Switch 
                                                        size="small" 
                                                        checked={activeBoundaryLayers.includes(layer.id)} 
                                                        onChange={(e) => toggleBoundaryLayer(layer.id, e.target.checked)}
                                                        sx={{ 
                                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' }, 
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#22d3ee' } 
                                                        }}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    )
                                )}
                            </div>
                            </Menu>

                            <motion.button
                                id="tutorial-screenshot-btn"
                                type="button"
                                onClick={handleScreenshot}
                                title="Capture Screenshot"
                                whileTap={{ scale: 0.95 }}
                                className="header-utility-action hidden h-10 items-center justify-center gap-2 px-2 sm:flex lg:px-3"
                            >
                                <Camera className="h-5 w-5 stroke-[1.5]" />
                                <span className="hidden text-xs font-medium xl:inline">Capture</span>
                            </motion.button>

                            <motion.button
                                type="button"
                                onClick={(event) => setMobileActionsAnchor(event.currentTarget)}
                                title="More map actions"
                                aria-label="More map actions"
                                aria-haspopup="menu"
                                aria-expanded={isMobileActionsOpen}
                                whileTap={{ scale: 0.95 }}
                                className={clsx(
                                    "header-utility-action flex h-10 w-10 items-center justify-center sm:hidden",
                                    isMobileActionsOpen && "bg-cyan-400/20"
                                )}
                            >
                                <MoreHorizontal className="h-5 w-5 stroke-[1.5]" />
                            </motion.button>
                            <Menu
                            anchorEl={mobileActionsAnchor}
                            open={isMobileActionsOpen}
                            onClose={handleMobileActionsClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{ className: "export-menu material-panel mt-2 min-w-[13rem] overflow-hidden rounded-xl border text-white", sx: { backgroundImage: 'none', backgroundColor: 'rgba(15, 23, 42, 0.98)' } }}
                            MenuListProps={{ className: 'p-1' }}
                        >
                            <MenuItem disabled={!state.currentJSON} onClick={() => { handleMobileActionsClose(); handleDownload('json'); }} className="gap-2 rounded-lg text-sm text-slate-100"><Download size={16} /> Download GeoJSON</MenuItem>
                            <MenuItem disabled={!state.currentJSON} onClick={() => { handleMobileActionsClose(); handleDownload('shp'); }} className="gap-2 rounded-lg text-sm text-slate-100"><Download size={16} /> Download Shapefile</MenuItem>
                            {state.scatterplotVariable === 'crw_daily' && crwScope === 'pacific' && (
                                <MenuItem onClick={() => { handleMobileActionsClose(); handlePacificNativeCrwDownload(); }} className="gap-2 rounded-lg text-sm text-slate-100"><Download size={16} /> Pacific 0.05° download</MenuItem>
                            )}
                            <MenuItem onClick={() => { handleMobileActionsClose(); handleScreenshot(); }} className="gap-2 rounded-lg text-sm text-slate-100"><Camera size={16} /> Capture screenshot</MenuItem>
                            </Menu>

                            <motion.button
                             id="tutorial-info-btn"
                             type="button"
                             onClick={handleRightDrawerToggle}
                             title="Toggle Info Panel"
                             aria-pressed={rightDrawerOpen}
                             whileTap={{ scale: 0.95 }}
                             className={clsx(
                                "header-utility-action header-info-action flex h-10 items-center justify-center gap-2 px-2 lg:px-3",
                                rightDrawerOpen && "bg-cyan-400/20"
                             )}
                         >
                              <Info className="h-5 w-5 stroke-[1.5]" />
                              <span className="hidden text-xs font-medium xl:inline">Help</span>
                         </motion.button>
                        </div>
                    </div>
                </header>

                {/* --- Left Drawer (Navigation) --- */}
                <AnimatePresence>
                    {mobileOpen && !isLargeScreen && (
                        <motion.div 
                            key="left-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={handleDrawerToggle}
                            className={clsx("fixed inset-0 z-40", !isLargeScreen && "z-[65] bg-slate-950/35 backdrop-blur-[1px]")}
                        />
                    )}
                    {mobileOpen && (
                        <motion.aside
                            key="left-drawer"
                            initial={isLargeScreen ? { x: -300 } : { y: "100%" }}
                                animate={isLargeScreen ? { x: 0 } : leftSheetControls}
                                exit={isLargeScreen ? { x: -300 } : { y: "100%" }}
                                transition={{ type: "spring", stiffness: 460, damping: 42 }}
                                drag={isLargeScreen ? false : "y"}
                                dragListener={false}
                                dragControls={leftSheetDragControls}
                                dragConstraints={{ top: 0, bottom: 0 }}
                                dragElastic={{ top: 0, bottom: 0.22 }}
                                dragMomentum={false}
                                onDragEnd={(_, info) => {
                                    if (info.offset.y > 112 || info.velocity.y > 620) {
                                        setMobileOpen(false);
                                        return;
                                    }
                                    leftSheetControls.start({
                                        y: 0,
                                        transition: { type: 'spring', stiffness: 460, damping: 42, velocity: info.velocity.y }
                                    });
                                }}
                                className={clsx(
                                    isLargeScreen
                                        ? "fixed inset-y-0 left-0 z-50 w-[280px] border-r"
                                        : "mobile-drawer-sheet fixed inset-x-0 bottom-0 z-[70] flex max-h-[82svh] w-full flex-col rounded-t-[1.5rem] border-t",
                                    "material-drawer"
                                )}
                            >
                                {!isLargeScreen && (
                                    <div className="mobile-sheet-header">
                                        <div
                                            className="mobile-sheet-grabber"
                                            onPointerDown={(event) => leftSheetDragControls.start(event)}
                                            role="separator"
                                            aria-label="Drag down to dismiss navigation"
                                        />
                                        <div className="flex items-center justify-between px-5 pb-3">
                                            <span className="text-sm font-semibold tracking-[0.01em] text-cyan-100">Navigation</span>
                                            <button type="button" onClick={() => setMobileOpen(false)} className="map-control rounded-full p-2 text-cyan-50" aria-label="Close navigation">
                                                <X size={18} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {drawerContent}

                                {/* Glassmorphic Vertical Collapse Tab */}
                                {isLargeScreen && <motion.button
                                    type="button"
                                    onClick={() => { setLeftHintDismissed(true); handleDrawerToggle(); }}
                                    initial={{ x: 0 }}
                                    animate={{ x: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className={clsx(
                                        "absolute -right-10 top-1/2 flex flex-col items-center justify-center gap-3 py-6 w-10 -translate-y-1/2",
                                        "drawer-handle rounded-r-2xl border-y border-r border-white/20 bg-black/70",
                                        "shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-white/80 hover:text-white group z-50"
                                    )}
                                >
                                    <ChevronLeft size={18} className="opacity-70 group-hover:opacity-100 transition-opacity" />
                                    <span className="text-[9px] font-medium uppercase tracking-[0.25em] [writing-mode:vertical-rl] rotate-180 opacity-70 group-hover:opacity-100 transition-opacity">
                                        {leftHintDismissed ? "Hide" : "Minimize"}
                                    </span>
                                </motion.button>}
                            </motion.aside>
                    )}
                </AnimatePresence>
                
                {/* Floating Open Button (Visible when menu is closed) */}
                <AnimatePresence>
                    {!mobileOpen && (
                        <motion.button
                            type="button"
                            key="left-open-btn"
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            exit={{ x: -50, opacity: 0, transition: { duration: 0.2, repeat: 0 } }}
                            onClick={handleDrawerToggle}
                            className={clsx(
                                isLargeScreen
                                    ? "fixed left-0 top-1/2 z-50 flex flex-col items-center justify-center gap-3 py-6 w-10 -translate-y-1/2"
                                    : "fixed bottom-5 left-5 z-50 flex h-12 w-12 items-center justify-center rounded-full",
                                "drawer-handle rounded-r-2xl border-y border-r border-white/20 bg-black/70",
                                "shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-white/80 hover:text-white group z-50"
                            )}
                        >
                            <ChevronRight size={18} className="opacity-70 group-hover:opacity-100 transition-opacity" />
                            {isLargeScreen && <span className="text-[9px] font-medium uppercase tracking-[0.25em] [writing-mode:vertical-rl] rotate-180 opacity-70 group-hover:opacity-100 transition-opacity">
                                Menu
                            </span>}
                        </motion.button>
                    )}
                </AnimatePresence>

                {/* --- Main Content Area --- */}
                <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    // Adjust margin based on left drawer state
                    marginLeft: 0,
                }}
            >
                    {/* Map Container */}
                      {/* <Box sx={{ flexGrow: 1,  width: '100%', position: 'relative' }}> */}
                      <Box
                          ref={mapContainerRef}
                          id="tutorial-map-container"
                          className={clsx('map-interaction-surface', mapInteractionActive && 'map-interaction-surface--active')}
                          onPointerEnter={() => setMapInteractionActive(true)}
                          onPointerLeave={() => setMapInteractionActive(false)}
                          sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}
                      >

                         {TUTORIAL_STEPS[tutorialStep - 1]?.action === 'regional-sea-level-virtual-station' && (
                             <div
                                 className="pointer-events-none absolute z-[100]"
                                 style={{
                                     left: rightDrawerOpen && isLargeScreen ? '120px' : '50%',
                                     top: '48%',
                                     transform: 'translate(-50%, -50%)',
                                 }}
                                 aria-hidden="true"
                             >
                                 <div className="relative h-20 w-20 border-[3px] border-white bg-cyan-300/30 shadow-[0_0_0_3px_rgba(34,211,238,0.75)]">
                                     <span className="absolute left-1/2 top-full mt-2 w-max -translate-x-1/2 rounded bg-slate-950/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-cyan-100">
                                         Selected grid cell
                                     </span>
                                 </div>
                             </div>
                         )}

                         {/* Map actions and load state share one compact, non-blocking surface. */}
                         {(!isHurricaneSelected || isLargeScreen) && <Box sx={{
                             position: 'absolute',
                             top: `${headerHeight + 20}px`,
                             right: rightDrawerOpen && isLargeScreen ? '770px' : (state.variableSelected === '7.2.1' ? '310px' : '20px'),
                             zIndex: 50,
                             transition: 'right 180ms cubic-bezier(0.16, 1, 0.3, 1), top 180ms cubic-bezier(0.16, 1, 0.3, 1)'
                         }}>
                             <div className="flex flex-col items-end gap-2">
                             {!isHurricaneSelected && (
                                 <div className={clsx("map-load-status material-control", isMapLoading && "map-load-status--loading")} role="status" aria-live="polite" aria-atomic="true">
                                     {isMapLoading && <span className="map-load-status__dot" aria-hidden="true" />}
                                     <span>{isLoadTimedOut ? 'Map update delayed' : mapLoadLabel}</span>
                                 </div>
                             )}
                             <div className="map-navigation-actions material-control" role="group" aria-label="Map navigation">
                                 <button
                                     type="button"
                                     className="map-navigation-action map-control"
                                     onClick={() => adjustMapZoom(-1)}
                                     aria-label="Zoom map out"
                                     title="Zoom out"
                                 >
                                     <Minus size={18} aria-hidden="true" />
                                 </button>
                                 <button
                                     type="button"
                                     className="map-navigation-action map-control"
                                     onClick={() => adjustMapZoom(1)}
                                     aria-label="Zoom map in"
                                     title="Zoom in"
                                 >
                                     <Plus size={18} aria-hidden="true" />
                                 </button>
                                 <motion.button
                                     id="tutorial-reset-view-btn"
                                     type="button"
                                     onClick={() => updateMapViewState({ ...(rmiPortal ? RMI_PORTAL_VIEW : { longitude: 134.5, latitude: 7.5, zoom: 5.5, bearing: 0, pitch: 0 }), transitionDuration: 1500 })}
                                     whileTap={{ scale: 0.97 }}
                                     className="map-reset-action map-navigation-reset map-control"
                                     title="Reset view"
                                 >
                                     <Maximize size={18} aria-hidden="true" />
                                     <span>Reset view</span>
                                 </motion.button>
                             </div>
                             <AnimatePresence>
                                 {showMapZoomHint && (
                                     <motion.div
                                         className="map-zoom-hint material-control"
                                         initial={{ opacity: 0, y: -4 }}
                                         animate={{ opacity: 1, y: 0 }}
                                         exit={{ opacity: 0, y: -4 }}
                                         transition={{ duration: 0.18 }}
                                         role="status"
                                     >
                                         <span>Scroll or pinch to zoom the map</span>
                                         <button type="button" onClick={dismissMapZoomHint} className="map-zoom-hint__dismiss map-control" aria-label="Dismiss map zoom hint">
                                             <X size={14} aria-hidden="true" />
                                         </button>
                                     </motion.div>
                                 )}
                             </AnimatePresence>
                             </div>
                         </Box>}

                         <ErrorBoundary message="An error occurred while loading the map.">
                            <DeckGL
                                 ContextProvider={MapContext ? MapContext.Provider : undefined}
                                 style={{ position: 'absolute', top: 0, left: 0, height: "100%", width:"100%" }} // Fill container
                                 controller={{
                                     dragPan: true,
                                     dragRotate: false,
                                     scrollZoom: true,
                                     doubleClickZoom: true,
                                     touchZoom: true,
                                     touchRotate: false,
                                     keyboard: true,
                                 }}
                                 views={REPEAT_MAP_VIEW}
                                 viewState={mapState.viewState}
                                //   viewState={mapState} // Pass the viewState object directly
                                //   onViewStateChange={({ viewState: newViewState }) => updateMapViewState(newViewState)} // Update view state on interaction
                                 glOptions={{ preserveDrawingBuffer: true }}
                                 onViewStateChange={handleMapViewStateChange}

                                  layers={[
                                      ...(isHurricaneSelected
                                          ? (state.currentJSON ? deckLayers : [])
                                          : state.variableSelected === '2.1.2' ? [pccLayer, pccLabelLayer]
                                          : state.variableSelected === '3.8.1' ? [ngardokMarkerLayer, ngardokLabelLayer]
                                          : state.variableSelected === '4.1' ? [awsLayer, awsLabelLayer]
                                          : state.variableSelected?.startsWith('3.7') && accessSLayer ? [accessSLayer]
                                          : (state.currentJSON ? deckLayers : initialMapLayers)),
                                      ...convergenceLayers,
                                      ...tutorialSeaLevelCellLayers,
                                      ...dynamicBoundaryLayers,
                                      ...hurricaneLayers,
                                      ...hurricaneLiveLayers,
                                  ]}
                                  getTooltip={getTooltip}
                                  pickingRadius={5} // Add picking radius for easier interaction
                              >
                                 <StaticMap mapStyle={basemap === 'satellite' ? SATELLITE_STYLE : TERRAIN_STYLE} reuseMaps preventStyleDiffing preserveDrawingBuffer={true} />

                                 
                                 {/* Universal Map Scale Control - Styled for high contrast and placed outside StaticMap to render above DeckGL layers */}
                                 <Box sx={{ 
                                     position: 'absolute', 
                                     right: rightDrawerOpen && isLargeScreen ? '850px' : '110px', 
                                     bottom: (state.isSliderVisible && !timelineCollapsed) ? '140px' : '30px', 
                                     zIndex: 50,
                                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                 }}>
                                     <ScaleControl 
                                         maxWidth={100} 
                                         unit="metric" 
                                         style={{ 
                                             backgroundColor: 'rgba(15, 23, 42, 0.85)',
                                             border: '2px solid #22d3ee',
                                             borderTop: 'none',
                                             color: '#22d3ee',
                                             fontWeight: 'bold',
                                             fontSize: '11px',
                                             padding: '6px 12px',
                                             borderRadius: '0 0 6px 6px',
                                             boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                             backdropFilter: 'blur(4px)',
                                             textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                                         }} 
                                     />
                                 </Box>
                              </DeckGL>
                         </ErrorBoundary>
                         
                         {/* Map Legends and Colorbars */}
                         <Box
                             ref={legendContainerRef}
                             onMouseDown={handleMouseDown}
                             onPointerDown={(e) => e.stopPropagation()}
                             onTouchStart={(e) => e.stopPropagation()}
                             onClick={(e) => e.stopPropagation()}
                             onDoubleClick={(e) => e.stopPropagation()}
                             onWheel={(e) => e.stopPropagation()}
                             sx={{
                                 position: 'absolute',
                                 bottom: `${legendPos.y}px`,
                                 left: `${legendPos.x}px`,
                                 zIndex: 10,
                                 display: 'flex',
                                 flexDirection: 'column-reverse',
                                 gap: 2,
                                 alignItems: 'flex-start',
                                 cursor: isDragging ? 'grabbing' : 'grab',
                                 transition: 'none',
                                 userSelect: 'none', // Prevent text selection while dragging
                             }}>
                             {convergenceEnabled && convergenceContext.paths.length > 0 && (
                                <ConvergenceZoneLegend
                                    currentLabel={convergenceCurrentVisible ? convergenceContext.currentLabel : ''}
                                    climatologyLabel={convergenceClimatologyVisible ? convergenceContext.climatologyLabel : ''}
                                    isLoading={isConvergenceLoading}
                                />
                             )}
                             {(!isHurricaneSelected || hurricaneLegendOpen) && <Suspense fallback={null}>
                                <MapLegend
                                    activeVariable={state.scatterplotVariable}
                                    variableSelected={state.variableSelected}
                                    forecastVarKey={activeForecastVar}
                                    forecastModel={forecastModel}
                                    windRenderMode={windRenderMode}
                                    crwAlertMode={crwAlertMode}
                                    apccAggregation={apccAggregation}
                                    cmip6Legend={activeCmip6Manifest?.legend}
                                    cmip6Label={isCmip7PreviewSelection(state.variableSelected)
                                        ? `CMIP7 · ${activeCmip6Manifest?.label || activeCmip6Variable}`
                                        : activeCmip6Manifest?.label}
                                    cmip6Units={activeCmip6Manifest?.units}
                                />
                             </Suspense>}
                             {/* Colorbar Image */}
                             {state.colorbar && (
                                 <IconButton
                                     onClick={handleColorbarModalOpen}
                                     sx={{
                                         padding: '0',
                                         backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                         borderRadius: '3px',
                                         '&:hover': {
                                             backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                         }
                                     }}
                                     aria-label="Enlarge colorbar"
                                     title="Enlarge colorbar"
                                 >
                                     <CachedImage
                                         src={state.colorbar}
                                         alt="Colorbar"
                                         style={{
                                             display: 'block',
                                             height: 'auto',
                                             maxHeight: 'min(300px, 40%)',
                                             width: 'auto',
                                             maxWidth: '60px',
                                             padding: '2px',
                                             borderRadius: '3px',
                                         }}
                                         onError={(e) => { e.target.style.display = 'none'; console.error("Colorbar failed to load:", state.colorbar);}}
                                     />
                                 </IconButton>
                             )}
                         </Box>

                         {/* Tropical Cyclone: compact overview first, details only on request. */}
                         <AnimatePresence>
                             {isHurricaneSelected && (
                                 <motion.section
                                     initial={{ opacity: 0, y: -12 }}
                                     animate={{ opacity: 1, y: 0 }}
                                     exit={{ opacity: 0, y: -12 }}
                                     className={clsx(
                                         "hurricane-overview material-panel absolute z-40 text-white",
                                         isLargeScreen ? "right-4 w-[20rem] rounded-2xl p-4" : "right-3 left-3 rounded-2xl p-3"
                                     )}
                                     style={{ top: `${headerHeight + 12}px` }}
                                 >
                                     {hurricaneStorms.length === 0 ? (
                                         <p className="text-sm text-slate-200">No active tropical cyclones right now.</p>
                                     ) : (
                                         <>
                                             <div className="flex items-start justify-between gap-3">
                                                 <button type="button" onClick={() => setHurricaneDetailsOpen(open => !open)} className="min-w-0 text-left" aria-expanded={hurricaneDetailsOpen}>
                                                     <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-300">Tropical Cyclone</p>
                                                     <h3 className="mt-1 truncate text-lg font-semibold leading-tight">{activeHurricaneStorm?.name} · {activeHurricaneStorm?.sshws_label} · {activeHurricaneStorm?.vmax_kt ?? '—'} kt</h3>
                                                     <p className="mt-1 text-[11px] text-slate-300">{isLargeScreen ? hurricaneUpdatedRelative : `${hurricaneModels[hurricaneSelectedModel]?.label || 'No'} forecast`}</p>
                                                 </button>
                                                 <button type="button" onClick={() => setHurricaneDetailsOpen(open => !open)} className="map-control mt-1 rounded-full p-2 text-cyan-100" aria-label="Toggle tropical cyclone details" aria-expanded={hurricaneDetailsOpen}>
                                                     <ChevronDown className={clsx("h-4 w-4 transition-transform", hurricaneDetailsOpen && "rotate-180")} />
                                                 </button>
                                             </div>

                                             <div className={clsx("mt-3 grid grid-cols-[1fr_auto] gap-2", !isLargeScreen && "hidden")}>
                                                 <Select value={activeHurricaneStorm?.id || ''} onChange={e => setHurricaneStormId(e.target.value)} size="small" aria-label="Active tropical cyclone" sx={{ color: 'white', minWidth: 0, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' }, '& .MuiSvgIcon-root': { color: 'white' }, fontSize: '0.82rem' }}>
                                                     {hurricaneStorms.map(s => <MenuItem key={s.id} value={s.id}>{s.name} · {s.sshws_label}</MenuItem>)}
                                                 </Select>
                                                 <select value={hurricaneSelectedModel} onChange={event => setHurricaneSelectedModel(event.target.value)} aria-label="Selected forecast model" className="h-10 max-w-[9.25rem] rounded-lg border border-white/20 bg-slate-950/60 px-2 text-xs font-semibold text-cyan-100 outline-none">
                                                     {availableHurricaneModels.map(model => <option key={model.id} value={model.id}>{model.label}</option>)}
                                                     {!availableHurricaneModels.length && <option value="">No forecast</option>}
                                                 </select>
                                             </div>

                                             <div className={clsx("mt-3 flex items-center justify-between gap-3 border-t border-white/10 pt-3 text-xs", !isLargeScreen && "hidden")}>
                                                 <span className="text-slate-300">{hurricaneTimelineStatus ? 'Selected time' : 'Current status'}</span>
                                                 <span className="font-medium text-white">{hurricaneTimelineStatus?.properties?.sshws_label || activeHurricaneStorm?.sshws_label} · {Math.round(hurricaneTimelineStatus?.properties?.vmax_kt ?? activeHurricaneStorm?.vmax_kt ?? 0) || '—'} kt</span>
                                             </div>

                                             <div className={clsx("mt-3 flex items-center gap-2", !isLargeScreen && "hidden")}>
                                                 <button type="button" onClick={() => setHurricaneLegendOpen(open => !open)} className="interaction-chip border border-white/15 bg-white/5 px-3 text-xs font-medium text-cyan-100" aria-expanded={hurricaneLegendOpen}>Legend</button>
                                                 <button type="button" onClick={() => setHurricaneFocusMode(focus => !focus)} className="interaction-chip border border-white/15 bg-white/5 px-3 text-xs font-medium text-cyan-100" aria-pressed={!hurricaneFocusMode}>{hurricaneFocusMode ? 'Show wind' : 'Mute wind'}</button>
                                                 <button type="button" onClick={() => setTimelineCollapsed(collapsed => !collapsed)} className="interaction-chip ml-auto border border-white/15 bg-white/5 px-3 text-xs font-medium text-cyan-100" aria-expanded={!timelineCollapsed}>{timelineCollapsed ? 'Timeline' : 'Hide timeline'}</button>
                                             </div>

                                             <AnimatePresence initial={false}>
                                                 {hurricaneDetailsOpen && activeHurricaneStorm && isLargeScreen && (
                                                     <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ type: 'spring', stiffness: 460, damping: 42 }} className="overflow-hidden">
                                                         <div className="mt-3 space-y-2 border-t border-white/10 pt-3 text-xs">
                                                             <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-xl bg-black/15 px-3 py-2.5">
                                                                 <span className="text-slate-400">Pressure</span><span className="text-right font-medium">{Math.round(hurricaneTimelineStatus?.properties?.mslp_mb ?? activeHurricaneStorm.mslp_mb ?? 0) || '—'} mb</span>
                                                                 <span className="text-slate-400">Valid time</span><span className="text-right font-medium">{new Date(hurricaneTimelineStatus?.properties?.valid || activeHurricaneStorm.latest_time).toLocaleString()}</span>
                                                                 <span className="text-slate-400">Data updated</span><span className="text-right font-medium">{hurricaneUpdatedExact}</span>
                                                             </div>
                                                             <p className="leading-relaxed text-slate-300">Historic observations remain visible. The selected forecast and its cone are the only forecast guidance shown.</p>
                                                         </div>
                                                     </motion.div>
                                                 )}
                                             </AnimatePresence>
                                         </>
                                     )}
                                 </motion.section>
                             )}
                         </AnimatePresence>

                         {/* Mobile details are a sheet, not a taller cyclone card. It
                             has a mid and a full snap point, plus a dismiss point. */}
                         <AnimatePresence>
                             {isHurricaneSelected && !isLargeScreen && hurricaneDetailsOpen && activeHurricaneStorm && (
                                 <motion.aside
                                     initial={{ y: '100%', opacity: 0 }}
                                     animate={hurricaneDetailsSheetControls}
                                     exit={{ y: '100%', opacity: 0 }}
                                     drag="y"
                                     dragListener={false}
                                     dragControls={hurricaneDetailsSheetDragControls}
                                     dragConstraints={{ top: 0, bottom: Math.round(window.innerHeight * 0.78) }}
                                     dragElastic={{ top: 0, bottom: 0.16 }}
                                     dragMomentum={false}
                                     onDragEnd={(_, info) => {
                                         const half = Math.round(window.innerHeight * 0.28);
                                         const full = 0;
                                         const dismiss = Math.round(window.innerHeight * 0.78);
                                         const current = hurricaneDetailsSnap === 'full' ? full : half;
                                         const projected = projectSheetRelease(current + info.offset.y, info.velocity.y);
                                         const target = [full, half, dismiss].reduce((nearest, snap) => (
                                             Math.abs(projected - snap) < Math.abs(projected - nearest) ? snap : nearest
                                         ), full);
                                         if (target === dismiss) {
                                             setHurricaneDetailsOpen(false);
                                             return;
                                         }
                                         setHurricaneDetailsSnap(target === full ? 'full' : 'half');
                                         hurricaneDetailsSheetControls.start({ y: target, opacity: 1, transition: { type: 'spring', stiffness: 460, damping: 42, velocity: info.velocity.y } });
                                     }}
                                     className="mobile-drawer-sheet material-drawer fixed inset-x-0 bottom-0 z-[72] flex h-[78svh] flex-col rounded-t-[1.5rem] border-t text-white"
                                     aria-label="Tropical cyclone details"
                                 >
                                     <div className="mobile-sheet-header shrink-0">
                                         <div className="mobile-sheet-grabber" onPointerDown={(event) => hurricaneDetailsSheetDragControls.start(event)} role="separator" aria-label="Drag cyclone details between snap points" />
                                         <div className="flex items-center justify-between px-5 pb-3">
                                             <div><p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">Tropical cyclone details</p><p className="mt-0.5 text-xs text-slate-300">{hurricaneUpdatedExact}</p></div>
                                             <button type="button" onClick={() => setHurricaneDetailsOpen(false)} className="map-control rounded-full p-2 text-cyan-50" aria-label="Close tropical cyclone details"><X size={18} /></button>
                                         </div>
                                     </div>
                                     <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 pb-8 text-sm">
                                         <div className="grid grid-cols-[1fr_auto] gap-2">
                                             <Select value={activeHurricaneStorm.id} onChange={e => setHurricaneStormId(e.target.value)} size="small" aria-label="Active tropical cyclone" sx={{ color: 'white', minWidth: 0, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' }, '& .MuiSvgIcon-root': { color: 'white' }, fontSize: '0.82rem' }}>
                                                 {hurricaneStorms.map(s => <MenuItem key={s.id} value={s.id}>{s.name} · {s.sshws_label}</MenuItem>)}
                                             </Select>
                                             <select value={hurricaneSelectedModel} onChange={event => setHurricaneSelectedModel(event.target.value)} aria-label="Selected forecast model" className="h-10 max-w-[9.25rem] rounded-lg border border-white/20 bg-slate-950/60 px-2 text-xs font-semibold text-cyan-100 outline-none">
                                                 {availableHurricaneModels.map(model => <option key={model.id} value={model.id}>{model.label}</option>)}
                                                 {!availableHurricaneModels.length && <option value="">No forecast</option>}
                                             </select>
                                         </div>
                                         <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/15 px-3 py-2.5 text-xs">
                                             <span className="text-slate-300">{hurricaneTimelineStatus ? 'Selected time' : 'Current status'}</span>
                                             <span className="font-medium text-white">{hurricaneTimelineStatus?.properties?.sshws_label || activeHurricaneStorm.sshws_label} · {Math.round(hurricaneTimelineStatus?.properties?.vmax_kt ?? activeHurricaneStorm.vmax_kt ?? 0) || '—'} kt</span>
                                         </div>
                                         <div className="flex items-center gap-2">
                                             <button type="button" onClick={() => setHurricaneLegendOpen(open => !open)} className="interaction-chip border border-white/15 bg-white/5 px-3 text-xs font-medium text-cyan-100" aria-expanded={hurricaneLegendOpen}>Legend</button>
                                             <button type="button" onClick={() => setHurricaneFocusMode(focus => !focus)} className="interaction-chip border border-white/15 bg-white/5 px-3 text-xs font-medium text-cyan-100" aria-pressed={!hurricaneFocusMode}>{hurricaneFocusMode ? 'Show wind' : 'Mute wind'}</button>
                                         </div>
                                         <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl bg-black/15 px-3 py-3 text-xs">
                                             <span className="text-slate-400">Pressure</span><span className="text-right font-medium">{Math.round(hurricaneTimelineStatus?.properties?.mslp_mb ?? activeHurricaneStorm.mslp_mb ?? 0) || '—'} mb</span>
                                             <span className="text-slate-400">Valid time</span><span className="text-right font-medium">{new Date(hurricaneTimelineStatus?.properties?.valid || activeHurricaneStorm.latest_time).toISOString().replace('.000Z', 'Z')}</span>
                                             <span className="text-slate-400">Data updated</span><span className="text-right font-medium">{hurricaneUpdatedExact}</span>
                                         </div>
                                         <p className="text-xs leading-relaxed text-slate-300">Historic observations remain visible. The selected forecast and its cone are the only forecast guidance shown.</p>
                                     </div>
                                 </motion.aside>
                             )}
                         </AnimatePresence>

                         {/* --- Marine Heat Wave Custom UI Panel --- */}
                         <AnimatePresence>
                             {state.variableSelected === '7.2.1' && (
                                 <motion.div
                                     id="tutorial-mhw-panel"
                                     initial={{ opacity: 0, y: -20 }}
                                     animate={{ opacity: 1, y: 0 }}
                                     exit={{ opacity: 0, y: -20 }}
                                     className="material-panel absolute top-4 right-4 z-40 flex flex-col gap-4 rounded-2xl border p-5 text-white w-72"
                                     style={{ top: `${headerHeight + 20}px` }}
                                 >
                                     <h3 className="text-sm font-bold tracking-[0.015em] text-cyan-300 border-b border-white/10 pb-2">Marine Heat Wave</h3>

                                     {/* Geographic scope is a first-class selection: RMI portals
                                         open on RMI and Indo-Pacific remains an explicit basin view. */}
                                     <div className="flex flex-col gap-1.5">
                                         <label className="map-control-label">Geographic scope</label>
                                         <Select
                                             value={mhwScope}
                                             onChange={e => setMhwScope(e.target.value)}
                                             size="small"
                                             sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }, '& .MuiSvgIcon-root': { color: 'white' }, fontSize: '0.875rem' }}
                                         >
                                             <MenuItem value="palau">Palau</MenuItem>
                                             <MenuItem value="rmi" disabled={!rmiPortal || !mhwScopeMetadata.rmi?.monthly?.dates?.length}>Marshall Islands</MenuItem>
                                             <MenuItem value="indo">Indo-Pacific</MenuItem>
                                         </Select>
                                     </div>
                                     
                                     {/* Timescale */}
                                     <div className="flex flex-col gap-1.5">
                                         <label className="map-control-label">Timescale</label>
                                         <Select 
                                             value={mhwTimescale} 
                                             onChange={e => setMhwTimescale(e.target.value)}
                                             size="small"
                                             sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }, '& .MuiSvgIcon-root': { color: 'white' }, fontSize: '0.875rem' }}
                                         >
                                             <MenuItem value="monthly">Monthly</MenuItem>
                                             <MenuItem value="seasonal">Seasonal</MenuItem>
                                             <MenuItem value="decadal">Decadal</MenuItem>
                                         </Select>
                                     </div>

                                     {/* Map Type */}
                                     <div className="flex flex-col gap-1.5">
                                         <label className="map-control-label">Map type</label>
                                         <Select 
                                             value={mhwMapType} 
                                             onChange={e => {
                                                 const newType = e.target.value;
                                                 setMhwMapType(newType);
                                                 
                                                 const payload = {
                                                     scatterplotVariable: mhwIsMaskedRef.current ? 'mhw_masked' : 'mhw'
                                                 };
                                                 if (newType !== 'gridded') {
                                                     payload.lat = null;
                                                     payload.long = null;
                                                 }
                                                 dispatch({ type: 'UPDATE_CONFIG', payload });
                                                 setRightDrawerOpen(true);
                                             }}
                                             size="small"
                                             sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }, '& .MuiSvgIcon-root': { color: 'white' }, fontSize: '0.875rem' }}
                                         >
                                            <MenuItem value="contours">Contours</MenuItem>
                                            <MenuItem value="gridded">Gridded</MenuItem>
                                            <MenuItem value="voronoi" disabled={mhwScope === 'indo'}>Voronoi{mhwScope === 'indo' ? ' (country views only)' : ''}</MenuItem>
                                        </Select>
                                     </div>

                                     {/* Masked Toggle */}
                                     <div className="flex items-center justify-between mt-2">
                                         <label className="map-control-label">Geographic mask</label>
                                         <button 
                                             type="button"
                                             onClick={() => {
                                                 const newMasked = !mhwIsMasked;
                                                 setMhwIsMasked(newMasked);
                                                 if (state.scatterplotVariable && state.scatterplotVariable.startsWith('mhw')) {
                                                     dispatch({ type: 'UPDATE_CONFIG', payload: { scatterplotVariable: newMasked ? 'mhw_masked' : 'mhw' } });
                                                 }
                                             }}
                                            disabled={mhwMapType === 'voronoi' || mhwScope !== 'palau'}
                                            className={clsx(
                                               "w-10 h-5 rounded-full transition-colors relative", 
                                               mhwIsMasked ? "bg-cyan-500" : "bg-white/20",
                                               (mhwMapType === 'voronoi' || mhwScope !== 'palau') && "opacity-50 cursor-not-allowed"
                                            )}
                                         >
                                            <div className={clsx("absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-md", mhwIsMasked ? "left-5" : "left-0.5")} />
                                        </button>
                                    </div>

                                    {/* Kept as a quick shortcut alongside the explicit scope picker. */}
                                    <div className="flex items-center justify-between">
                                        <label className="map-control-label">Pacific Ocean</label>
                                        <button
                                            type="button"
                                            title="Show the whole Pacific (excludes the Indian Ocean). Gridded/contours only, multi-resolution."
                                            onClick={() => setMhwPacific(p => !p)}
                                            className={clsx(
                                               "w-10 h-5 rounded-full transition-colors relative",
                                               mhwPacific ? "bg-cyan-500" : "bg-white/20"
                                            )}
                                        >
                                            <div className={clsx("absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-md", mhwPacific ? "left-5" : "left-0.5")} />
                                        </button>
                                    </div>

                                    {/* Pacific Resolution (only when Pacific is on) */}
                                    {mhwPacific && (
                                        <div className="flex flex-col gap-1.5">
                                            <label className="map-control-label">Resolution</label>
                                            <Select
                                                value={mhwRes}
                                                onChange={e => setMhwRes(e.target.value)}
                                                size="small"
                                                sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }, '& .MuiSvgIcon-root': { color: 'white' }, fontSize: '0.875rem' }}
                                            >
                                                <MenuItem value="0p25deg">0.25&deg; (high)</MenuItem>
                                                <MenuItem value="0p5deg">0.5&deg;</MenuItem>
                                                <MenuItem value="1deg">1&deg; (low bandwidth)</MenuItem>
                                            </Select>
                                        </div>
                                    )}

                                    {/* Date Selector (Synced with bottom slider timeline) */}
                                     <div className="flex flex-col gap-1.5 mt-2">
                                         <label className="map-control-label">Time step</label>
                                         <Select 
                                             value={mhwCurrentDate} 
                                            onChange={e => {
                                                const newIndex = mhwDateOptions.indexOf(e.target.value);
                                                if (newIndex !== -1) {
                                                    dispatch({ type: 'UPDATE_CONFIG', payload: { sliderValue: newIndex } });
                                                }
                                            }}
                                             size="small"
                                             sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }, '& .MuiSvgIcon-root': { color: 'white' }, fontSize: '0.875rem', maxHeight: 300 }}
                                             MenuProps={{ PaperProps: { sx: { maxHeight: 300, backgroundColor: '#0f172a' } } }}
                                         >
                                             {mhwDateOptions.map(date => (
                                                 <MenuItem key={date} value={date} sx={{ color: 'white' }}>{date}</MenuItem>
                                             ))}
                                         </Select>
                                     </div>
                                 </motion.div>
                             )}
                         </AnimatePresence>
                      </Box>

                      {/* Slider Container */}
                     <AnimatePresence>
                        {state.isSliderVisible && !timelineCollapsed && (
                            <motion.div
                                initial={{ y: 100, opacity: 0, paddingLeft: 0, paddingRight: 0 }}
                                animate={isLargeScreen ? {
                                    y: 0,
                                    opacity: 1,
                                    paddingLeft: mobileOpen ? 280 : 0,
                                    paddingRight: rightDrawerOpen ? 750 : 0
                                } : timelineSheetControls}
                                exit={{ y: 100, opacity: 0 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                drag={isLargeScreen ? false : "y"}
                                dragListener={false}
                                dragControls={timelineSheetDragControls}
                                dragConstraints={{ top: 0, bottom: Math.round(window.innerHeight * 0.35) }}
                                dragElastic={{ top: 0, bottom: 0.16 }}
                                dragMomentum={false}
                                onDragEnd={(_, info) => {
                                    const dismiss = Math.round(window.innerHeight * 0.35);
                                    const projected = projectSheetRelease(info.offset.y, info.velocity.y);
                                    if (Math.abs(projected - dismiss) < Math.abs(projected)) {
                                        setTimelineCollapsed(true);
                                        return;
                                    }
                                    timelineSheetControls.start({ y: 0, opacity: 1, transition: { type: 'spring', stiffness: 460, damping: 42, velocity: info.velocity.y } });
                                }}
                                className="fixed bottom-8 left-0 right-0 z-40 flex justify-center"
                            >
                                <div id="tutorial-timeline-slider" className={clsx(
                                    "pointer-events-auto flex flex-nowrap items-center gap-x-2 md:gap-6 px-3 md:px-6 py-1.5 md:py-2",
                                    "material-control relative rounded-full border",
                                    "w-[95%] sm:w-[90%] max-w-4xl transition-all duration-300"
                                )}>
                                    
                                {/* Collapse Tab */}
                                <motion.button
                                    onClick={() => setTimelineCollapsed(true)}
                                    onPointerDown={(event) => { if (!isLargeScreen) timelineSheetDragControls.start(event); }}
                                    className="material-control absolute -top-6 left-1/2 z-10 flex h-6 w-16 -translate-x-1/2 items-center justify-center rounded-t-xl border border-b-0 border-cyan-400/30 text-cyan-100/60 hover:text-cyan-100 cursor-pointer"
                                    title="Hide Timeline"
                                >
                                    <ChevronDown size={16} />
                                </motion.button>

                                {/* Play/Pause Button and Speed Selector Group */}
                                <div className="flex items-center gap-3 order-1">
                                <motion.button
                                    type="button"
                                    onClick={() => updateCMIPImmediate(Math.max(0, state.sliderValue - 1), state.variableSelected)}
                                    disabled={state.sliderValue <= 0}
                                    className="flex h-8 w-8 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-full bg-white/10 border border-white/10 text-white disabled:opacity-30"
                                    title="Previous time step"
                                ><ChevronLeft className="h-4 w-4" /></motion.button>
                                {/* Play/Pause Button */}
                                <motion.button
                                    type="button"
                                    onClick={() => dispatch({ type: 'TOGGLE_PLAY' })}
                                    whileTap={{ scale: 0.95 }}
                                    className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white transition-colors hover:bg-white/20 md:h-12 md:w-12"
                                >
                                    {state.isPlaying ? (
                                        <Pause className="h-3 w-3 md:h-5 md:w-5 fill-current" />
                                    ) : (
                                        <Play className="h-3 w-3 md:h-5 md:w-5 fill-current ml-0.5 md:ml-1" />
                                    )}
                                </motion.button>

                                {/* Speed Selector */}
                                <div className="hidden md:block relative shrink-0">
                                    <Select
                                        value={animationSpeed}
                                        onChange={(e) => setAnimationSpeed(e.target.value)}
                                        variant="standard"
                                        disableUnderline
                                        displayEmpty
                                        renderValue={(value) => (
                                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-white hover:bg-white/10 transition-colors cursor-pointer">
                                                <Activity size={14} className="text-cyan-400" />
                                                <span className="tracking-[0.01em]">
                                                    {value === 2000 ? 'Slow' : 
                                                     value === 1000 ? 'Normal' : 
                                                     value === 500 ? 'Fast' : 'Turbo'}
                                                </span>
                                            </div>
                                        )}
                                        MenuProps={{
                                            PaperProps: {
                                                className: "material-panel border rounded-xl mt-2",
                                                sx: {
                                                    backgroundColor: 'rgba(15, 23, 42, 0.9)', // Force dark background for visibility in light mode
                                                    '& .MuiMenuItem-root': {
                                                        color: 'white',
                                                        fontSize: '0.875rem',
                                                        my: 0.5,
                                                        mx: 1,
                                                        borderRadius: 1,
                                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                                                        '&.Mui-selected': { 
                                                            backgroundColor: 'rgba(34, 211, 238, 0.15)', 
                                                            color: '#22d3ee',
                                                            '&:hover': { backgroundColor: 'rgba(34, 211, 238, 0.25)' } 
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                        sx={{
                                            '& .MuiSelect-select': { padding: 0, '&:focus': { backgroundColor: 'transparent' } },
                                            '& .MuiSvgIcon-root': { display: 'none' }
                                        }}
                                    >
                                        <MenuItem value={2000}>Slow</MenuItem>
                                        <MenuItem value={1000}>Normal</MenuItem>
                                        <MenuItem value={500}>Fast</MenuItem>
                                        <MenuItem value={100}>Turbo</MenuItem>
                                    </Select>
                                </div>
                                </div>

                                <motion.button
                                    type="button"
                                    onClick={() => updateCMIPImmediate(Math.min(state.dateSelected.length - 1, state.sliderValue + 1), state.variableSelected)}
                                    disabled={state.sliderValue >= state.dateSelected.length - 1}
                                    className="flex h-8 w-8 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-full bg-white/10 border border-white/10 text-white disabled:opacity-30 order-4"
                                    title="Next time step"
                                ><ChevronRight className="h-4 w-4" /></motion.button>

                                {/* Slider */}
                                <div className="flex-grow flex items-center px-1 md:px-2 order-2">
                                    <Slider
                                        min={0}
                                        max={Math.max(0, state.dateSelected.length - 1)}
                                        marks={sliderMarks}
                                        step={1}
                                        value={localSliderValue}
                                        onChange={(e, v) => setLocalSliderValue(v)}
                                        onChangeCommitted={(e, v) => updateCMIPImmediate(v, state.variableSelected)}
                                        valueLabelFormat={getSliderTooltip}
                                        valueLabelDisplay="auto"
                                        getAriaValueText={getSliderTooltip}
                                        aria-label="Timeline"
                                        disabled={!state.dateSelected || state.dateSelected.length <= 1}
                                        sx={{
                                            height: 6,
                                            '& .MuiSlider-rail': {
                                                opacity: 1,
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: '4px',
                                            },
                                            '& .MuiSlider-track': {
                                                border: 'none',
                                                borderRadius: '4px',
                                                background: 'linear-gradient(90deg, #22d3ee, #0ea5e9)',
                                            },
                                            '& .MuiSlider-thumb': {
                                                width: 20,
                                                height: 20,
                                                backgroundColor: '#fff',
                                                border: '4px solid rgba(14, 165, 233, 0.2)',
                                                boxShadow: 'none',
                                                transition: 'transform 150ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 150ms ease-out',
                                                animation: 'none',
                                                '&:hover, &.Mui-focusVisible, &.Mui-active': {
                                                    boxShadow: '0 0 0 3px rgba(165, 243, 252, 0.42)',
                                                    transform: 'translate(-50%, -50%) scale(1.05)',
                                                    animation: 'none',
                                                },
                                                '&::before': { display: 'none' },
                                            },
                                            '& .MuiSlider-mark': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                width: 2,
                                                height: 5,
                                                borderRadius: 1,
                                                '&.MuiSlider-markActive': {
                                                    backgroundColor: '#fff',
                                                    opacity: 0.8,
                                                }
                                            },
                                            '& .MuiSlider-markLabel': {
                                                top: 28,
                                                color: 'rgba(207, 250, 254, 0.78)',
                                                fontFamily: ATLAS_FONT_FAMILIES.mono,
                                                fontSize: 'clamp(0.64rem, 1.5vw, 0.76rem)',
                                                fontWeight: 500,
                                                letterSpacing: '0.01em',
                                                lineHeight: 1.1,
                                                whiteSpace: 'nowrap',
                                                fontVariantNumeric: 'tabular-nums',
                                            },
                                            '& .MuiSlider-valueLabel': {
                                                lineHeight: 1.2,
                                                fontSize: '0.75rem',
                                                fontFamily: 'monospace',
                                                background: 'rgba(15, 23, 42, 0.9)',
                                                backdropFilter: 'blur(8px)',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                '&:before': { display: 'none' },
                                            }
                                        }}
                                    />
                                </div>

                                {/* Date/Time Display */}
                                <div className="shrink-0 flex flex-col items-end justify-center min-w-[70px] md:min-w-[90px] pl-2 md:pl-4 border-l border-white/10 order-3">
                                    <span className="timeline-meta-label hidden md:block">Time</span>
                                    <span className="text-xs md:text-sm font-mono font-medium text-cyan-200 tabular-nums drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]">
                                        {state.dateSelected && state.dateSelected[localSliderValue] 
                                            ? (typeof state.dateSelected[localSliderValue] === 'string' 
                                                ? (state.dateSelected[localSliderValue].match(/\d{4}-\d{2}-\d{2}/)?.[0] || state.dateSelected[localSliderValue])
                                                : state.dateSelected[localSliderValue])
                                            : "---"}
                                    </span>
                                </div>
                                </div>
                            </motion.div>
                        )}
                        {state.isSliderVisible && timelineCollapsed && (
                            <motion.button
                                initial={{ y: 50, opacity: 0, x: "-50%" }}
                                animate={{ y: 0, opacity: 1, x: "-50%" }}
                                exit={{ y: 50, opacity: 0, x: "-50%" }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                onClick={() => setTimelineCollapsed(false)}
                                className="material-control fixed bottom-6 left-1/2 z-40 flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium text-cyan-300 hover:border-cyan-400/50 group"
                                title="Show Timeline"
                            >
                                <ChevronUp size={16} className="opacity-80 group-hover:opacity-100" />
                                <span className="tracking-[0.01em]">Show timeline</span>
                            </motion.button>
                        )}
                    </AnimatePresence>
                </Box> {/* End Main Content Area */}

                 {/* --- Right Drawer (Graphs/Docs/Info) --- */}
                 <AnimatePresence>
                    {rightDrawerOpen && !isLargeScreen && (
                        <motion.div
                            key="right-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={handleRightDrawerToggle}
                            className="fixed inset-0 z-[65] bg-slate-950/35 backdrop-blur-[1px]"
                        />
                    )}
                    {rightDrawerOpen && (
                        <motion.aside
                            id="tutorial-info-panel"
                            key="right-drawer"
                            initial={isLargeScreen ? { x: "100%" } : { y: "100%" }}
                            animate={isLargeScreen ? { x: 0 } : rightSheetControls}
                            exit={isLargeScreen ? { x: "100%" } : { y: "100%" }}
                            transition={{ type: "spring", stiffness: 460, damping: 42 }}
                            drag={isLargeScreen ? false : "y"}
                            dragListener={false}
                            dragControls={rightSheetDragControls}
                            dragConstraints={{ top: 0, bottom: 0 }}
                            dragElastic={{ top: 0, bottom: 0.22 }}
                            dragMomentum={false}
                            onDragEnd={(_, info) => {
                                if (info.offset.y > 112 || info.velocity.y > 620) {
                                    setRightDrawerOpen(false);
                                    return;
                                }
                                rightSheetControls.start({
                                    y: 0,
                                    transition: { type: 'spring', stiffness: 460, damping: 42, velocity: info.velocity.y }
                                });
                            }}
                            className={clsx(
                                isLargeScreen
                                    ? "fixed inset-y-0 right-0 z-50 w-full max-w-[750px] border-l flex flex-col"
                                    : "mobile-drawer-sheet fixed inset-x-0 bottom-0 z-[70] flex max-h-[82svh] w-full flex-col rounded-t-[1.5rem] border-t",
                                "material-drawer"
                            )}
                        >
                            {!isLargeScreen && (
                                <div className="mobile-sheet-header">
                                    <div
                                        className="mobile-sheet-grabber"
                                        onPointerDown={(event) => rightSheetDragControls.start(event)}
                                        role="separator"
                                        aria-label="Drag down to dismiss information"
                                    />
                                    <div className="flex items-center justify-between px-5 pb-3">
                                        <span className="text-sm font-semibold tracking-[0.01em] text-cyan-100">Information</span>
                                        <button type="button" onClick={() => setRightDrawerOpen(false)} className="map-control rounded-full p-2 text-cyan-50" aria-label="Close information">
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                            {/* Spacer for Header */}
                            <motion.div 
                                initial={false}
                                animate={{ height: headerHeight }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                className={clsx("shrink-0", !isLargeScreen && "hidden")}
                            />

                            {/* Glassmorphic Vertical Collapse Tab (Moves with panel) */}
                            {isLargeScreen && <motion.button
                                type="button"
                                onClick={handleRightDrawerToggle}
                                initial={{ x: 0 }}
                                animate={{ x: 0 }}
                                transition={{ duration: 0.2 }}
                                className={clsx(
                                    "absolute -left-10 top-1/2 flex flex-col items-center justify-center gap-3 py-6 w-10 -translate-y-1/2",
                                    "drawer-handle rounded-l-2xl border-y border-l border-white/20 bg-black/70",
                                    "shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-white/80 hover:text-white group z-50"
                                )}
                            >
                                <ChevronRight size={18} className="opacity-70 group-hover:opacity-100 transition-opacity" />
                                <span className="text-[9px] font-medium uppercase tracking-[0.25em] [writing-mode:vertical-rl] rotate-180 opacity-70 group-hover:opacity-100 transition-opacity">
                                    Minimize
                                </span>
                            </motion.button>}

                            <div className="flex-1 overflow-y-auto relative flex flex-col">
                            <Suspense fallback={<div className="flex h-full items-center justify-center"><CircularProgress /></div>}>
                                <RightSidebar
                                    onClose={handleRightDrawerToggle}
                                    activeDoc={state.variableSelected === '7.2.1' ? { 
                                        title: "Marine Heat Waves", 
                                        summaryContent: <MHWDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '6.1.1.3' ? {
                                        title: "Historical Regional Sea Level",
                                        summaryContent: <RegionalSeaLevelDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '6.2.2.3' ? {
                                        title: "Historical Flood Frequency",
                                        summaryContent: <FloodFrequencyDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '6.2.3.3' ? {
                                        title: "Historical Flood Duration",
                                        summaryContent: <FloodDurationDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : ['6.3.2.1', '6.3.2.2', '6.3.2.3'].includes(state.variableSelected) ? {
                                        title: state.variableSelected === '6.3.2.1'
                                            ? "Historical Wave Hindcast"
                                            : (state.variableSelected === '6.3.2.2' ? "ERA5 Wave Reanalysis" : "NOAA WW3 Wave Conditions & Forecast"),
                                        summaryContent: <WaveDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected?.startsWith('8.2') ? {
                                        title: "Satellite Chlorophyll-a",
                                        summaryContent: <div className="text-sm font-light leading-relaxed text-slate-300"><p>Monthly ESA Ocean Colour CCI v6 chlorophyll-a composites, retrieved from NOAA PIFSC OceanWatch. The {state.variableSelected === '8.2.1' ? 'linear view uses equal 0.05 mg m⁻³ bins from 0 to 0.5 mg m⁻³' : 'logarithmic view uses equal log₁₀ intervals from 0.01 to 10 mg m⁻³'}; values outside the displayed range use the nearest end color. The plot uses the same vertical scaling as the selected map.</p><p className="mt-3 text-xs text-slate-400">Coverage: September 1997 to the newest monthly source composite. Satellite gaps are expected in persistently cloudy periods.</p></div>,
                                        mathSubHeader: "Interpretation",
                                        mathContent: state.variableSelected === '8.2.1'
                                            ? "Chlorophyll-a concentration is reported in mg m⁻³ without transformation. Equal numeric intervals make absolute differences directly comparable."
                                            : "Chlorophyll-a concentration is reported in mg m⁻³ and color-binned after a log₁₀ transformation. The logarithmic scale reveals relative differences across orders of magnitude."
                                    } : state.variableSelected === '8.3.1' ? {
                                        title: "Median Phytoplankton Size",
                                        summaryContent: <div className="text-sm font-light leading-relaxed text-slate-300"><p>NOAA PIFSC’s experimental monthly MD50 product estimates median phytoplankton size using CoralTemp SST and ESA Ocean Colour CCI chlorophyll-a, following Barnes et al. (2011).</p><p className="mt-3 text-xs text-slate-400">Coverage: 1998 to the newest upstream monthly product. Treat MD50 as an experimental remote-sensing indicator, not an in-situ measurement.</p></div>,
                                        mathSubHeader: "Interpretation",
                                        mathContent: "MD50 is shown in micrometres (µm). It is a model-derived estimate of median community size rather than a direct count of phytoplankton cells."
                                    } : ((isForecastWindSelected || isHurricaneSelected) && windRenderMode === 'barbs') ? {
                                        title: "NOAA Wind Barbs",
                                        summaryContent: <WindBarbDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected?.startsWith('7.4.4.') ? {
                                        title: `NOAA Coral Reef Watch — ${CRW_DAILY_PRODUCTS[crwProductFromVariable(state.variableSelected)]?.label || 'Daily Product'}`,
                                        summaryContent: (
                                            <div className="text-sm text-slate-300 font-light leading-relaxed">
                                                <p>{CRW_DAILY_PRODUCTS[crwProductFromVariable(state.variableSelected)]?.description}</p>
                                                <p className="mt-3">This is NOAA Coral Reef Watch v3.1 daily 5 km satellite data. The map retains the NOAA product units and product-specific color thresholds; only the display geometry is aggregated for responsive interaction.</p>
                                                <a className="mt-3 inline-block text-cyan-300 underline hover:text-cyan-100" href="https://coralreefwatch.noaa.gov/product/5km/" target="_blank" rel="noopener noreferrer">NOAA CRW daily 5 km product guide</a>
                                            </div>
                                        ),
                                        mathSubHeader: "Product interpretation",
                                        mathContent: "Use the map legend for the active product's NOAA CRW color thresholds and units. DHW is accumulated heat stress; HotSpot is instantaneous warming above the climatological Maximum Monthly Mean; the seven-day trend shows the rate and direction of recent CoralTemp SST change."
                                    } : isApccSelected ? {
                                        title: apccSelectionLabel(apccParsed),
                                        summaryContent: (
                                            <div className="text-sm font-light leading-relaxed text-slate-300">
                                                <p>APEC Climate Center multi-model ensemble {apccParsed?.dataType}. The deterministic product is the simple composite ensemble anomaly; the probabilistic product shows the most likely below-normal, near-normal, or above-normal tercile and its probability.</p>
                                                <p className="mt-3">Lead horizon and method are explicit menu choices. Use the header to switch monthly/seasonal aggregation, map geometry, and—where supported—Palau, RMI, or Indo-Pacific extent. Sea-level pressure, 850 hPa temperature, and 500 hPa geopotential height are intentionally basin-only.</p>
                                                <p className="mt-3 text-xs text-slate-400">Native APCC 1.0° output. APCC MME Producing Centers provide the source hindcast/forecast data; APCC collects, archives, and produces the MME predictions.</p>
                                                <a className="mt-3 inline-block text-cyan-300 underline hover:text-cyan-100" href="https://www.apcc21.org/prediction/global/method?lang=en" target="_blank" rel="noopener noreferrer">APCC MME methodology</a>
                                            </div>
                                        ),
                                        mathSubHeader: "Forecast interpretation",
                                        mathContent: "Probabilistic values are tercile occurrence probabilities in percent. Deterministic values are bias-corrected ensemble anomalies in the units shown by the map legend. Seasonal values represent three-month periods and must not be interpreted as a single month."
                                    } : isForecastVariable(state.variableSelected) ? {
                                        title: forecastPageTitle(state.variableSelected) || 'NWP Forecast',
                                        summaryContent: (
                                            <div className="text-sm text-slate-300 font-light leading-relaxed">
                                                {forecastParsed?.model === 'gfs'
                                                    ? 'Short-range NOAA GFS (0.25°) daily outlook for Palau, RMI, and the Indo-Pacific.'
                                                    : 'Seasonal NOAA CFSv2 monthly-mean outlook for Palau, RMI, and the Indo-Pacific.'}
                                                {' '}Use the toolbar to switch gridded/contour and cycle map extent.
                                                Click a grid cell for local forecast history; the chart defaults to the EEZ average.
                                                {forecastParsed?.varKey === 'sst' && (
                                                    <div className="mt-5"><ConvergenceZoneMethodology /></div>
                                                )}
                                            </div>
                                        ),
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '7.1.2.2' ? {
                                        ...documentationMap['7.1.2.2']
                                    } : state.variableSelected === '2.1.1.3' ? {
                                        title: "Historical Surface Temperature",
                                        summaryContent: <SurfaceTempDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '2.1.2' ? {
                                        title: "PCC Probes",
                                        summaryContent: (
                                            <div className="text-sm text-slate-300 font-light leading-relaxed">
                                                <p>
                                                    View real-time and historical data from the <strong className="text-white font-medium">Palau Community College (PCC) Cooperative Research and Extension (CRE)</strong> environmental monitoring probes.
                                                </p>
                                                <p className="mt-3">
                                                    Select a station on the map or from the dashboard dropdown below to view its specific atmospheric and soil condition data.
                                                </p>
                                            </div>
                                        ),
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '4.1' ? {
                                        title: "Automated Weather Stations",
                                        summaryContent: (
                                            <div className="text-sm text-slate-300 font-light leading-relaxed">
                                                <p>
                                                    View historical data from the <strong className="text-white font-medium">Automated Weather Stations (AWS)</strong> across Palau.
                                                </p>
                                                <p className="mt-3">
                                                    Select a station on the map or from the dashboard dropdown below to view its specific meteorological conditions.
                                                </p>
                                            </div>
                                        ),
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '3.8.1' ? {
                                        title: "Ngardok Lake",
                                        summaryContent: (
                                            <div className="text-sm text-slate-300 font-light leading-relaxed">
                                                <p>
                                                    View historical water level and temperature data for <strong className="text-white font-medium">Lake Ngardok</strong>.
                                                </p>
                                                <p className="mt-3">
                                                    Toggle between Water Level and Temperature, or enable Year-over-Year comparison to see seasonal trends.
                                                </p>
                                            </div>
                                        ),
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '2.2.1.3' ? {
                                        title: "Historical Hot Days",
                                        summaryContent: <HotDaysDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '2.3.1.3' ? {
                                        title: "Historical Cold Nights",
                                        summaryContent: <ColdNightsDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '3.1.1.3' ? {
                                        title: "Historical Total Wet-Day Rainfall",
                                        summaryContent: <TotalWetDayDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '3.2.1.3' ? {
                                        title: "Historical Consecutive Dry Days",
                                        summaryContent: <ConsecutiveDryDaysDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '3.3.1.3' ? {
                                        title: "Historical Heavy Rainfall",
                                        summaryContent: <HeavyRainfallDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '3.4.1.3' ? {
                                        title: "Historical Wet Days",
                                        summaryContent: <WetDaysDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : state.variableSelected === '3.5.1.3' ? {
                                        title: "Historical Dry Days",
                                        summaryContent: <DryDaysDocumentation />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : isCmip7Selection(state.variableSelected) ? {
                                        title: isCmip7CatalogueSelection(state.variableSelected) ? "CMIP7 Availability Explorer" : "CMIP7 Projection",
                                        summaryContent: <CMIP7PreviewDocumentation variableSelected={state.variableSelected} />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : isCmip6Selection(state.variableSelected) ? {
                                        title: "CMIP6 Climate Projections",
                                        summaryContent: <CMIP6Documentation variableSelected={state.variableSelected} />,
                                        mathSubHeader: "",
                                        mathContent: ""
                                    } : ({
                                        title: "Variable Documentation",
                                        summaryContent: "Select a variable to see the summary.",
                                        mathSubHeader: "",
                                        mathContent: "No mathematical description available."
                                    })}
                                    scatterplotVariable={state.scatterplotVariable}
                                    lat={state.lat}
                                    long={state.long}
                                    dateSelected={state.dateSelected}
                                    sliderValue={state.dateSelected && state.dateSelected[state.sliderValue] ? String(state.dateSelected[state.sliderValue]) : ""}
                                    floodMetadata={state.floodMetadata}
                                    onMetadataChange={handleMetadataChange}
                                    mhwIsMasked={mhwIsMasked}
                                    mhwScope={mhwScope}
                                    variableSelected={state.variableSelected}
                                    oisstRegion={oisstRegion}
                                    oisstRegionLabel={oisstRegionPlotLabel}
                                    oisstAverageCsvUrl={oisstAverageCsvUrl}
                                    oisstBoundaryComparisonCsvUrl={oisstBoundaryComparisonCsvUrl}
                                    oisstLocationJsonUrl={oisstRegionMetadata[oisstRegion]?.locationJsonUrl}
                                    oisstAllowLocation={!(oisstViewScope === 'rmi' && activeOisstBoundary) && !oisstIndoPacific && oisstMode !== 'contour'}
                                    oisstMode={oisstMode}
                                    oisstIndoPacific={oisstIndoPacific}
                                    forecastAverageCsvUrl={
                                        !forecastIndoPacific && forecastRegionMetaAll[forecastRegion]?.averageCsvTemplate
                                            ? forecastRegionMetaAll[forecastRegion].averageCsvTemplate.replace('{var}', activeForecastVar)
                                            : null
                                    }
                                    forecastBoundaryComparisonCsvUrl={
                                        isForecastWindSelected && forecastRegion === 'rmi' && !forecastIndoPacific
                                            ? `/jsonFiles/forecasts/${forecastModel}/wind/rmi/timeseries/boundaries/${oisstBoundaryScheme}_comparison.csv`
                                            : null
                                    }
                                    forecastLocationJsonUrl={
                                        !forecastIndoPacific && forecastRegionMetaAll[forecastRegion]?.locationJsonTemplate
                                            ? forecastRegionMetaAll[forecastRegion].locationJsonTemplate.replace('{var}', activeForecastVar)
                                            : null
                                    }
                                    forecastRegionLabel={
                                        forecastIndoPacific
                                            ? 'Indo-Pacific'
                                            : (forecastRegionMetaAll[forecastRegion]?.label || forecastViewScopeLabel)
                                    }
                                    forecastAllowLocation={!forecastIndoPacific && forecastMode !== 'contour' && !(isForecastWindSelected && forecastRegion === 'rmi')}
                                    forecastVariableLabel={activeForecastVarMeta.label || activeForecastVar}
                                    forecastUnit={activeForecastVarMeta.unit || ''}
                                    forecastVarKey={activeForecastVar}
                                    forecastRegionKey={forecastRegion}
                                    crwAlertMode={crwAlertMode}
                                    crwScope={crwScope}
                                    crwDailyCellSelected={crwDailyHistoryKey === state.variableSelected}
                                    onResetCrwTile={resetCrwTileView}
                                />
                            </Suspense>
                            </div>
                        </motion.aside>
                    )}
                 </AnimatePresence>

                 {/* Floating Open Button for Right Drawer (Visible when menu is closed) */}
                 <AnimatePresence>
                    {!rightDrawerOpen && (
                        <motion.button
                            type="button"
                            key="right-open-btn"
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            exit={{ x: 50, opacity: 0, transition: { duration: 0.2, repeat: 0 } }}
                            onClick={handleRightDrawerToggle}
                            className={clsx(
                                isLargeScreen
                                    ? "fixed right-0 top-1/2 z-50 flex flex-col items-center justify-center gap-3 py-6 w-10 -translate-y-1/2"
                                    : "fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full",
                                "drawer-handle rounded-l-2xl border-y border-l border-white/20 bg-black/70",
                                "shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-white/80 hover:text-white group z-50"
                            )}
                        >
                            <ChevronLeft size={18} className="opacity-70 group-hover:opacity-100 transition-opacity" />
                            {isLargeScreen && <span className="text-[9px] font-medium uppercase tracking-[0.25em] [writing-mode:vertical-rl] rotate-180 opacity-70 group-hover:opacity-100 transition-opacity">
                                Panel
                            </span>}
                        </motion.button>
                    )}
                 </AnimatePresence>

            </Box> {/* End Root Flex Box */}



            
            <Dialog
            open={colorbarModalOpen}
            onClose={handleColorbarModalClose}
            aria-labelledby="colorbar-dialog-title"
            maxWidth="xs" // Adjust max width as needed
        >
            <DialogTitle id="colorbar-dialog-title" sx={{ m: 0, p: 2 }}>
                Colorbar
                <IconButton 
                    aria-label="close"
                    onClick={handleColorbarModalClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <X />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ padding: 0 }}> {/* Remove padding if image should fill */}
                {state.colorbar && (
                    <CachedImage
                        src={state.colorbar}
                        alt="Enlarged Colorbar"
                        style={{
                            display: 'block', // Ensure it behaves correctly
                            width: '100%', // Fill the dialog content width
                            height: 'auto', // Maintain aspect ratio
                            maxWidth: '100%', // Prevent exceeding container
                        }}
                    />
                )}
            </DialogContent>
        </Dialog>

        {/* --- Landing Page Overlay --- */}
        <AnimatePresence>
            {landingOpen && (
                <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-blue-900/90"><CircularProgress sx={{ color: 'white' }} /></div>}>
                    <LandingPage onEnter={handleLandingEnter} onStartTutorial={handleStartTutorial} />
                </Suspense>
            )}
        </AnimatePresence>
        
        <TutorialOverlay
            step={tutorialStep}
            setStep={setTutorialStep}
            portalName={rmiPortal ? 'Marshall Islands Ocean Portal' : 'Palau Ocean Portal'}
        />
        </ThemeProvider>
        </MotionConfig>
        </HelmetProvider>
    );
}

export default App;
